// ══════════════════════════════════════════════════════════════
// Centros de Apoyo — Part 2: Detail, Tabs, Link Notes, Invoice, Assets
// ══════════════════════════════════════════════════════════════

// ── Cajas: usa directamente venta.cantidad (igual que el flujo QR de n8n) ───────
// El SQL en n8n hace: SUM(vt.cantidad) AS cajas
// Por eso la función simplemente devuelve el número que ya viene calculado de BD.

/**
 * Devuelve las cajas de una venta. En el backoffice se usa v.cantidad directamente
 * (igual que el workflow n8n QR que hace SUM(vt.cantidad) AS cajas en Postgres).
 * @param {number|string} cantidad - valor de venta.cantidad
 * @returns {number}
 */
function calcularCajasVenta(cantidad){
    return Math.max(0, Math.round(parseFloat(cantidad) || 0));
}

// No se necesita catálogo de productos
async function _ensureProductosCalif(){
    return {};
}




// ══ HELPER CRÍTICO: Recalcula credito_usado desde fuente de verdad ══════════
// Fórmula: SUM(notas aprobadas excl.activaciones) - SUM(TODOS los pagos)
// Permite negativos = saldo A FAVOR del centro.
async function _recalcCreditoUsado(centroId, delta){
    if(delta === undefined || delta === null) return null; // sin delta no actualizar
    try {
        const d = Number(delta);
        await _caSql(`UPDATE centro_apoyo SET credito_usado = credito_usado + ${d.toFixed(2)}, actualizado_en=NOW() WHERE id=${centroId}`);
        const res = await _caSql(`SELECT credito_usado FROM centro_apoyo WHERE id=${centroId}`);
        const balance = Number(res?.[0]?.credito_usado || 0);
        const c = _caData.find(x=>x.id===centroId);
        if(c) c.credito_usado = balance;
        return balance;
    } catch(e) { console.warn('[recalcCreditoUsado]', e.message); return null; }
}

// ── Recálculo completo del balance ───────────────────────────────
// Fórmula: SUM(notas aprobadas vinculadas, excluyendo activaciones)
//        - SUM(abonos en movimientos)
// Los reversos NO se suman al balance porque reversarCobro() ya
// elimina el abono original de centro_apoyo_movimiento (DELETE).
// El registro tipo 'reverso' es solo informativo/auditoría.
// Los 'cargo'/'ajuste' en movimientos NO se usan como fuente de deuda
// porque también registran activaciones que no generan deuda.
// Muestra diagnóstico antes de aplicar.
async function recalcBalanceFull(centroId){
    if(!centroId){ BO_AUTH.showAlert('⚠ Sin centro seleccionado','warning'); return; }
    const btn = event?.target;
    if(btn){ btn.disabled=true; btn.textContent='⏳ Calculando...'; }
    try {
        const c = _caData.find(x=>x.id===centroId);
        const moneda  = c?.moneda || 'MXN';
        const balActual = Number(c?.credito_usado || 0);

        // 1) Deuda: notas aprobadas vinculadas al centro, EXCLUYENDO activaciones
        const notasRes = await _caSql(`
            SELECT COALESCE(SUM(total), 0) AS total_notas, COUNT(*) AS num_notas
            FROM venta
            WHERE centro_apoyo_id = ${centroId}
              AND approved = 1
              AND (producto_id IS NULL OR producto_id != 'calificacion-mes')
        `);
        const totalNotas = Number(notasRes?.[0]?.total_notas || 0);
        const numNotas   = Number(notasRes?.[0]?.num_notas   || 0);

        // 2) Pagos: solo abonos y reversos en movimientos (NO cargos ni ajustes)
        const abonosRes = await _caSql(`
            SELECT COALESCE(SUM(monto), 0) AS total_abonos, COUNT(*) AS num_abonos
            FROM centro_apoyo_movimiento
            WHERE centro_id = ${centroId}
              AND tipo IN ('abono')
        `);
        const totalAbonos = Number(abonosRes?.[0]?.total_abonos || 0);
        const numAbonos   = Number(abonosRes?.[0]?.num_abonos   || 0);

        // 2b) Reversos (solo informativo para diagnóstico, no afectan el cálculo)
        const revRes = await _caSql(`
            SELECT COALESCE(SUM(monto), 0) AS total_reversos, COUNT(*) AS num_reversos
            FROM centro_apoyo_movimiento
            WHERE centro_id = ${centroId}
              AND tipo = 'reverso'
        `);
        const totalReversos = Number(revRes?.[0]?.total_reversos || 0);
        const numReversos   = Number(revRes?.[0]?.num_reversos   || 0);

        // 3) Balance: deuda - pagos (reversos son solo informativos,
        //    reversarCobro() ya borra el abono original del movimiento)
        const balNuevo = totalNotas - totalAbonos;
        const fmt = v => `$${Math.abs(v).toLocaleString('es-MX',{minimumFractionDigits:2})} ${moneda}`;

        // 4) Diagnóstico
        const confirmMsg =
            `📊 DIAGNÓSTICO — ${c?.nombre || centroId}\n\n` +
            `  🔴 Notas aprobadas (sin activaciones) (${numNotas}): ${fmt(totalNotas)}\n` +
            `  🟢 Abonos/pagos registrados (${numAbonos}): ${fmt(totalAbonos)}\n` +
            (numReversos ? `  🟡 Reversos (info/auditoría) (${numReversos}): ${fmt(totalReversos)}\n` : '') +
            `  ─────────────────────────────\n` +
            `  Resultado: ${balNuevo < 0 ? '✅ ' + fmt(balNuevo) + ' A FAVOR' : '🔴 ' + fmt(balNuevo) + ' DEUDA'}\n\n` +
            `  Balance actual: ${balActual < 0 ? fmt(balActual)+' A FAVOR' : fmt(balActual)+' DEUDA'}\n` +
            `  Diferencia: ${fmt(balNuevo - balActual)}\n\n` +
            `¿Aplicar el nuevo balance?`;

        if(!confirm(confirmMsg)) return;

        // 5) Guardar
        await _caSql(`UPDATE centro_apoyo SET credito_usado = ${balNuevo.toFixed(2)}, actualizado_en=NOW() WHERE id=${centroId}`);
        if(c) c.credito_usado = balNuevo;
        verDetalleCentro(centroId);
        renderCentrosTable();
        BO_AUTH.showAlert(`✅ Balance aplicado: ${balNuevo < 0 ? fmt(balNuevo)+' A FAVOR' : fmt(balNuevo)+' DEUDA'}`, 'success');

    } catch(e) {
        BO_AUTH.showAlert('❌ Error al recalcular: '+e.message, 'danger');
        console.error('[recalcBalanceFull]', e);
    } finally {
        if(btn){ btn.disabled=false; btn.textContent='⟳ Recalcular'; }
    }
}


// ── Ajuste manual de balance (corrección de emergencia) ─────────
// Permite ingresar directamente el credito_usado correcto cuando el historial no es suficiente
async function ajusteManualBalance(centroId){
    if(!centroId){ BO_AUTH.showAlert('⚠ Sin centro seleccionado','warning'); return; }
    const c = _caData.find(x=>x.id===centroId);
    const moneda = c?.moneda || 'MXN';
    const actual = Number(c?.credito_usado || 0);

    const input = prompt(
        `✏ Ajuste manual de balance para: ${c?.nombre || centroId}\n` +
        `Moneda: ${moneda} | Balance actual: $${actual.toLocaleString('es-MX',{minimumFractionDigits:2})}\n\n` +
        `Ingresa el balance correcto (negativo = saldo a favor del centro):\n` +
        `Ejemplo: -16225 (a favor), 9690 (deuda)`
    );
    if(input === null || input.trim() === '') return; // canceló

    const nuevoBalance = parseFloat(input.replace(/,/g,'').replace(/\$/g,'').trim());
    if(isNaN(nuevoBalance)){ BO_AUTH.showAlert('⚠ Número inválido','warning'); return; }

    if(!confirm(
        `¿Confirmas ajustar el balance de ${c?.nombre} a:\n` +
        `$${nuevoBalance.toLocaleString('es-MX',{minimumFractionDigits:2})} ${moneda}?\n\n` +
        `(${nuevoBalance < 0 ? '✅ Saldo a FAVOR del centro' : '🔴 Deuda del centro'})`
    )) return;

    try {
        await _caSql(`UPDATE centro_apoyo SET credito_usado = ${nuevoBalance.toFixed(2)}, actualizado_en=NOW() WHERE id=${centroId}`);
        if(c) c.credito_usado = nuevoBalance;
        verDetalleCentro(centroId);
        renderCentrosTable();
        BO_AUTH.showAlert(`✅ Balance ajustado a $${nuevoBalance.toLocaleString('es-MX',{minimumFractionDigits:2})} ${moneda}`, 'success');
    } catch(e) {
        BO_AUTH.showAlert('❌ Error: '+e.message,'danger');
    }
}

// ── Detalle Centro (modal con tabs) ───────────────────────────
async function verDetalleCentro(id){
    window._caDetalleId = id;
    const c = _caData.find(x => String(x.id) === String(id));
    if(!c) return;

    if (c._isVirtual) {
        // Abrir modal dedicado "Cola de Activaciones Por Asignar"
        abrirColaActivaciones();
        return;
    }

    document.getElementById('caDetalleTitulo').textContent='🏪 '+c.nombre;
    document.getElementById('caDetalleHeader').innerHTML=`
        <div><div style="color:var(--text-dim);font-size:.72rem;">Líder</div><div style="font-weight:600;">${c._lider_nombre||('#'+c.lider_id)||'—'}</div></div>
        <div><div style="color:var(--text-dim);font-size:.72rem;">Admin</div><div style="font-weight:600;">${c._admin_nombre||('#'+c.admin_id)||'—'}</div></div>
        <div><div style="color:var(--text-dim);font-size:.72rem;">Estado</div><div>${_caEstadoBadge(c.estado)}</div></div>`;

    const lim = Number(c.limite_credito||0);
    let balance = Number(c.credito_usado||0);

    const creditBarEl   = document.getElementById('caCreditBar');
    const creditPctEl   = document.getElementById('caBarraPct');
    const creditUsadoEl = document.getElementById('caBarraUsado');
    const creditLimEl   = document.getElementById('caBarraLimite');

    if(balance < 0){
        const aFavor = Math.abs(balance);
        if(creditBarEl){ creditBarEl.style.width='100%'; creditBarEl.style.background='#22c55e'; }
        if(creditPctEl){ creditPctEl.textContent='✅ A FAVOR'; creditPctEl.style.color='#22c55e'; }
        if(creditUsadoEl){ creditUsadoEl.textContent='✅ '+_caMoneyFmt(aFavor,c.moneda)+' a favor'; creditUsadoEl.style.color='#22c55e'; }
        if(creditLimEl)  creditLimEl.textContent='Límite: '+_caMoneyFmt(lim,c.moneda);
    } else {
        const uso = balance;
        const pct = lim>0 ? Math.min(100,Math.round(uso/lim*100)) : 0;
        const barColor = pct>=90?'#ef4444':pct>=70?'#f59e0b':'#22c55e';
        if(creditBarEl){ creditBarEl.style.width=pct+'%'; creditBarEl.style.background=barColor; }
        if(creditPctEl){ creditPctEl.textContent=pct+'%'; creditPctEl.style.color=''; }
        if(creditUsadoEl){ creditUsadoEl.textContent=_caMoneyFmt(uso,c.moneda); creditUsadoEl.style.color=''; }
        if(creditLimEl)  creditLimEl.textContent='Límite: '+_caMoneyFmt(lim,c.moneda);
    }

    setCaTab('notas');
    document.getElementById('modalDetalleCentro').style.display='flex';
}
function cerrarModalDetalleCentro(){document.getElementById('modalDetalleCentro').style.display='none';}

// ══════════════════════════════════════════════════════════════
// ══ MODAL: Cola de Activaciones — Por Asignar (#230)  ════════
// ══════════════════════════════════════════════════════════════

// Alias del botón Absorción Global (antes roto)
function abrirModalAbsorberNotas(){ abrirColaActivaciones(); }

// Estado de filtros del panel
window._paFiltros = { texto: '', periodo: '90', soloDisp: false };

function abrirColaActivaciones(){
    // Crear modal si no existe
    if(!document.getElementById('modalColaActivaciones')){
        const m = document.createElement('div');
        m.id = 'modalColaActivaciones';
        m.style.cssText = 'display:none;position:fixed;inset:0;background:rgba(0,0,0,.65);z-index:9100;align-items:center;justify-content:center;padding:1rem;';
        m.innerHTML = `
        <div style="background:var(--bg-card,#1e2030);border:1px solid rgba(255,255,255,.1);border-radius:16px;width:100%;max-width:900px;max-height:92vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 24px 60px rgba(0,0,0,.5);">

          <!-- Header -->
          <div style="padding:1.1rem 1.4rem;border-bottom:1px solid rgba(255,255,255,.07);display:flex;align-items:center;justify-content:space-between;gap:1rem;">
            <div style="display:flex;align-items:center;gap:.65rem;">
              <span style="font-size:1.3rem;">📦</span>
              <div>
                <div style="font-weight:700;font-size:1rem;letter-spacing:-.01em;">Cola de Activaciones — Por Asignar <span style="color:#a5b4fc;font-size:.8rem;">(#230)</span></div>
                <div style="font-size:.72rem;color:var(--text-dim);">Notas pagadas pendientes de asignar a un líder</div>
              </div>
            </div>
            <button onclick="cerrarColaActivaciones()" style="background:none;border:none;color:var(--text-dim);font-size:1.3rem;cursor:pointer;line-height:1;">✕</button>
          </div>

          <!-- KPIs -->
          <div id="paKpisRow" style="display:grid;grid-template-columns:repeat(3,1fr);gap:.75rem;padding:1rem 1.4rem 0;">
            <div style="background:rgba(99,102,241,.1);border:1px solid rgba(99,102,241,.2);border-radius:10px;padding:.75rem 1rem;text-align:center;">
              <div id="paKpi-notas" style="font-size:1.6rem;font-weight:800;color:#a5b4fc;">--</div>
              <div style="font-size:.72rem;color:var(--text-dim);margin-top:2px;">Notas en transito</div>
            </div>
            <div style="background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.2);border-radius:10px;padding:.75rem 1rem;text-align:center;">
              <div id="paKpi-cajas" style="font-size:1.6rem;font-weight:800;color:#fbbf24;">--</div>
              <div style="font-size:.72rem;color:var(--text-dim);margin-top:2px;">Cajas pendientes</div>
            </div>
            <div onclick="_paSetVista('asignadas')" style="background:rgba(34,197,94,.08);border:1px solid rgba(34,197,94,.2);border-radius:10px;padding:.75rem 1rem;text-align:center;cursor:pointer;transition:background .15s;" onmouseover="this.style.background='rgba(34,197,94,.18)'" onmouseout="this.style.background='rgba(34,197,94,.08)'" title="Click para ver detalle">
              <div id="paKpi-asig" style="font-size:1.6rem;font-weight:800;color:#22c55e;">--</div>
              <div style="font-size:.72rem;color:var(--text-dim);margin-top:2px;">Asignadas (mes) 👆</div>
            </div>
          </div>

          <!-- Filtros + Tab toggle -->
          <div style="padding:.85rem 1.4rem 0;display:flex;gap:.5rem;flex-wrap:wrap;align-items:center;">
            <div style="display:flex;background:rgba(255,255,255,.05);border-radius:8px;padding:3px;gap:2px;">
              <button id="paTabPendientes" onclick="_paSetVista('pendientes')" style="padding:.35rem .8rem;border-radius:6px;border:none;font-size:.78rem;font-weight:600;cursor:pointer;background:#6366f1;color:#fff;">Pendientes</button>
              <button id="paTabAsignadas"  onclick="_paSetVista('asignadas')"  style="padding:.35rem .8rem;border-radius:6px;border:none;font-size:.78rem;font-weight:600;cursor:pointer;background:transparent;color:var(--text-dim);">Asignadas</button>
            </div>
            <input id="paFiltroTexto" type="text" placeholder="ID, nombre, destino..." oninput="_paAplicarFiltros()" style="flex:1;min-width:140px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:8px;padding:.42rem .75rem;color:inherit;font-size:.82rem;">
            <select id="paFiltroPeriodo" onchange="_paAplicarFiltros()" style="background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:8px;padding:.42rem .65rem;color:inherit;font-size:.82rem;">
              <option value="0">Todos</option>
              <option value="7">7 dias</option>
              <option value="30">30 dias</option>
              <option value="90" selected>3 meses</option>
              <option value="180">6 meses</option>
            </select>
            <label style="font-size:.78rem;color:var(--text-dim);display:flex;align-items:center;gap:.3rem;cursor:pointer;">
              <input type="checkbox" id="paFiltroSoloDisp" onchange="_paAplicarFiltros()"> Solo disp.
            </label>
            <button onclick="loadColaActivaciones()" style="background:rgba(99,102,241,.2);border:1px solid rgba(99,102,241,.35);border-radius:8px;padding:.4rem .75rem;color:#a5b4fc;font-size:.78rem;cursor:pointer;">Recargar</button>
          </div>

          <!-- Tabla -->
          <div style="flex:1;overflow-y:auto;padding:0 1.4rem 1.4rem;margin-top:.75rem;">
            <!-- Vista Pendientes -->
            <div id="paTablaPendientes">
              <table style="width:100%;border-collapse:collapse;font-size:.82rem;">
                <thead>
                  <tr style="color:var(--text-dim);font-size:.7rem;text-transform:uppercase;letter-spacing:.05em;">
                    <th style="padding:.5rem .6rem;text-align:left;font-weight:600;">Nota</th>
                    <th style="padding:.5rem .6rem;text-align:left;font-weight:600;">Fecha</th>
                    <th style="padding:.5rem .6rem;text-align:left;font-weight:600;">Destino detectado</th>
                    <th style="padding:.5rem .6rem;text-align:right;font-weight:600;">Total</th>
                    <th style="padding:.5rem .6rem;text-align:center;font-weight:600;">Cajas</th>
                    <th style="padding:.5rem .6rem;text-align:center;font-weight:600;">Accion</th>
                  </tr>
                </thead>
                <tbody id="paTablaBody">
                  <tr><td colspan="6" style="text-align:center;padding:2rem;color:var(--text-dim);">Cargando...</td></tr>
                </tbody>
              </table>
            </div>
            <!-- Vista Asignadas -->
            <div id="paTablaAsignadas" style="display:none;">
              <table style="width:100%;border-collapse:collapse;font-size:.82rem;">
                <thead>
                  <tr style="color:var(--text-dim);font-size:.7rem;text-transform:uppercase;letter-spacing:.05em;">
                    <th style="padding:.5rem .6rem;text-align:left;font-weight:600;">ID Activacion</th>
                    <th style="padding:.5rem .6rem;text-align:left;font-weight:600;">Fecha</th>
                    <th style="padding:.5rem .6rem;text-align:left;font-weight:600;">Lider activado</th>
                    <th style="padding:.5rem .6rem;text-align:left;font-weight:600;">Nota respaldo</th>
                    <th style="padding:.5rem .6rem;text-align:center;font-weight:600;">Cancelar</th>
                  </tr>
                </thead>
                <tbody id="paTablaAsigBody">
                  <tr><td colspan="5" style="text-align:center;padding:2rem;color:var(--text-dim);">Cargando...</td></tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>`;
        document.body.appendChild(m);
    }
    document.getElementById('modalColaActivaciones').style.display = 'flex';
    loadColaActivaciones();
}

function cerrarColaActivaciones(){
    const m = document.getElementById('modalColaActivaciones');
    if(m) m.style.display = 'none';
}

// ── Vista tabs (Pendientes / Asignadas) ────────────────────────
window._paVista = 'pendientes';
window._paAsignadasCache = [];

function _paSetVista(vista){
    window._paVista = vista;
    const esPend = vista === 'pendientes';
    const divPend = document.getElementById('paTablaPendientes');
    const divAsig = document.getElementById('paTablaAsignadas');
    if(divPend) divPend.style.display = esPend ? '' : 'none';
    if(divAsig) divAsig.style.display  = esPend ? 'none' : '';
    const btnP = document.getElementById('paTabPendientes');
    const btnA = document.getElementById('paTabAsignadas');
    if(btnP){ btnP.style.background = esPend ? '#6366f1' : 'transparent'; btnP.style.color = esPend ? '#fff' : 'var(--text-dim)'; }
    if(btnA){ btnA.style.background = !esPend ? '#22c55e' : 'transparent'; btnA.style.color = !esPend ? '#fff' : 'var(--text-dim)'; }
    if(!esPend){ window._paAsignadasCache.length ? _paRenderAsignadas() : _paCargarAsignadas(); }
}

async function _paCargarAsignadas(){
    const tb = document.getElementById('paTablaAsigBody');
    if(tb) tb.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:2rem;color:var(--text-dim);">Cargando...</td></tr>';
    try {
        const periodo = parseInt(document.getElementById('paFiltroPeriodo')?.value || '90');
        const dateWhere = periodo > 0 ? `AND v.fecha_venta >= NOW() - INTERVAL '${periodo} days'` : '';
        const rows = await _caSql(`
            SELECT v.id, v.fecha_venta, v.comentario,
                   c.nombre || ' ' || COALESCE(c.ape_p,'') AS lider_nombre,
                   c.registro AS lider_reg
            FROM venta v
            LEFT JOIN cliente c ON c.id = v.cliente_id
            WHERE v.producto_id = 'calificacion-mes'
              AND v.approved = 1
              AND (v.comentario ILIKE '%ACTIVACI_N PAGO ADQUIRIDO%')
              ${dateWhere}
            ORDER BY v.fecha_venta DESC
            LIMIT 300
        `);
        window._paAsignadasCache = rows;
        _paRenderAsignadas();
    } catch(e){
        if(tb) tb.innerHTML = `<tr><td colspan="5" style="color:#ef4444;padding:1rem;">Error: ${e.message}</td></tr>`;
    }
}

function _paRenderAsignadas(){
    const tb = document.getElementById('paTablaAsigBody');
    if(!tb) return;
    const texto = (document.getElementById('paFiltroTexto')?.value || '').toLowerCase().trim();
    let rows = window._paAsignadasCache || [];
    if(texto) rows = rows.filter(r =>
        (r.id||'').toLowerCase().includes(texto)
        || (r.lider_nombre||'').toLowerCase().includes(texto)
        || String(r.lider_reg||'').includes(texto)
        || (r.comentario||'').toLowerCase().includes(texto)
    );
    if(!rows.length){
        tb.innerHTML = `<tr><td colspan="5" style="text-align:center;padding:2rem;color:var(--text-dim);">Sin activaciones${texto ? ' que coincidan' : ' en este periodo'}</td></tr>`;
        return;
    }
    tb.innerHTML = rows.map(r => {
        const respaldoMatch = (r.comentario||'').match(/RESPALDO NOTA ([\w-]+)/i);
        const respaldoId    = respaldoMatch ? respaldoMatch[1] : '';
        const respaldoShort = respaldoId ? respaldoId.substring(0,14)+'...' : 'sin ref.';
        const fecha = r.fecha_venta ? new Date(r.fecha_venta).toLocaleDateString('es-MX') : '--';
        const liderEsc = (r.lider_nombre||'--').replace(/\\/g,'\\\\').replace(/'/g,"\\'");
        const actEsc   = r.id.replace(/\\/g,'\\\\').replace(/'/g,"\\'");
        const respEsc  = respaldoId.replace(/\\/g,'\\\\').replace(/'/g,"\\'");
        return `<tr style="border-bottom:1px solid rgba(255,255,255,.04);" onmouseover="this.style.background='rgba(255,255,255,.02)'" onmouseout="this.style.background=''">
            <td style="padding:.55rem .6rem;font-family:monospace;font-size:.72rem;color:#a5b4fc;">${r.id.substring(0,20)}</td>
            <td style="padding:.55rem .6rem;white-space:nowrap;">${fecha}</td>
            <td style="padding:.55rem .6rem;"><strong>${r.lider_nombre||'--'}</strong> <span style="color:var(--text-dim);font-size:.75rem;">#${r.lider_reg||'?'}</span></td>
            <td style="padding:.55rem .6rem;font-family:monospace;font-size:.72rem;color:#94a3b8;" title="${respaldoId}">${respaldoShort}</td>
            <td style="padding:.55rem .6rem;text-align:center;">
                <button onclick="_paCancelarActivacion('${actEsc}','${respEsc}','${liderEsc}')"
                  style="background:rgba(239,68,68,.12);border:1px solid rgba(239,68,68,.3);border-radius:6px;padding:.3rem .65rem;color:#ef4444;font-size:.72rem;cursor:pointer;font-weight:600;">
                  Cancelar
                </button>
            </td>
        </tr>`;
    }).join('');
}

function _paCancelarActivacion(actId, respaldoNotaId, liderNombre){
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.75);z-index:9300;display:flex;align-items:center;justify-content:center;padding:1rem;';
    overlay.innerHTML = `
    <div style="background:#1e2030;border:1px solid rgba(239,68,68,.3);border-radius:12px;max-width:420px;width:100%;padding:1.4rem;box-shadow:0 20px 50px rgba(0,0,0,.6);">
        <div style="font-weight:700;font-size:.95rem;margin-bottom:.5rem;">Cancelar Activacion</div>
        <div style="font-size:.82rem;color:var(--text-dim);margin-bottom:1rem;line-height:1.5;">
            Esto eliminara la activacion <span style="color:#a5b4fc;font-family:monospace;font-size:.75rem;">${actId}</span>
            de <strong>${liderNombre}</strong> y devolvera 1 cupo a la nota respaldo.
            <br><br><span style="color:#f59e0b;">Esta accion no se puede deshacer.</span>
        </div>
        <div style="display:flex;gap:.6rem;justify-content:flex-end;">
            <button onclick="this.closest('[style*=fixed]').remove()"
              style="background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.1);border-radius:8px;padding:.5rem 1rem;color:var(--text-dim);font-size:.85rem;cursor:pointer;">
              No, mantener
            </button>
            <button id="btnConfCancelarAct"
              onclick="_paEjecutarCancelacion('${actId.replace(/'/g,"\\'")}','${respaldoNotaId.replace(/'/g,"\\'")}',this)"
              style="background:#dc2626;border:none;border-radius:8px;padding:.5rem 1rem;color:#fff;font-size:.85rem;font-weight:700;cursor:pointer;">
              Si, cancelar
            </button>
        </div>
    </div>`;
    document.body.appendChild(overlay);
}

async function _paEjecutarCancelacion(actId, respaldoNotaId, btn){
    btn.disabled = true;
    btn.textContent = 'Procesando...';
    try {
        await _caSql(`DELETE FROM venta WHERE id='${actId}'`);
        if(respaldoNotaId){
            await _caSql(`UPDATE venta SET activaciones_usadas = GREATEST(0, COALESCE(activaciones_usadas,0) - 1) WHERE id='${respaldoNotaId}'`);
        }
        btn.closest('[style*=fixed]').remove();
        BO_AUTH.showAlert('Activacion cancelada y cupo devuelto a la nota origen.', 'success');
        window._paAsignadasCache = [];
        await loadColaActivaciones();
        if(window._paVista === 'asignadas') await _paCargarAsignadas();
    } catch(e){
        btn.disabled = false;
        btn.textContent = 'Si, cancelar';
        BO_AUTH.showAlert('Error: ' + e.message, 'danger');
    }
}

// Cache de notas cargadas para filtrar en cliente
window._paNotasCache = [];

async function loadColaActivaciones(){
    const tb = document.getElementById('paTablaBody');
    if(!tb) return;
    tb.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:2rem;color:var(--text-dim);">⏳ Cargando notas…</td></tr>';

    try {
        const periodo = parseInt(document.getElementById('paFiltroPeriodo')?.value || '90');
        const dateWhere = periodo > 0 ? `AND v.fecha_venta >= NOW() - INTERVAL '${periodo} days'` : '';

        // Traer todas las notas del líder #230 (Por Asignar)
        const rows = await _caSql(`
            SELECT v.id, v.fecha_venta, v.total,
                   COALESCE(v.cantidad, 1) AS cantidad,
                   COALESCE(v.activaciones_usadas, 0) AS activaciones_usadas,
                   v.comentario
            FROM venta v
            WHERE v.cliente_id = '270a09e5-8250-44ea-82fa-b26dfc0553f0'
              AND v.approved = 1
              AND (v.producto_id IS NULL OR v.producto_id != 'calificacion-mes')
              ${dateWhere}
            ORDER BY v.fecha_venta DESC
            LIMIT 500
        `);

        // KPI: notas de activación generadas este mes
        const mesActual = new Date().toISOString().substring(0,7);
        const actsRes = await _caSql(`
            SELECT COUNT(*) AS n
            FROM venta
            WHERE producto_id = 'calificacion-mes'
              AND approved = 1
              AND TO_CHAR(fecha_venta, 'YYYY-MM') = '${mesActual}'
              AND comentario ILIKE '%ACTIVACI_N PAGO ADQUIRIDO%'
        `);

        window._paNotasCache = rows;

        // Calcular KPIs
        const totalNotas = rows.length;
        const totalCajasPend = rows.reduce((s, r) => {
            const cajas = Math.max(1, Math.round(parseFloat(r.cantidad)||1));
            const usadas = parseInt(r.activaciones_usadas) || 0;
            return s + Math.max(0, cajas - usadas);
        }, 0);
        const totalAsig = parseInt(actsRes[0]?.n || 0);

        const setKpi = (id, v) => { const el = document.getElementById(id); if(el) el.textContent = v; };
        setKpi('paKpi-notas', totalNotas);
        setKpi('paKpi-cajas', totalCajasPend);
        setKpi('paKpi-asig', totalAsig);

        _paRenderTabla();

    } catch(e) {
        if(tb) tb.innerHTML = `<tr><td colspan="6" style="color:#ef4444;padding:1rem;">Error: ${e.message}</td></tr>`;
    }
}

function _paAplicarFiltros(){
    window._paFiltros = {
        texto: (document.getElementById('paFiltroTexto')?.value || '').toLowerCase().trim(),
        periodo: document.getElementById('paFiltroPeriodo')?.value || '90',
        soloDisp: document.getElementById('paFiltroSoloDisp')?.checked || false
    };
    if (window._paVista === 'asignadas') {
        _paRenderAsignadas();
        if (document.getElementById('paFiltroPeriodo') === document.activeElement) {
            _paCargarAsignadas();
        }
    } else {
        _paRenderTabla();
        if (document.getElementById('paFiltroPeriodo') === document.activeElement){
            loadColaActivaciones();
        }
    }
}

function _paRenderTabla(){
    const tb = document.getElementById('paTablaBody');
    if(!tb) return;
    const { texto, soloDisp } = window._paFiltros || {};

    let rows = window._paNotasCache || [];

    // Filtro texto (cliente, id, comentario)
    if(texto) rows = rows.filter(r => {
        const destMatch = (r.comentario||'').match(/Para:\s*([^|]+)/i) || (r.comentario||'').match(/ACTIVOS_TERCEROS\s*([^$]+)/i);
        const dest = destMatch ? destMatch[1].toLowerCase() : '';
        return r.id.toLowerCase().includes(texto)
            || (r.comentario||'').toLowerCase().includes(texto)
            || dest.includes(texto);
    });

    // Filtro solo disponibles
    const pendientes = rows.filter(r => {
        const cajas = Math.max(1, Math.round(parseFloat(r.cantidad)||1));
        const usadas = parseInt(r.activaciones_usadas) || 0;
        return (cajas - usadas) > 0;
    });
    const completadas = rows.filter(r => {
        const cajas = Math.max(1, Math.round(parseFloat(r.cantidad)||1));
        const usadas = parseInt(r.activaciones_usadas) || 0;
        return (cajas - usadas) <= 0;
    });

    const displayRows = soloDisp ? pendientes : rows;
    if(!displayRows.length){
        tb.innerHTML = `<tr><td colspan="6" style="text-align:center;padding:2rem;color:var(--text-dim);">${texto||soloDisp ? 'Sin resultados para los filtros aplicados' : 'No hay notas por asignar'}</td></tr>`;
        return;
    }

    const renderRow = (r, dimmed) => {
        const cajas = Math.max(1, Math.round(parseFloat(r.cantidad)||1));
        const usadas = parseInt(r.activaciones_usadas) || 0;
        const disponibles = cajas - usadas;
        const agotada = disponibles <= 0;
        const excedida = usadas > cajas;

        const destMatch = (r.comentario||'').match(/Para:\s*([^|]+)/i) || (r.comentario||'').match(/ACTIVOS_TERCEROS\s*([^$]+)/i);
        const destNombre = destMatch ? destMatch[1].trim() : 'Sin destinatario explícito';
        const safeDest = destNombre.replace(/'/g, "\\'");
        const safeCom  = (r.comentario||'').replace(/'/g, "\\'");

        const cajasBadge = excedida
            ? `<span style="background:rgba(239,68,68,.15);color:#ef4444;border:1px solid rgba(239,68,68,.3);border-radius:4px;padding:.15rem .45rem;font-size:.7rem;font-weight:700;">⚠ ${usadas}/${cajas}</span>`
            : agotada
                ? `<span style="background:rgba(100,116,139,.12);color:#94a3b8;border:1px solid rgba(100,116,139,.2);border-radius:4px;padding:.15rem .45rem;font-size:.7rem;">✓ ${usadas}/${cajas}</span>`
                : `<span style="background:rgba(34,197,94,.1);color:#22c55e;border:1px solid rgba(34,197,94,.25);border-radius:4px;padding:.15rem .45rem;font-size:.7rem;font-weight:600;">🟢 ${usadas}/${cajas}</span>`;

        const btnActivar = agotada
            ? `<span style="font-size:.72rem;color:#4b5563;">Agotada</span>`
            : `<button onclick="activarLiderDesdeNota('${r.id}')" style="background:#6366f1;border:none;border-radius:6px;padding:.3rem .7rem;color:#fff;font-size:.72rem;cursor:pointer;font-weight:600;">⚡ Activar (${disponibles})</button>`;

        const fecha = r.fecha_venta ? new Date(r.fecha_venta).toLocaleDateString('es-MX') : '—';
        const destColor = destNombre === 'Sin destinatario explícito' ? 'color:var(--text-dim);font-style:italic;' : 'color:#e2e8f0;';

        return `<tr style="border-bottom:1px solid rgba(255,255,255,.04);opacity:${dimmed?'0.45':'1'};transition:opacity .15s;" onmouseover="this.style.background='rgba(255,255,255,.02)'" onmouseout="this.style.background=''">
            <td style="padding:.55rem .6rem;font-family:monospace;font-size:.75rem;color:#a5b4fc;">${r.id.substring(0,12)}…</td>
            <td style="padding:.55rem .6rem;white-space:nowrap;">${fecha}</td>
            <td style="padding:.55rem .6rem;${destColor}">${destNombre}</td>
            <td style="padding:.55rem .6rem;text-align:right;font-weight:600;">${_caMoneyFmt(r.total,'MXN')}</td>
            <td style="padding:.55rem .6rem;text-align:center;">${cajasBadge}</td>
            <td style="padding:.55rem .6rem;text-align:center;">${btnActivar}</td>
        </tr>`;
    };

    let html = '';

    if(pendientes.length && !soloDisp){
        html += `<tr><td colspan="6" style="padding:.5rem .6rem;font-size:.7rem;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#fbbf24;background:rgba(245,158,11,.04);">⏳ Pendientes de asignar (${pendientes.reduce((s,r)=>s+Math.max(0,Math.max(1,Math.round(parseFloat(r.cantidad)||1))-(parseInt(r.activaciones_usadas)||0)),0)} cajas disponibles)</td></tr>`;
        html += pendientes.map(r => renderRow(r, false)).join('');
    } else if(soloDisp){
        html += pendientes.map(r => renderRow(r, false)).join('');
    }

    if(completadas.length && !soloDisp){
        html += `<tr><td colspan="6" style="padding:.5rem .6rem;font-size:.7rem;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--text-dim);background:rgba(255,255,255,.02);cursor:pointer;" onclick="this.nextSibling && (this.nextSibling.style.display = this.nextSibling.style.display==='none'?'':'none')">✓ Completamente asignadas (${completadas.length}) — click para expandir ▾</td></tr>`;
        html += `<tbody id="paCompletadasBody" style="display:none">${completadas.map(r => renderRow(r, true)).join('')}</tbody>`;
    }

    tb.innerHTML = html;
}

function setCaTab(tab){
    ['notas','pagos','activaciones','inventario','movimientos','facturar','notasActivadas','rendimiento'].forEach(t=>{
        const btn=document.getElementById('caTab-'+t);
        const content=document.getElementById('caTabContent-'+t);
        if(btn)btn.classList.toggle('active',t===tab);
        if(content)content.style.display=t===tab?'':'none';
    });
    const id=window._caDetalleId;
    if(tab==='notas')loadNotasVinculadas(id);
    if(tab==='pagos')loadPagosCentro(id);
    if(tab==='activaciones')loadActivacionesCentro(id);
    if(tab==='inventario')loadInventarioCentro(id);
    if(tab==='movimientos')loadMovimientosCentro(id);
    if(tab==='facturar')loadFacturarCentro(id);
    if(tab==='notasActivadas')loadNotasActivadasCentro(id);
    if(tab==='rendimiento')loadRendimientoCentro(id);
}

// ── Notas vinculadas (incl. Centro Especial Por Asignar) ─────────
async function loadNotasVinculadas(centroId){
    const tb=document.getElementById('caNotasVinculadasBody');
    const c = _caData.find(x => String(x.id) === String(centroId));
    
    // Si es Centro Especial Virtual Por Asignar:
    if (c && c._isVirtual) {
        try {
            // Traer cantidad y activaciones_usadas para control de capacidad
            const rows = await _caSql(`
                SELECT v.id, v.fecha_venta, v.total, v.approved, v.comentario,
                       COALESCE(v.cantidad, 1) AS cantidad,
                       COALESCE(v.activaciones_usadas, 0) AS activaciones_usadas,
                       'Por Asignar (#230)' AS cliente_nombre
                FROM venta v
                WHERE v.cliente_id = '270a09e5-8250-44ea-82fa-b26dfc0553f0'
                  AND v.approved = 1 
                  AND (v.producto_id IS NULL OR v.producto_id != 'calificacion-mes')
                ORDER BY v.fecha_venta DESC
                LIMIT 300
            `);
            
            let actionBanner = `
                <div style="background:rgba(99,102,241,.12);border:1px solid rgba(99,102,241,.35);border-radius:10px;padding:.85rem 1.1rem;margin-bottom:1rem;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:.5rem;">
                    <div>
                        <h4 style="margin:0;color:#a5b4fc;font-size:0.92rem;display:flex;align-items:center;gap:.4rem;">⚡ Notas en Tránsito del Usuario "Por Asignar" (#230)</h4>
                        <p style="margin:4px 0 0 0;font-size:0.78rem;color:var(--text-dim);">Selecciona cualquier nota para generar la activación del mes del líder correspondiente ($0 Crédito deudor).</p>
                    </div>
                    <button class="btn btn-primary btn-sm" style="background:#6366f1;border:none;font-weight:600;padding:.4rem .9rem;" onclick="abrirModalAbsorberNotas()">📥 Abrir Panel de Absorción Global</button>
                </div>
            `;
            
            const existingBanner = tb?.parentElement?.parentElement?.querySelector('#caVirtualBanner');
            if (existingBanner) existingBanner.remove();
            
            const bannerDiv = document.createElement('div');
            bannerDiv.id = 'caVirtualBanner';
            bannerDiv.innerHTML = actionBanner;
            if(tb?.parentElement?.parentElement) {
                tb.parentElement.parentElement.insertBefore(bannerDiv, tb.parentElement);
            }

            if(!rows.length){
                tb.innerHTML='<tr><td colspan="7" style="text-align:center;color:var(--text-dim);padding:1rem;">Sin notas por asignar pendientes</td></tr>';
                return;
            }
            tb.innerHTML = rows.map(r=>{
                const destMatch = (r.comentario||'').match(/Para:\s*([^|]+)/i) || (r.comentario||'').match(/ACTIVOS_TERCEROS\s*([^$]+)/i);
                const destNombre = destMatch ? destMatch[1].trim() : 'Sin destinatario explícito';
                const estadoBadge = Number(r.approved)===1?'<span style="color:#22c55e;font-size:.78rem;">✓ Pagada (Por Asignar)</span>':'<span style="color:#f59e0b;">⏳ Pendiente</span>';
                
                const safeCom = (r.comentario||'').replace(/'/g,"\\'");
                const safeDest = destNombre.replace(/'/g,"\\'");
                // ── Control de capacidad por nota ──
                const cajas = Math.max(1, Math.round(parseFloat(r.cantidad) || 1));
                const usadas = parseInt(r.activaciones_usadas) || 0;
                const disponibles = cajas - usadas;
                const agotada = disponibles <= 0;
                const excedida = usadas > cajas;
                const cajasBadge = excedida
                    ? `<span style="background:rgba(239,68,68,.15);color:#ef4444;border:1px solid rgba(239,68,68,.3);border-radius:4px;padding:.15rem .45rem;font-size:.7rem;font-weight:700;">⚠ ${usadas}/${cajas} excedida</span>`
                    : agotada
                        ? `<span style="background:rgba(239,68,68,.1);color:#ef4444;border:1px solid rgba(239,68,68,.25);border-radius:4px;padding:.15rem .45rem;font-size:.7rem;font-weight:600;">🔴 ${usadas}/${cajas} agotada</span>`
                        : `<span style="background:rgba(34,197,94,.1);color:#22c55e;border:1px solid rgba(34,197,94,.25);border-radius:4px;padding:.15rem .45rem;font-size:.7rem;font-weight:600;">🟢 ${usadas}/${cajas} disp.</span>`;
                const btnActivar = agotada
                    ? `<button class="btn btn-sm" disabled style="font-size:.72rem;padding:.25rem .6rem;background:rgba(255,255,255,.06);color:var(--text-dim);border:1px solid var(--border);cursor:not-allowed;" title="Nota sin activaciones disponibles">✓ Agotada</button>`
                    : `<button class="btn btn-primary btn-sm" style="font-size:.72rem;padding:.25rem .6rem;background:#6366f1;border:none;" onclick="activarLiderDesdeNota('${r.id}')">⚡ Activar (${disponibles} disp.)</button>`;

                return `<tr>
                <td><span style="font-family:monospace;font-size:.78rem;">${(r.id||'').toString().substring(0,12)}</span></td>
                <td>${r.fecha_venta?new Date(r.fecha_venta).toLocaleDateString('es-MX'):'—'}</td>
                <td><strong>${r.cliente_nombre}</strong><div style="font-size:.72rem;color:#a5b4fc;">📌 ${destNombre}</div></td>
                <td style="text-align:right;">${_caMoneyFmt(r.total,'MXN')}</td>
                <td>${cajasBadge}</td>
                <td>${estadoBadge}</td>
                <td style="white-space:nowrap;">${btnActivar}</td>
            </tr>`;
            }).join('');
            return;
        } catch(e) {
            tb.innerHTML='<tr><td colspan="6" style="color:#ef4444;">Error: '+e.message+'</td></tr>';
            return;
        }
    } else {
        const existingBanner = tb?.parentElement?.parentElement?.querySelector('#caVirtualBanner');
        if (existingBanner) existingBanner.remove();
    }

    try{
        const rows=await _caSql(`SELECT v.id, v.fecha_venta, v.total, v.approved, v.comentario, COALESCE(c.nombre || ' ' || COALESCE(c.ape_p,''), 'Público General') AS cliente_nombre FROM venta v LEFT JOIN cliente c ON c.id = v.cliente_id WHERE v.centro_apoyo_id=${centroId} ORDER BY v.fecha_venta DESC`);
        if(!rows.length){tb.innerHTML='<tr><td colspan="6" style="text-align:center;color:var(--text-dim);padding:1rem;">Sin notas vinculadas aún</td></tr>';return;}
        tb.innerHTML=rows.map(r=>{
            const estadoBadge=Number(r.approved)===1?'<span style="color:#22c55e;">✓ Aprobada</span>':'<span style="color:#f59e0b;">⏳ Pendiente</span>';
            return `<tr>
            <td><span style="font-family:monospace;font-size:.78rem;">${(r.id||'').toString().substring(0,12)}</span></td>
            <td>${r.fecha_venta?new Date(r.fecha_venta).toLocaleDateString('es-MX'):'—'}</td>
            <td>${r.cliente_nombre||'—'}</td>
            <td style="text-align:right;">${_caMoneyFmt(r.total,'MXN')}</td>
            <td>${estadoBadge}</td>
            <td><button class="btn btn-outline btn-sm" style="color:#ef4444;border-color:#ef4444;font-size:.72rem;" onclick="desvincularNotaCentro('${r.id}',${centroId})">✕</button></td>
        </tr>`;
        }).join('');
    }catch(e){tb.innerHTML='<tr><td colspan="6" style="color:#ef4444;">Error: '+e.message+'</td></tr>';}
}

// ── Activar Líder desde Nota Por Asignar ─────────────────────────
// Abre popup propio (sin prompt/confirm del navegador)
async function activarLiderDesdeNota(notaId, destNombre, comentario, cajas, usadas){
    // 1. Verificar capacidad en BD (doble-check)
    try {
        const notaRow = (window._paNotasCache || []).find(x => x.id === notaId);
        const notaActual = await _caSql(`SELECT id, comentario, COALESCE(cantidad,1) AS cajas, COALESCE(activaciones_usadas,0) AS usadas FROM venta WHERE id='${notaId}'`);
        const cajasReal  = Math.max(1, Math.round(parseFloat(notaActual[0]?.cajas) || 1));
        const usadasReal = parseInt(notaActual[0]?.usadas) || 0;
        if(cajasReal - usadasReal <= 0){
            BO_AUTH.showAlert(`🔒 Esta nota ya agotó sus ${cajasReal} activación(es). Sin cupo disponible.`, 'warning');
            return;
        }
        const fullComentario = notaActual[0]?.comentario || notaRow?.comentario || comentario || '';
        const destMatch = fullComentario.match(/Para:\s*([^|]+)/i) || fullComentario.match(/ACTIVOS_TERCEROS\s*([^$]+)/i);
        const finalDestNombre = (destNombre && destNombre !== 'Sin destinatario explícito') ? destNombre : (destMatch ? destMatch[1].trim() : 'Sin destinatario explícito');

        // 2. Abrir popup con los datos actuales de BD
        _paAbrirPopupActivar({ notaId, destNombre: finalDestNombre, cajasReal, usadasReal });
    } catch(e){
        BO_AUTH.showAlert('❌ Error al verificar nota: ' + e.message, 'danger');
    }
}

// ── Popup de búsqueda y confirmación de activación (Soporte Multi-Caja) ─────────────
function _paAbrirPopupActivar({ notaId, destNombre, cajasReal, usadasReal }){
    const disponibles = Math.max(1, cajasReal - usadasReal);
    const sugerencia  = (destNombre && destNombre !== 'Sin destinatario explícito') ? destNombre : '';

    // Crear popup si no existe
    let p = document.getElementById('popupActivarLider');
    if(!p){
        p = document.createElement('div');
        p.id = 'popupActivarLider';
        p.style.cssText = 'display:none;position:fixed;inset:0;background:rgba(0,0,0,.75);z-index:9200;align-items:center;justify-content:center;padding:1rem;backdrop-filter:blur(6px);';
        p.innerHTML = `
        <div style="background:#1e2030;border:1px solid rgba(255,255,255,.12);border-radius:14px;width:100%;max-width:540px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 50px rgba(0,0,0,.6);">
          <!-- Header -->
          <div style="padding:1rem 1.2rem;border-bottom:1px solid rgba(255,255,255,.07);display:flex;justify-content:space-between;align-items:center;">
            <div>
              <div style="font-weight:700;font-size:.95rem;" id="popActTitulo">⚡ Asignar Activación</div>
              <div id="popActNota" style="font-size:.72rem;color:#a5b4fc;margin-top:2px;font-family:monospace;"></div>
            </div>
            <button onclick="_paCerrarPopupActivar()" style="background:none;border:none;color:var(--text-dim);font-size:1.2rem;cursor:pointer;">✕</button>
          </div>

          <!-- Info nota -->
          <div id="popActInfo" style="padding:.75rem 1.2rem;background:rgba(99,102,241,.07);border-bottom:1px solid rgba(255,255,255,.05);font-size:.8rem;display:flex;gap:1.5rem;align-items:center;justify-content:space-between;flex-wrap:wrap;"></div>

          <!-- Slots Container (Scrollable) -->
          <div id="popActSlotsContainer" style="flex:1;overflow-y:auto;padding:1rem 1.2rem;display:flex;flex-direction:column;gap:.9rem;">
          </div>

          <!-- Error / aviso -->
          <div id="popActError" style="display:none;margin:0 1.2rem .5rem;padding:.6rem .9rem;background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.25);border-radius:8px;font-size:.78rem;color:#ef4444;"></div>

          <!-- Botones Footer -->
          <div style="padding:1rem 1.2rem;display:flex;gap:.6rem;justify-content:flex-end;border-top:1px solid rgba(255,255,255,.06);background:rgba(0,0,0,.15);">
            <button onclick="_paCerrarPopupActivar()" style="background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.1);border-radius:8px;padding:.5rem 1.1rem;color:var(--text-dim);font-size:.85rem;cursor:pointer;">Cancelar</button>
            <button id="popActConfirmar" onclick="_paEjecutarActivacion()" disabled
              style="background:#6366f1;border:none;border-radius:8px;padding:.5rem 1.3rem;color:#fff;font-size:.85rem;font-weight:700;cursor:not-allowed;opacity:.5;transition:opacity .2s;">
              ⚡ Confirmar Activación
            </button>
          </div>
        </div>`;
        document.body.appendChild(p);
    }

    // Inicializar slots en contexto
    const _hoy = new Date();
    const _hoyStr = _hoy.getFullYear() + '-' + String(_hoy.getMonth()+1).padStart(2,'0') + '-' + String(_hoy.getDate()).padStart(2,'0');

    window._paActivarCtx = {
        notaId,
        cajasReal,
        usadasReal,
        disponibles,
        slots: Array.from({ length: disponibles }, (_, i) => ({
            index: i,
            cajaNum: usadasReal + i + 1,
            lider: null,
            fecha: _hoyStr
        }))
    };

    document.getElementById('popActNota').textContent = `Nota: ${notaId.substring(0,14)}…`;
    document.getElementById('popActTitulo').textContent = disponibles > 1 ? `⚡ Asignar Activaciones (${disponibles} Cajas Disponibles)` : `⚡ Asignar Activación`;
    document.getElementById('popActInfo').innerHTML = `
        <span>📦 <strong style="color:#fbbf24;">${disponibles}</strong> caja(s) disponible(s)</span>
        <span style="color:var(--text-dim);">Usadas: ${usadasReal}/${cajasReal}</span>
        ${sugerencia ? `<span style="color:#a5b4fc;font-size:.75rem;font-style:italic;" title="${sugerencia}">Destino sugerido: ${sugerencia.substring(0,25)}</span>` : ''}
    `;

    document.getElementById('popActError').style.display = 'none';

    _paRenderSlotsUI(sugerencia);
    p.style.display = 'flex';
}

function _paRenderSlotsUI(sugerencia = ''){
    const container = document.getElementById('popActSlotsContainer');
    if(!container || !window._paActivarCtx) return;
    const { slots, disponibles, cajasReal } = window._paActivarCtx;

    container.innerHTML = slots.map((slot, idx) => {
        const isMulti = disponibles > 1;
        const title = isMulti ? `📦 Caja ${slot.cajaNum} de ${cajasReal}` : `Líder a Activar`;
        
        return `
        <div id="popActSlotCard_${idx}" style="background:rgba(255,255,255,.03);border:1px solid ${slot.lider ? 'rgba(34,197,94,.35)' : 'rgba(255,255,255,.08)'};border-radius:10px;padding:.85rem 1rem;position:relative;">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:.6rem;">
                <span style="font-size:.82rem;font-weight:700;color:${slot.lider ? '#4ade80' : '#a5b4fc'};">${title}</span>
                ${slot.lider ? `<button onclick="_paDeseleccionarLiderSlot(${idx})" style="background:none;border:none;color:#ef4444;font-size:.72rem;cursor:pointer;padding:0;font-weight:600;">✕ Cambiar líder</button>` : ''}
            </div>

            <!-- Búsqueda (si no tiene líder seleccionado) -->
            <div id="popActBusquedaWrap_${idx}" style="${slot.lider ? 'display:none;' : ''}">
                <label style="font-size:.72rem;color:var(--text-dim);display:block;margin-bottom:.3rem;">Buscar líder por nombre o registro:</label>
                <input id="popActBuscar_${idx}" type="text" placeholder="Ej: Lupita Martínez o 70576"
                  autocomplete="off"
                  style="width:100%;box-sizing:border-box;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.15);border-radius:8px;padding:.45rem .75rem;color:inherit;font-size:.84rem;outline:none;"
                  oninput="_paOnBuscarInputSlot(${idx}, this.value)"
                >
                <div id="popActResultados_${idx}" style="margin-top:.35rem;border-radius:8px;overflow:hidden;border:1px solid rgba(255,255,255,.08);display:none;max-height:160px;overflow-y:auto;background:#181926;z-index:10;"></div>
            </div>

            <!-- Líder Seleccionado Chip -->
            <div id="popActSeleccionado_${idx}" style="${slot.lider ? '' : 'display:none;'}padding:.6rem .8rem;background:rgba(34,197,94,.08);border:1px solid rgba(34,197,94,.25);border-radius:8px;margin-bottom:.6rem;">
                <div style="font-size:.68rem;color:#22c55e;font-weight:600;margin-bottom:.15rem;">✓ LÍDER ASIGNADO</div>
                <div id="popActSelNombre_${idx}" style="font-weight:700;font-size:.85rem;color:#fff;">${slot.lider ? `${slot.lider.nombre} ${slot.lider.ap}` : ''}</div>
                <div id="popActSelReg_${idx}" style="font-size:.72rem;color:var(--text-dim);">${slot.lider ? `Registro: #${slot.lider.registro}` : ''}</div>
            </div>

            <!-- Fecha de Activación -->
            <div style="display:flex;align-items:center;gap:.75rem;margin-top:.5rem;">
                <label style="font-size:.72rem;color:var(--text-dim);white-space:nowrap;">Fecha:</label>
                <input id="popActFecha_${idx}" type="date" value="${slot.fecha}"
                  style="flex:1;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);border-radius:6px;padding:.35rem .65rem;color:inherit;font-size:.78rem;outline:none;"
                  onchange="if(window._paActivarCtx && window._paActivarCtx.slots[${idx}]) window._paActivarCtx.slots[${idx}].fecha = this.value"
                >
            </div>
        </div>`;
    }).join('');

    _paActualizarBotonConfirmar();

    // Si hay sugerencia para el primer slot y aún no tiene líder, buscar automático
    if(sugerencia && slots[0] && !slots[0].lider){
        const inp0 = document.getElementById('popActBuscar_0');
        if(inp0){
            inp0.value = sugerencia;
            _paOnBuscarInputSlot(0, sugerencia);
        }
    }
}

function _paCerrarPopupActivar(){
    const p = document.getElementById('popupActivarLider');
    if(p) p.style.display = 'none';
    window._paActivarCtx = null;
}

let _paBuscarTimerSlot = {};
function _paOnBuscarInputSlot(slotIdx, val){
    clearTimeout(_paBuscarTimerSlot[slotIdx]);
    if(window._paActivarCtx && window._paActivarCtx.slots[slotIdx]){
        window._paActivarCtx.slots[slotIdx].lider = null;
    }
    _paActualizarBotonConfirmar();
    const box = document.getElementById(`popActResultados_${slotIdx}`);
    if(!val || val.trim().length < 2){
        if(box) box.style.display = 'none';
        return;
    }
    if(box){
        box.style.display = 'block';
        box.innerHTML = '<div style="padding:.5rem .7rem;font-size:.75rem;color:var(--text-dim);">Buscando…</div>';
    }
    _paBuscarTimerSlot[slotIdx] = setTimeout(() => _paBuscarLideresSlot(slotIdx, val.trim()), 300);
}

async function _paBuscarLideresSlot(slotIdx, q){
    const box = document.getElementById(`popActResultados_${slotIdx}`);
    if(!box) return;
    try {
        const qEsc = q.replace(/'/g, "''");
        const res = await _caSql(`
            SELECT id, registro, nombre, COALESCE(ape_p,'') AS ap
            FROM cliente
            WHERE (COALESCE(nombre,'') || ' ' || COALESCE(ape_p,'')) ILIKE '%${qEsc}%'
               OR registro::text = '${qEsc}'
               OR id::text = '${qEsc}'
            ORDER BY registro ASC
            LIMIT 8
        `);
        if(!res || !res.length){
            box.innerHTML = '<div style="padding:.5rem .7rem;font-size:.75rem;color:var(--text-dim);">Sin resultados — intenta con el número de registro exacto.</div>';
            return;
        }
        box.innerHTML = res.map(l => `
            <div onclick="_paSeleccionarLiderSlot(${slotIdx}, ${JSON.stringify(l).replace(/"/g,'&quot;')})"
              style="padding:.45rem .7rem;font-size:.78rem;cursor:pointer;border-bottom:1px solid rgba(255,255,255,.04);display:flex;justify-content:space-between;align-items:center;"
              onmouseover="this.style.background='rgba(99,102,241,.18)'" onmouseout="this.style.background=''">
              <span><strong>${l.nombre} ${l.ap}</strong></span>
              <span style="color:#a5b4fc;font-size:.72rem;font-family:monospace;">#${l.registro}</span>
            </div>`).join('');
    } catch(e){
        box.innerHTML = `<div style="padding:.5rem;color:#ef4444;font-size:.75rem;">Error: ${e.message}</div>`;
    }
}

function _paSeleccionarLiderSlot(slotIdx, lider){
    if(!window._paActivarCtx || !window._paActivarCtx.slots[slotIdx]) return;
    window._paActivarCtx.slots[slotIdx].lider = lider;
    _paRenderSlotsUI();
}

function _paDeseleccionarLiderSlot(slotIdx){
    if(!window._paActivarCtx || !window._paActivarCtx.slots[slotIdx]) return;
    window._paActivarCtx.slots[slotIdx].lider = null;
    _paRenderSlotsUI();
}

function _paActualizarBotonConfirmar(){
    const btn = document.getElementById('popActConfirmar');
    if(!btn || !window._paActivarCtx) return;
    const asignados = window._paActivarCtx.slots.filter(s => s.lider).length;
    btn.disabled = asignados === 0;
    btn.style.opacity = asignados > 0 ? '1' : '.5';
    btn.style.cursor = asignados > 0 ? 'pointer' : 'not-allowed';
    btn.textContent = asignados > 1 ? `⚡ Confirmar ${asignados} Activaciones` : (asignados === 1 ? `⚡ Confirmar 1 Activación` : `⚡ Confirmar Activación`);
}

// Aliases para compatibilidad legacy
function _paOnBuscarInput(val){ _paOnBuscarInputSlot(0, val); }
function _paBuscarLideres(q){ _paBuscarLideresSlot(0, q); }
function _paSeleccionarLider(lider){ _paSeleccionarLiderSlot(0, lider); }
function _paSetConfirmarBtn(enabled){ _paActualizarBotonConfirmar(); }

// ── Diálogo de confirmación para activaciones adicionales (Opción B) ─────────────
function _paMostrarConfirmacionDuplicados(duplicados){
    const existing = document.getElementById('popConfDuplicados');
    if(existing) existing.remove();

    const listaHtml = duplicados.map(d => `
        <div style="display:flex;justify-content:space-between;align-items:center;padding:.4rem 0;border-bottom:1px solid rgba(255,255,255,.06);">
            <div>
                <strong style="color:#fff;">${d.lider.nombre} ${d.lider.ap}</strong>
                <span style="color:#a5b4fc;font-size:.74rem;font-family:monospace;margin-left:4px;">#${d.lider.registro}</span>
            </div>
            <span style="color:#fbbf24;font-size:.72rem;background:rgba(245,158,11,.1);padding:2px 6px;border-radius:4px;border:1px solid rgba(245,158,11,.25);">${d.motivo}</span>
        </div>
    `).join('');

    const overlay = document.createElement('div');
    overlay.id = 'popConfDuplicados';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.82);z-index:9350;display:flex;align-items:center;justify-content:center;padding:1rem;backdrop-filter:blur(6px);';
    overlay.innerHTML = `
    <div style="background:#1e2030;border:1px solid rgba(245,158,11,.4);border-radius:14px;max-width:500px;width:100%;padding:1.4rem;box-shadow:0 24px 60px rgba(0,0,0,.8);">
        <div style="display:flex;align-items:center;gap:.6rem;margin-bottom:.8rem;">
            <span style="font-size:1.4rem;">⚠️</span>
            <div style="font-weight:700;font-size:1rem;color:#fbbf24;">Activación Adicional Detectada</div>
        </div>
        <div style="font-size:.82rem;color:var(--text-dim);margin-bottom:.8rem;line-height:1.5;">
            Los siguientes líderes <strong>ya cuentan con una activación registrada</strong> en el periodo seleccionado:
        </div>
        <div style="background:rgba(0,0,0,.3);border:1px solid rgba(255,255,255,.06);border-radius:8px;padding:.5rem .8rem;margin-bottom:1rem;font-size:.8rem;max-height:180px;overflow-y:auto;">
            ${listaHtml}
        </div>
        <div style="font-size:.8rem;color:#cbd5e1;margin-bottom:1.2rem;line-height:1.5;">
            ¿Deseas registrarles una <strong>caja adicional en este mismo mes</strong> o prefieres programarlas para el <strong>mes siguiente</strong>?
        </div>
        <div style="display:flex;flex-direction:column;gap:.5rem;">
            <button id="btnConfExtraMes"
              style="background:#6366f1;color:#fff;border:none;border-radius:8px;padding:.65rem 1rem;font-weight:700;font-size:.84rem;cursor:pointer;transition:background .2s;"
              onmouseover="this.style.background='#4f46e5'" onmouseout="this.style.background='#6366f1'">
              ⚡ Sí, registrar caja extra en este mes
            </button>
            <button id="btnConfMoverMes"
              style="background:rgba(59,130,246,.15);border:1px solid rgba(59,130,246,.35);color:#60a5fa;border-radius:8px;padding:.65rem 1rem;font-weight:600;font-size:.84rem;cursor:pointer;transition:background .2s;"
              onmouseover="this.style.background='rgba(59,130,246,.25)'" onmouseout="this.style.background='rgba(59,130,246,.15)'">
              📅 Mover al mes siguiente automáticamente
            </button>
            <button onclick="document.getElementById('popConfDuplicados').remove()"
              style="background:transparent;border:1px solid rgba(255,255,255,.1);color:var(--text-dim);border-radius:8px;padding:.5rem 1rem;font-size:.8rem;cursor:pointer;margin-top:.2rem;">
              Volver a revisar
            </button>
        </div>
    </div>`;

    document.body.appendChild(overlay);

    document.getElementById('btnConfExtraMes').onclick = () => {
        overlay.remove();
        _paEjecutarActivacion(true);
    };

    document.getElementById('btnConfMoverMes').onclick = () => {
        _paMoverDuplicadosMesSiguiente(duplicados);
    };
}

function _paMoverDuplicadosMesSiguiente(duplicados){
    const dialog = document.getElementById('popConfDuplicados');
    if(dialog) dialog.remove();
    if(!window._paActivarCtx || !window._paActivarCtx.slots) return;

    duplicados.forEach(d => {
        const slot = window._paActivarCtx.slots[d.slotIndex];
        if(slot){
            const f = slot.fecha ? new Date(slot.fecha + 'T12:00:00') : new Date();
            f.setMonth(f.getMonth() + 1);
            f.setDate(1); // 1er día del mes siguiente
            const y = f.getFullYear();
            const m = String(f.getMonth() + 1).padStart(2, '0');
            const day = String(f.getDate()).padStart(2, '0');
            slot.fecha = `${y}-${m}-${day}`;
        }
    });

    _paRenderSlotsUI();
    BO_AUTH.showAlert('📅 Se actualizaron las fechas de los líderes señalados al mes siguiente (día 1). Revisa y confirma cuando estés listo.', 'info');
}

async function _paEjecutarActivacion(forzar = false){
    const ctx = window._paActivarCtx;
    if(!ctx || !ctx.slots) return;
    const slotsAsignados = ctx.slots.filter(s => s.lider);
    if(!slotsAsignados.length) return;

    const { notaId, cajasReal } = ctx;
    const errEl = document.getElementById('popActError');
    if(errEl) errEl.style.display = 'none';

    const btn = document.getElementById('popActConfirmar');
    if(btn){
        btn.disabled = true;
        btn.textContent = '⏳ Procesando activaciones…';
    }

    try {
        // 1. Anti-doble-click: reverificar capacidad en BD
        const recheck = await _caSql(`SELECT COALESCE(activaciones_usadas,0) AS usadas, COALESCE(cantidad,1) AS cajas FROM venta WHERE id='${notaId}'`);
        const usadasNow = parseInt(recheck[0]?.usadas) || 0;
        const cajasNow  = Math.max(1, Math.round(parseFloat(recheck[0]?.cajas) || 1));
        const disponiblesNow = cajasNow - usadasNow;

        if(slotsAsignados.length > disponiblesNow){
            throw new Error(`La nota solo tiene ${disponiblesNow} caja(s) disponible(s) actualmente.`);
        }

        // 2. Verificar duplicados por cada líder en su mes respectivo (Aviso Opción B)
        if(!forzar){
            const duplicados = [];
            const conteoEnModal = {};

            for(const s of slotsAsignados){
                const f = s.fecha ? new Date(s.fecha + 'T12:00:00') : new Date();
                const mesStr = f.getFullYear() + '-' + String(f.getMonth()+1).padStart(2,'0');
                const key = `${s.lider.id}_${mesStr}`;

                conteoEnModal[key] = (conteoEnModal[key] || 0) + 1;
                let motivo = '';

                if(conteoEnModal[key] > 1){
                    motivo = `Asignado ${conteoEnModal[key]} veces en esta nota para ${mesStr}`;
                } else {
                    const actCheck = await _caSql(`
                        SELECT id FROM venta
                        WHERE cliente_id = '${s.lider.id}'
                          AND producto_id = 'calificacion-mes'
                          AND TO_CHAR(fecha_venta,'YYYY-MM') = '${mesStr}'
                          AND approved = 1
                    `);
                    if(actCheck && actCheck.length > 0){
                        motivo = `Ya cuenta con ${actCheck.length} activación(es) en BD en ${mesStr}`;
                    }
                }

                if(motivo){
                    duplicados.push({
                        slotIndex: s.index,
                        lider: s.lider,
                        mesStr,
                        motivo
                    });
                }
            }

            if(duplicados.length > 0){
                if(btn){
                    btn.disabled = false;
                    _paActualizarBotonConfirmar();
                }
                _paMostrarConfirmacionDuplicados(duplicados);
                return;
            }
        }

        // 3. Crear nota de activación para cada slot asignado
        for(const s of slotsAsignados){
            const f = s.fecha ? new Date(s.fecha + 'T12:00:00') : new Date();
            const fechaSql = f.getFullYear() + '-' +
                             String(f.getMonth()+1).padStart(2,'0') + '-' +
                             String(f.getDate()).padStart(2,'0');
            const newId = 'ACT-PA-' + Math.random().toString(36).substring(2,10).toUpperCase();

            await _caSql(`
                INSERT INTO venta (id, cliente_id, total, cantidad, approved, fecha_venta, producto_id, comentario)
                VALUES ('${newId}','${s.lider.id}',0.00,1,1,'${fechaSql}','calificacion-mes',
                'ACTIVACION PAGO ADQUIRIDO || RESPALDO NOTA ${notaId} (Caja ${s.cajaNum}/${cajasReal}) || LIDER #${s.lider.registro} ${s.lider.nombre} ${s.lider.ap}')
            `);
        }

        // 4. Descontar cupo en la nota origen
        await _caSql(`UPDATE venta SET activaciones_usadas = COALESCE(activaciones_usadas,0) + ${slotsAsignados.length} WHERE id='${notaId}'`);

        _paCerrarPopupActivar();
        BO_AUTH.showAlert(
            `✅ ${slotsAsignados.length} activación(es) generada(s) exitosamente.\n📦 Cupo restante en nota: ${cajasNow - usadasNow - slotsAsignados.length}/${cajasNow}`,
            'success'
        );

        // 5. Recargar panel correcto
        if(document.getElementById('modalColaActivaciones')?.style.display === 'flex'){
            await loadColaActivaciones();
        } else {
            await loadNotasVinculadas(window._caDetalleId || _caCobrarCentroId);
        }
    } catch(e){
        if(errEl){
            errEl.textContent = '❌ ' + e.message;
            errEl.style.display = 'block';
        } else {
            BO_AUTH.showAlert('❌ ' + e.message, 'danger');
        }
    } finally {
        if(btn){
            btn.disabled = false;
            _paActualizarBotonConfirmar();
        }
    }
}

// ── Buscar notas para vincular ────────────────────────────────
let _caNotaTimer=null;
async function buscarNotasParaVincular(q){
    clearTimeout(_caNotaTimer);
    const box=document.getElementById('caBusquedaResultados');
    if(q.length<2){box.innerHTML='';return;}
    _caNotaTimer=setTimeout(async()=>{
        try{
            const qEsc=q.replace(/'/g,"''");
            const dias=parseInt(document.getElementById('caBuscadorPeriodo')?.value||'90');
            const dateFilter=dias>0?`AND v.fecha_venta >= NOW() - INTERVAL '${dias} days'`:'';
            const sql=`SELECT v.id, v.fecha_venta, v.total, v.approved, v.centro_apoyo_id, COALESCE(c.nombre || ' ' || COALESCE(c.ape_p,''), 'Público General') AS cliente_nombre FROM venta v LEFT JOIN cliente c ON c.id = v.cliente_id WHERE v.approved = 1 ${dateFilter} AND (v.id::text ILIKE '%${qEsc}%' OR (COALESCE(c.nombre,'') || ' ' || COALESCE(c.ape_p,'')) ILIKE '%${qEsc}%') ORDER BY v.fecha_venta DESC LIMIT 15`;
            const rows=await _caSql(sql);
            if(!rows.length){box.innerHTML='<div style="font-size:.8rem;color:var(--text-dim);padding:.5rem;">Sin resultados</div>';return;}
            box.innerHTML='<div style="background:rgba(99,102,241,.05);border:1px solid rgba(99,102,241,.2);border-radius:8px;overflow:hidden;">'+rows.map(r=>{
                const yaAsig=r.centro_apoyo_id&&r.centro_apoyo_id!==window._caDetalleId;
                const mismoC=r.centro_apoyo_id===window._caDetalleId;
                const fecha=r.fecha_venta?new Date(r.fecha_venta).toLocaleDateString('es-MX'):'';
                return `<div style="display:flex;justify-content:space-between;align-items:center;padding:.5rem .75rem;border-bottom:1px solid rgba(255,255,255,.05);font-size:.82rem;opacity:${mismoC?.5:1}">
                <div><span style="font-family:monospace;">${(r.id||'').toString().substring(0,12)}</span> · ${r.cliente_nombre||'—'} · ${_caMoneyFmt(r.total,'MXN')} <span style="color:var(--text-dim);">${fecha}${mismoC?' ✓ Ya vinculada aquí':yaAsig?' ⚠ En otro centro':''}</span></div>
                ${mismoC?'<span style="color:#22c55e;font-size:.72rem;">✓ Vinculada</span>':yaAsig?`<button class="btn btn-outline btn-sm" style="font-size:.72rem;padding:.2rem .5rem;border-color:#f59e0b;color:#f59e0b;" onclick="vincularNotaCentro('${r.id}',${window._caDetalleId})">↩ Reasignar</button>`:`<button class="btn btn-primary btn-sm" style="font-size:.72rem;padding:.2rem .5rem;" onclick="vincularNotaCentro('${r.id}',${window._caDetalleId})">➕ Vincular</button>`}
            </div>`;
            }).join('')+'</div>';
        }catch(e){box.innerHTML='<div style="color:#ef4444;">Error: '+e.message+'</div>';}
    },350);
}

async function vincularNotaCentro(ventaId,centroId){
    try{
        const venta=await _caSql(`SELECT * FROM venta WHERE id='${ventaId}'`);
        if(!venta.length){BO_AUTH.showAlert('⚠ Nota no encontrada','warning');return;}
        if(Number(venta[0].centro_apoyo_id)===centroId){
            BO_AUTH.showAlert('⚠ Esta nota ya está vinculada a este centro','warning');return;
        }
        if(venta[0].centro_apoyo_id && Number(venta[0].centro_apoyo_id)!==centroId){
            BO_AUTH.showAlert('⚠ Esta nota ya está vinculada a otro centro (ID: '+venta[0].centro_apoyo_id+')','warning');return;
        }
        let monto=Number(venta[0].total||0);
        const moneda=venta[0]?.moneda||'MXN';
        const esActivacion = venta[0].producto_id === 'calificacion-mes';
        
        if(!monto && venta[0]?.piezas){
            try{
                const pzStr=typeof venta[0].piezas==='string'?venta[0].piezas:JSON.stringify(venta[0].piezas);
                const pzs=JSON.parse(pzStr);
                monto=pzs.reduce((s,p)=>s+(Number(p.cantidad||0)*Number(p.precio_unitario||0)),0);
            }catch(_){}
        }
        if(!monto){
            const det=await _caSql(`SELECT COALESCE(SUM(cantidad * precio_unitario),0) AS calc FROM detalle_venta WHERE venta_id='${ventaId}'`);
            monto=Number(det[0]?.calc||0);
        }
        if(!monto){BO_AUTH.showAlert('⚠ La nota tiene total $0 — verifica los datos','warning');return;}
        await _caSql(`UPDATE venta SET centro_apoyo_id=${centroId} WHERE id='${ventaId}'`);
        // Incrementar deuda solo para notas reales (no activaciones)
        if(!esActivacion) await _recalcCreditoUsado(centroId, +monto);
        
        const user=CURRENT_USER?(CURRENT_USER.nombre+' '+CURRENT_USER.apellido).replace(/'/g,"''"):'admin';
        await _caSql(`INSERT INTO centro_apoyo_movimiento(centro_id,tipo,monto,moneda,descripcion,venta_id,registrado_por) VALUES(${centroId},'cargo',${monto},'${moneda}','Nota ${ventaId} vinculada','${ventaId}','${user}')`);
        
        if(!esActivacion) BO_AUTH.showAlert('Nota vinculada ✓ Balance recalculado','success');
        else BO_AUTH.showAlert('Activación vinculada ✓ (No genera deuda)','success');
        
        document.getElementById('caBuscadorNotas').value='';
        document.getElementById('caBusquedaResultados').innerHTML='';
        await loadCentrosApoyo();
        loadNotasVinculadas(centroId);
    }catch(e){BO_AUTH.showAlert('Error: '+e.message,'danger');}
}

async function desvincularNotaCentro(ventaId,centroId){
    try{
        const venta=await _caSql(`SELECT total, producto_id, factura_emitida, factura_uuid FROM venta WHERE id='${ventaId}'`);
        if (venta[0] && (venta[0].factura_emitida === true || (venta[0].factura_uuid && String(venta[0].factura_uuid).trim() !== ''))) {
            BO_AUTH.showAlert('⚠️ Esta nota ya fue timbrada en un CFDI ante el SAT. Usa "Sustituir" en Facturación para liberarla antes de desvincularla.', 'warning');
            return;
        }
        const monto=venta[0]?Number(venta[0].total||0):0;
        const esActivacion = venta[0] && venta[0].producto_id === 'calificacion-mes';
        
        await _caSql(`UPDATE venta SET centro_apoyo_id=NULL, requiere_factura=false WHERE id='${ventaId}'`);

        // Si la nota estaba incluida en alguna solicitud pendiente (GLOBAL-* o CENTRO-*), retirarla de ventas_ids
        try {
            const safeVid = String(ventaId).replace(/'/g, "''");
            const solsPend = await _caSql(`SELECT id, ventas_ids, comentario FROM facturas_solicitadas WHERE estado='solicitada' AND ventas_ids IS NOT NULL AND ventas_ids LIKE '[%' AND ventas_ids LIKE '%${safeVid}%'`);
            for (const sRow of (solsPend || [])) {
                let idsArr = [];
                try { idsArr = JSON.parse(sRow.ventas_ids || '[]'); } catch (_) {}
                if (!Array.isArray(idsArr)) continue;
                const kept = idsArr.filter(x => String(x) !== String(ventaId));
                if (kept.length === idsArr.length) continue;
                if (kept.length === 0) {
                    await _caSql(`UPDATE facturas_solicitadas SET ventas_ids='[]', estado='rechazada' WHERE id=${Number(sRow.id)}`);
                } else {
                    const nuevoCom = String(sRow.comentario || '').replace(/n=\d+/, 'n=' + kept.length).replace(/'/g, "''");
                    const keptJson = JSON.stringify(kept).replace(/'/g, "''");
                    await _caSql(`UPDATE facturas_solicitadas SET ventas_ids='${keptJson}', comentario='${nuevoCom}' WHERE id=${Number(sRow.id)}`);
                }
            }
        } catch (eClean) { console.warn('Aviso limpiando ventas_ids al desvincular nota:', eClean); }

        // Decrementar deuda (permite negativo = a favor, sin GREATEST)
        if(!esActivacion) await _recalcCreditoUsado(centroId, -monto);
        
        const user=CURRENT_USER?(CURRENT_USER.nombre+' '+CURRENT_USER.apellido).replace(/'/g,"''"):'admin';
        await _caSql(`INSERT INTO centro_apoyo_movimiento(centro_id,tipo,monto,descripcion,venta_id,registrado_por) VALUES(${centroId},'ajuste',${-monto},'Nota ${ventaId} desvinculada','${ventaId}','${user}')`);
        BO_AUTH.showAlert('Nota desvinculada ✓ Balance y solicitudes pendientes actualizados','success');
        await loadCentrosApoyo();loadNotasVinculadas(centroId);
        if(typeof loadHistorialFacturas === 'function') loadHistorialFacturas();
    }catch(e){BO_AUTH.showAlert('Error: '+e.message,'danger');}
}

// ── Inventario calculado ──────────────────────────────────────
async function loadInventarioCentro(centroId){
    const tb=document.getElementById('caInventarioBody');
    // ── Filtro de período ──────────────────────────────────────
    const periodoInv=Number(document.getElementById('caInvPeriodo')?.value||0);
    const whereInv=periodoInv>0?` AND v.created_at >= NOW() - INTERVAL '${periodoInv} months'`:'';
    const labelInv=document.getElementById('caInvPeriodo');
    try{
        // Get raw piezas from linked ventas (con filtro de período)
        const ventas=await _caSql(`SELECT v.piezas::text AS piezas FROM venta v WHERE v.centro_apoyo_id=${centroId}${whereInv}`);
        if(!ventas.length){tb.innerHTML='<tr><td colspan="3" style="text-align:center;color:var(--text-dim);padding:1rem;">Sin inventario — vincula notas primero</td></tr>';return;}
        
        // Load product catalog and components
        let catalogo=window.PRODUCTOS_CAT||[];
        let compData=window.PRODUCTOS_COMP||[];
        if(!catalogo.length||!compData.length){
            const [rCat,rComp]=await Promise.all([
                _caSql('SELECT id, sku, nombre, categoria FROM productos'),
                _caSql('SELECT pc.paquete_id, pc.componente_id, pc.cantidad, p.nombre AS comp_nombre FROM producto_componente pc JOIN productos p ON p.id = pc.componente_id')
            ]);
            catalogo=rCat||[];compData=rComp||[];
            window.PRODUCTOS_CAT=catalogo;window.PRODUCTOS_COMP=compData;
        }
        
        // Aggregate with paquete expansion
        const stock={};
        for(const v of ventas){
            let pzs=[];
            try{pzs=typeof v.piezas==='string'?JSON.parse(v.piezas):(v.piezas||[]);}catch(_){continue;}
            for(const p of pzs){
                const prodId=String(p.producto_id||'');
                const cat=catalogo.find(c=>String(c.id)===prodId);
                const qty=Number(p.cantidad||p.qty||0);
                if(cat&&cat.categoria==='paquete'){
                    const comps=compData.filter(c=>String(c.paquete_id)===String(cat.id));
                    if(comps.length){
                        for(const comp of comps){
                            const compQty=(parseInt(comp.cantidad)||1)*qty;
                            const key=comp.comp_nombre||comp.componente_id;
                            stock[key]=(stock[key]||0)+compQty;
                        }
                        continue;
                    }
                }
                const key=cat?.nombre||p.sku||prodId;
                stock[key]=(stock[key]||0)+qty;
            }
        }
        
        if(!Object.keys(stock).length){tb.innerHTML='<tr><td colspan="3" style="text-align:center;color:var(--text-dim);padding:1rem;">Sin productos</td></tr>';return;}
        tb.innerHTML=Object.entries(stock).sort((a,b)=>a[0].localeCompare(b[0])).map(([nombre,total])=>{
            return `<tr>
            <td>${nombre}</td>
            <td style="text-align:right;">${total}</td>
            <td style="text-align:right;">${total}</td>
        </tr>`;
        }).join('');
    }catch(e){tb.innerHTML='<tr><td colspan="3" style="color:#ef4444;">Error: '+e.message+'</td></tr>';}
}

// ── Movimientos de crédito ────────────────────────────────────
async function loadMovimientosCentro(centroId){
    const tb=document.getElementById('caMovimientosBody');
    // ── Filtro de mes ──────────────────────────────────────────
    const mesInputMov=document.getElementById('caMovMesFiltro');
    const verTodoMovEl=document.getElementById('caMovVerTodo');
    if(mesInputMov && !mesInputMov.value){
        const now=new Date();
        mesInputMov.value=now.getFullYear()+'-'+String(now.getMonth()+1).padStart(2,'0');
        if(verTodoMovEl && !verTodoMovEl.dataset.touched) { verTodoMovEl.checked=true; verTodoMovEl.dataset.touched='1'; }
    }
    const verTodoMov=verTodoMovEl?.checked||false;
    const mesFiltroMov=(!verTodoMov && mesInputMov?.value)?mesInputMov.value:null;
    const whereMesMov=mesFiltroMov?` AND TO_CHAR(creado_en,'YYYY-MM')='${mesFiltroMov}'`:'';
    // Actualizar badge de período
    const movLabelEl=document.getElementById('caMovPeriodo-label');
    if(movLabelEl){
        const meses=['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
        if(mesFiltroMov){
            const [y,m]=mesFiltroMov.split('-');
            movLabelEl.textContent=meses[parseInt(m)-1]+' '+y;
            movLabelEl.style.display='inline-block';
        } else {
            movLabelEl.textContent='Histórico completo';
            movLabelEl.style.display='inline-block';
        }
    }
    try{
        const rows=await _caSql(`SELECT * FROM centro_apoyo_movimiento WHERE centro_id=${centroId}${whereMesMov} ORDER BY creado_en DESC`);
        if(!rows.length){tb.innerHTML='<tr><td colspan="6" style="text-align:center;color:var(--text-dim);padding:1rem;">Sin movimientos</td></tr>';return;}
        const tipoBadge=t=>({
            cargo:'<span style="color:#ef4444;">📤 Cargo</span>',
            abono:'<span style="color:#22c55e;">📥 Abono</span>',
            ajuste:'<span style="color:#a5b4fc;">🔄 Ajuste</span>',
            aumento_limite:'<span style="color:#f59e0b;">📈 Aumento</span>',
            reverso:'<span style="color:#f59e0b;">↩️ Reverso</span>'
        }[t]||t);
        tb.innerHTML=rows.map(r=>{
            const esAbono=r.tipo==='abono';
            const safeDesc=(r.descripcion||'').replace(/'/g,"\\'").replace(/"/g,"&quot;");
            const btnReversar=esAbono?`<button class="btn btn-outline btn-sm" style="font-size:.68rem;color:#f59e0b;border-color:rgba(245,158,11,.4);padding:2px 8px;" onclick="reversarCobro(${r.id},${centroId},${Math.abs(r.monto)},'${(r.lider_ids||'').replace(/'/g,"\\'")}','${safeDesc}',this)">↩️ Reversar</button>`:'';
            return `<tr>
            <td>${r.creado_en?new Date(r.creado_en).toLocaleDateString('es-MX'):'—'}</td>
            <td>${tipoBadge(r.tipo)}</td>
            <td style="text-align:right;font-weight:600;color:${Number(r.monto)>=0?'#ef4444':'#22c55e'};">${_caMoneyFmt(Math.abs(r.monto),r.moneda)}</td>
            <td style="font-size:.78rem;">${r.descripcion||'—'}</td>
            <td style="font-size:.78rem;">${r.registrado_por||'—'}</td>
            <td style="text-align:center;">${btnReversar}</td>
        </tr>`;
        }).join('');
    }catch(e){tb.innerHTML='<tr><td colspan="6" style="color:#ef4444;">Error: '+e.message+'</td></tr>';}
}

let _reversandoCobroId = null;
async function reversarCobro(movId,centroId,monto,liderIds,descripcion,btnEl){
    if(_reversandoCobroId === movId) return;
    if(!confirm(`¿Estás seguro de reversar este cobro de activos por ${_caMoneyFmt(monto)}?\n\nLos activos se devolverán a los líderes y se restaurará el saldo en el Centro.`)) return;

    const btn = btnEl || event?.target;
    if(btn){
        btn.disabled = true;
        btn.textContent = '⏳ Reversando...';
    }
    _reversandoCobroId = movId;

    try{
        // 0. Validar que el movimiento siga existiendo (evita ejecuciones dobles)
        const movRows = await _caSql(`SELECT id, creado_en FROM centro_apoyo_movimiento WHERE id=${movId} AND centro_id=${centroId}`);
        if(!movRows || !movRows.length) {
            BO_AUTH.showAlert('Este movimiento ya fue reversado o no existe.', 'warning');
            return;
        }
        const movFecha = movRows[0]?.creado_en ? new Date(movRows[0].creado_en).toISOString().slice(0, 10) : '';

        const user=CURRENT_USER?CURRENT_USER.nombre+' '+(CURRENT_USER.apellido||CURRENT_USER.ape_p||''):'admin';
        const _userEsc=user.replace(/'/g,"''");
        const _descEsc=('REVERSO: '+descripcion).replace(/'/g,"''");

        // 1. Devolver activos a cada líder (eliminar débitos de activos_transacciones)
        // Intentar primero por enlace directo venta_id='CAMOV-${movId}' o tag en descripcion
        let deletedActivos = await _caSql(`DELETE FROM activos_transacciones WHERE venta_id = 'CAMOV-${movId}' OR descripcion LIKE '%[CAMOV:${movId}]%' RETURNING id`);
        const hasDeleted = Array.isArray(deletedActivos) && deletedActivos.some(r => r && (r.id !== undefined && r.id !== null));
        if(!hasDeleted && liderIds){
            const liders=liderIds.split(',').map(l=>l.trim()).filter(Boolean);
            for(const reg of liders){
                // Búsqueda de respaldo acotada al líder, tipo débito y ventana de búsqueda tolerante a desfases de fecha (hasta 60 días)
                const descSnippet = (descripcion||'').replace(/'/g,"''").substring(0,25);
                let fallbackWhere = `user_id = ${reg} AND tipo = 'debito'`;
                if(descSnippet) fallbackWhere += ` AND (descripcion ILIKE '%${descSnippet}%' OR descripcion ILIKE '%#${reg}%')`;
                if(movFecha) fallbackWhere += ` AND created_at >= ('${movFecha}'::date - INTERVAL '60 days') AND created_at <= ('${movFecha}'::date + INTERVAL '2 days')`;
                await _caSql(`DELETE FROM activos_transacciones WHERE id = (SELECT id FROM activos_transacciones WHERE ${fallbackWhere} ORDER BY created_at DESC LIMIT 1)`);
            }
        }

        // 2. Eliminar el registro vinculado en centro_apoyo_pago (evita pagos fantasmas en facturación)
        // Extraer folio / referencia bancaria si existe en la descripción (ej: 'Ref: 180647963779')
        const refMatch = (descripcion || '').match(/Ref:\s*([^\s,\-]+)/i);
        const refVal = refMatch ? refMatch[1].trim().replace(/'/g, "''") : '';
        let pagoWhere = `referencia = 'CAMOV-${movId}' OR (centro_id = ${centroId} AND tipo_pago = 'Cobro Activos' AND descripcion ILIKE '%${descripcion.replace(/'/g,"''").substring(0,35)}%')`;
        if(refVal){
            pagoWhere += ` OR (centro_id = ${centroId} AND referencia = '${refVal}')`;
        } else if(monto > 0){
            const descSub = (descripcion || '').replace(/'/g,"''").substring(0, 30);
            pagoWhere += ` OR (centro_id = ${centroId} AND ABS(monto - ${monto}) < 0.01 AND descripcion ILIKE '%${descSub}%')`;
        }
        await _caSql(`DELETE FROM centro_apoyo_pago WHERE id = (SELECT id FROM centro_apoyo_pago WHERE ${pagoWhere} ORDER BY id DESC LIMIT 1)`);

        // 3. Registrar movimiento de reverso para auditoría
        await _caSql(`INSERT INTO centro_apoyo_movimiento(centro_id,tipo,monto,descripcion,lider_ids,registrado_por) VALUES(${centroId},'reverso',${monto},'${_descEsc}','${(liderIds||'').replace(/'/g,"''")}','${_userEsc}')`);

        // 4. Revertir pago: subir deuda de vuelta
        await _recalcCreditoUsado(centroId, +monto);

        // 5. Eliminar el movimiento original de abono
        await _caSql(`DELETE FROM centro_apoyo_movimiento WHERE id=${movId}`);

        BO_AUTH.showAlert('↩️ Cobro reversado exitosamente · Activos devueltos a líderes · Balance actualizado','success');
        await loadCentrosApoyo();
        loadMovimientosCentro(centroId);
    }catch(e){
        BO_AUTH.showAlert('Error al reversar: '+e.message,'danger');
        if(btn){
            btn.disabled = false;
            btn.textContent = '↩️ Reversar';
        }
    }finally{
        _reversandoCobroId = null;
    }
}

// ── Exportar CSV: Movimientos de Crédito ──────────────────────
async function exportarCsvMovimientosCentro(centroId){
    if(!centroId) centroId = window._caDetalleId;
    if(!centroId) return;
    const c = _caData.find(x=>x.id===centroId);
    const centroNombre = c ? (c.nombre || '').replace(/[/\\?%*:|"<>]/g, '-').trim() : `Centro_${centroId}`;
    const moneda = c?.moneda || 'MXN';

    const mesInputMov = document.getElementById('caMovMesFiltro');
    const verTodoMovEl = document.getElementById('caMovVerTodo');
    const verTodoMov = verTodoMovEl?.checked || false;
    const mesFiltroMov = (!verTodoMov && mesInputMov?.value) ? mesInputMov.value : null;
    const whereMesMov = mesFiltroMov ? ` AND TO_CHAR(creado_en,'YYYY-MM')='${mesFiltroMov}'` : '';

    try {
        const rows = await _caSql(`SELECT * FROM centro_apoyo_movimiento WHERE centro_id=${centroId}${whereMesMov} ORDER BY creado_en DESC`);
        if(!rows || !rows.length){
            BO_AUTH.showAlert('Sin movimientos registrados para exportar en este período', 'warning');
            return;
        }

        const csvHeaders = [
            'ID',
            'Fecha',
            'Tipo',
            'Monto',
            'Moneda',
            'Descripción',
            'Nota / Venta ID',
            'Líderes Relacionados',
            'Registrado Por'
        ];

        const csvEscape = (val) => {
            if (val === null || val === undefined) return '""';
            const str = String(val).replace(/"/g, '""');
            return `"${str}"`;
        };

        const tipoLabels = {
            cargo: 'Cargo',
            abono: 'Abono',
            ajuste: 'Ajuste',
            aumento_limite: 'Aumento de Límite',
            reverso: 'Reverso'
        };

        const csvLines = [csvHeaders.join(',')];

        for (const r of rows) {
            const fecha = r.creado_en ? new Date(r.creado_en).toLocaleString('es-MX') : '';
            const tipo = tipoLabels[r.tipo] || r.tipo || '';
            const monto = Number(r.monto || 0).toFixed(2);

            csvLines.push([
                r.id || '',
                csvEscape(fecha),
                csvEscape(tipo),
                monto,
                csvEscape(r.moneda || moneda),
                csvEscape(r.descripcion || ''),
                csvEscape(r.venta_id || ''),
                csvEscape(r.lider_ids || ''),
                csvEscape(r.registrado_por || '')
            ].join(','));
        }

        const csvContent = '\uFEFF' + csvLines.join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const periodoStr = mesFiltroMov ? mesFiltroMov : 'historico_completo';
        a.download = `movimientos_${centroNombre}_${periodoStr}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);

        BO_AUTH.showAlert(`✓ CSV de Movimientos descargado (${rows.length} registros)`, 'success');
    } catch(e) {
        console.error('Error exportando CSV de movimientos:', e);
        BO_AUTH.showAlert('Error al generar CSV de movimientos: ' + e.message, 'danger');
    }
}
window.exportarCsvMovimientosCentro = exportarCsvMovimientosCentro;

// ── Tab Facturar: Constructor Interactivo de Pre-Factura ─────────────
window._caFactTabData = { notas: [], activos: [], pagos: [], productos: [], componentes: [] };
window._caFactTabCfdi = null;

async function loadFacturarCentro(centroId){
    if(!centroId) centroId = window._caDetalleId;
    if(!centroId) return;
    
    // Initialize month picker to current month if empty
    const mesInput = document.getElementById('caFactMes');
    if(mesInput && !mesInput.value){
        const now = new Date();
        mesInput.value = now.getFullYear() + '-' + String(now.getMonth()+1).padStart(2,'0');
    }
    const mes = mesInput?.value || '';
    
    const notasBody = document.getElementById('caFactTab-notasBody');
    const activosBody = document.getElementById('caFactTab-activosBody');
    const pagosBody = document.getElementById('caFactTab-pagosBody');
    const statusBadge = document.getElementById('caFactTab-statusBadge');
    
    if(statusBadge) statusBadge.innerHTML = '<span style="color:#a5b4fc;">⏳ Cargando datos...</span>';
    if(notasBody) notasBody.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:.75rem;color:var(--text-dim);">Cargando…</td></tr>';
    if(activosBody) activosBody.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:.75rem;color:var(--text-dim);">Cargando…</td></tr>';
    if(pagosBody) pagosBody.innerHTML = '<tr><td colspan="4" style="text-align:center;padding:.75rem;color:var(--text-dim);">Cargando…</td></tr>';

    // Autofill fiscal data from centro
    const c = _caData.find(x => x.id === centroId);
    if(c){
        const setVal = (id, val) => { const el = document.getElementById(id); if(el && !el.value) el.value = val; };
        setVal('caFactTab-rfc', c.rfc || 'XAXX010101000');
        setVal('caFactTab-razon', c.razon_social || 'PUBLICO EN GENERAL');
        setVal('caFactTab-cp', c.codigo_postal || '44100');
        setVal('caFactTab-regimen', c.regimen_fiscal || '616');
        setVal('caFactTab-usoCfdi', c.uso_cfdi || 'S01');
        setVal('caFactTab-formaPago', '99');
    }

    try{
        const [notas, activos, pagos, prevSol, productos, componentes] = await Promise.all([
            // 1. Notas no facturadas del periodo (ESTRICTO: sin emitidas, sin uuid, sin solicitudes activas)
            _caSql(`SELECT v.id, v.fecha_venta, v.total, v.monto_facturable, v.piezas, v.comentario,
                COALESCE(c.nombre || ' ' || COALESCE(c.ape_p,''), 'Público General') AS cliente_nombre
                FROM venta v LEFT JOIN cliente c ON c.id = v.cliente_id
                WHERE v.centro_apoyo_id=${centroId} AND v.approved=1
                AND COALESCE(v.factura_emitida,false)=false
                AND (v.factura_uuid IS NULL OR v.factura_uuid='')
                AND COALESCE(v.requiere_factura,false)=false
                AND COALESCE(v.producto_id,'') != 'calificacion-mes'
                AND COALESCE(v.comentario,'') NOT ILIKE '%calificacion del mes%'
                AND v.id::text NOT IN (SELECT jsonb_array_elements_text(ventas_ids::jsonb) FROM facturas_solicitadas WHERE estado IN ('solicitada','en_proceso','facturada') AND ventas_ids IS NOT NULL AND ventas_ids <> '[]' AND ventas_ids <> 'null')
                AND v.id::text NOT IN (SELECT numero_nota::text FROM facturas_solicitadas WHERE estado IN ('solicitada','en_proceso','facturada') AND numero_nota IS NOT NULL)
                AND TO_CHAR(v.fecha_venta,'YYYY-MM')='${mes}'
                ORDER BY v.fecha_venta DESC`),
            // 2. Activos cobrados del mes
            _caSql(`SELECT id, monto, descripcion, registrado_por, creado_en
                FROM centro_apoyo_pago
                WHERE centro_id=${centroId} AND (tipo_pago='Cobro Activos' OR LOWER(tipo_pago) LIKE '%activo%')
                AND TO_CHAR(creado_en,'YYYY-MM')='${mes}'
                ORDER BY creado_en DESC`),
            // 3. Pagos/depósitos directos (no activos)
            _caSql(`SELECT id, monto, tipo_pago, referencia, descripcion, registrado_por, creado_en
                FROM centro_apoyo_pago
                WHERE centro_id=${centroId} AND COALESCE(tipo_pago,'') != 'Cobro Activos' AND LOWER(COALESCE(tipo_pago,'')) NOT LIKE '%activo%'
                AND TO_CHAR(creado_en,'YYYY-MM')='${mes}'
                ORDER BY creado_en DESC`),
            // 4. Activos ya aplicados en facturas de centros del mismo periodo
            _caSql(`SELECT id, numero_nota, comentario, estado
                FROM facturas_solicitadas
                WHERE (numero_nota LIKE 'CENTRO-${centroId}-%' OR numero_nota LIKE 'GLOBAL-%-CA_${centroId}')
                AND estado IN ('solicitada','en_proceso','facturada')
                AND (numero_nota LIKE '%${mes}%' OR comentario LIKE '%${mes}%')`),
            // 5. Catálogo de productos
            _caSql(`SELECT id, sku, nombre, precio, tasa_iva, categoria FROM productos`),
            // 6. Componentes de paquetes
            _caSql(`SELECT paquete_id, componente_id, cantidad FROM producto_componente`)
        ]);

        let activosYaConsumidos = 0;
        (prevSol || []).forEach(s => {
            const m = String(s.comentario||'').match(/act=([0-9.]+)/);
            if(m) activosYaConsumidos += parseFloat(m[1]) || 0;
        });

        const totalActivosBrutos = (activos || []).reduce((s,a) => s + Number(a.monto||0), 0);
        const totalActivosDisponibles = Math.max(0, totalActivosBrutos - activosYaConsumidos);

        window._caFactTabData = {
            notas,
            activos,
            pagos,
            productos,
            componentes,
            totalActivosBrutos,
            activosYaConsumidos,
            totalActivosDisponibles
        };

        // Render notas
        const nBadge = document.getElementById('caFactTab-notasCountBadge');
        if(nBadge) nBadge.textContent = String(notas.length);
        const nSelectAll = document.getElementById('caFactTab-selectAll');
        if(nSelectAll) nSelectAll.checked = true;

        if(!notas.length){
            if(notasBody) notasBody.innerHTML = '<tr><td colspan="5" style="text-align:center;color:var(--success);padding:.75rem;">✅ Sin notas pendientes no facturadas para este periodo</td></tr>';
        } else {
            if(notasBody) {
                notasBody.innerHTML = notas.map(r => `<tr>
                    <td style="text-align:center;"><input type="checkbox" class="caFactTab-check" value="${r.id}" checked onchange="calcularPreviewCfdiCentroTab()"></td>
                    <td style="font-family:monospace;font-size:.75rem;">${(r.id||'').toString().substring(0,12)}</td>
                    <td style="font-size:.78rem;">${r.fecha_venta ? new Date(r.fecha_venta).toLocaleDateString('es-MX', { timeZone: 'UTC' }) : '—'}</td>
                    <td style="font-size:.78rem;">${r.cliente_nombre || '—'}</td>
                    <td style="text-align:right;font-size:.78rem;font-weight:600;">${_caMoneyFmt(r.total,'MXN')}</td>
                </tr>`).join('');
            }
        }

        // Render activos con checkboxes y monto manual
        const aBadge = document.getElementById('caFactTab-activosCountBadge');
        if(aBadge) aBadge.textContent = String(activos.length);
        const aSelectAll = document.getElementById('caFactTab-selectAllActivos');
        if(aSelectAll) aSelectAll.checked = true;
        const montoInput = document.getElementById('caFactTab-montoActivosInput');
        if(montoInput) montoInput.value = totalActivosDisponibles.toFixed(2);

        if(!activos.length){
            if(activosBody) activosBody.innerHTML = '<tr><td colspan="5" style="text-align:center;color:var(--text-dim);padding:.75rem;">Sin cobro de activos registrado en el periodo</td></tr>';
        } else {
            if(activosBody) {
                activosBody.innerHTML = activos.map(a => `<tr>
                    <td style="text-align:center;"><input type="checkbox" class="caFactTab-activoCheck" value="${a.id}" data-monto="${a.monto||0}" checked onchange="caFactTabOnActivoRowChange()"></td>
                    <td style="font-size:.78rem;">${a.creado_en ? new Date(a.creado_en).toLocaleDateString('es-MX', { timeZone: 'UTC' }) : '—'}</td>
                    <td style="font-size:.78rem;font-weight:700;color:#22c55e;">${_caMoneyFmt(a.monto,'MXN')}</td>
                    <td style="font-size:.75rem;color:var(--text-dim);">${a.descripcion || 'Cobro Activos'}</td>
                    <td style="font-size:.75rem;color:var(--text-dim);">${a.registrado_por || '—'}</td>
                </tr>`).join('');
            }
        }

        // Render pagos con checkboxes
        const pBadge = document.getElementById('caFactTab-pagosCountBadge');
        if(pBadge) pBadge.textContent = String(pagos.length);
        const pSelectAll = document.getElementById('caFactTab-selectAllPagos');
        if(pSelectAll) pSelectAll.checked = true;

        if(!pagos.length){
            if(pagosBody) pagosBody.innerHTML = '<tr><td colspan="5" style="text-align:center;color:var(--text-dim);padding:.75rem;">Sin depósitos directos en el periodo</td></tr>';
        } else {
            if(pagosBody) {
                pagosBody.innerHTML = pagos.map(p => `<tr>
                    <td style="text-align:center;"><input type="checkbox" class="caFactTab-check-pago" value="${p.id}" data-monto="${Number(p.monto||0)}" checked onchange="caFactTabOnPagoRowChange()"></td>
                    <td style="font-size:.78rem;">${p.creado_en ? new Date(p.creado_en).toLocaleDateString('es-MX', { timeZone: 'UTC' }) : '—'}</td>
                    <td style="font-size:.78rem;">${p.tipo_pago || 'Depósito'}</td>
                    <td style="font-size:.78rem;font-weight:700;color:#f59e0b;">${_caMoneyFmt(p.monto,'MXN')}</td>
                    <td style="font-size:.75rem;color:var(--text-dim);">${p.referencia || p.descripcion || '—'}</td>
                </tr>`).join('');
            }
        }

        if(statusBadge) statusBadge.innerHTML = `<span style="color:#22c55e;">✅ ${notas.length} notas cargadas</span>`;

        // Calcular pre-factura CFDI
        calcularPreviewCfdiCentroTab();

    }catch(e){
        console.error('Error loadFacturarCentro: '+e.message);
        if(statusBadge) statusBadge.innerHTML = `<span style="color:#ef4444;">❌ Error: ${e.message}</span>`;
        if(notasBody) notasBody.innerHTML = `<tr><td colspan="5" style="text-align:center;color:#ef4444;padding:1rem;">Error: ${e.message}</td></tr>`;
    }
}

// ── Event Handlers de Selección de Notas, Activos y Pagos en Tab ────
function caFactTabToggleAllNotas(checked){
    document.querySelectorAll('.caFactTab-check').forEach(ch => ch.checked = checked);
    calcularPreviewCfdiCentroTab();
}

function caFactTabToggleAllActivos(checked){
    document.querySelectorAll('.caFactTab-activoCheck').forEach(ch => ch.checked = checked);
    const data = window._caFactTabData;
    const input = document.getElementById('caFactTab-montoActivosInput');
    if(input){
        input.value = checked ? (data?.totalActivosDisponibles || 0).toFixed(2) : '0.00';
    }
    calcularPreviewCfdiCentroTab();
}

function caFactTabToggleAllPagos(checked){
    document.querySelectorAll('.caFactTab-check-pago').forEach(ch => ch.checked = checked);
    calcularPreviewCfdiCentroTab();
}

function caFactTabOnActivoRowChange(){
    const checks = document.querySelectorAll('.caFactTab-activoCheck:checked');
    const allChecks = document.querySelectorAll('.caFactTab-activoCheck');
    let sum = 0;
    checks.forEach(ch => sum += Number(ch.getAttribute('data-monto')||0));
    const input = document.getElementById('caFactTab-montoActivosInput');
    if(input) input.value = sum.toFixed(2);
    const aSelectAll = document.getElementById('caFactTab-selectAllActivos');
    if(aSelectAll) aSelectAll.checked = (checks.length === allChecks.length && allChecks.length > 0);
    calcularPreviewCfdiCentroTab();
}

function caFactTabOnPagoRowChange(){
    const checks = document.querySelectorAll('.caFactTab-check-pago:checked');
    const allChecks = document.querySelectorAll('.caFactTab-check-pago');
    const selectAll = document.getElementById('caFactTab-selectAllPagos');
    if(selectAll) selectAll.checked = (checks.length === allChecks.length && allChecks.length > 0);
    calcularPreviewCfdiCentroTab();
}

function caFactTabOnMontoManualChange(){
    const input = document.getElementById('caFactTab-montoActivosInput');
    let val = parseFloat(input?.value) || 0;
    const data = window._caFactTabData;
    const maxDisp = data?.totalActivosDisponibles || 0;
    // Cap al disponible
    if(val > maxDisp && maxDisp > 0) val = maxDisp;
    // Cap al total de notas seleccionadas (no aplicar más activos que el valor de las notas)
    const checkedNotas = document.querySelectorAll('.caFactTab-check:checked');
    const selectedNotas = (data?.notas || []).filter(n => [...checkedNotas].map(ch => ch.value).includes(String(n.id)));
    const totalNotasSel = selectedNotas.reduce((s,n) => s + Number(n.total||0), 0);
    if(totalNotasSel > 0 && val > totalNotasSel) val = totalNotasSel;
    if(input) input.value = val.toFixed(2);
    calcularPreviewCfdiCentroTab();
}

function caFactTabUsarTodoActivos(){
    const data = window._caFactTabData;
    let maxDisp = data?.totalActivosDisponibles || 0;
    // Cap al total de notas seleccionadas
    const checkedNotas = document.querySelectorAll('.caFactTab-check:checked');
    const selectedNotas = (data?.notas || []).filter(n => [...checkedNotas].map(ch => ch.value).includes(String(n.id)));
    const totalNotasSel = selectedNotas.reduce((s,n) => s + Number(n.total||0), 0);
    if(totalNotasSel > 0 && maxDisp > totalNotasSel) maxDisp = totalNotasSel;
    const input = document.getElementById('caFactTab-montoActivosInput');
    if(input) input.value = maxDisp.toFixed(2);
    const aSelectAll = document.getElementById('caFactTab-selectAllActivos');
    if(aSelectAll) aSelectAll.checked = true;
    document.querySelectorAll('.caFactTab-activoCheck').forEach(ch => ch.checked = true);
    calcularPreviewCfdiCentroTab();
}

function caFactTabUsarCeroActivos(){
    const input = document.getElementById('caFactTab-montoActivosInput');
    if(input) input.value = '0.00';
    const aSelectAll = document.getElementById('caFactTab-selectAllActivos');
    if(aSelectAll) aSelectAll.checked = false;
    document.querySelectorAll('.caFactTab-activoCheck').forEach(ch => ch.checked = false);
    calcularPreviewCfdiCentroTab();
}

function caFactTabTogglePublico(checked){
    const rfc = document.getElementById('caFactTab-rfc');
    const razon = document.getElementById('caFactTab-razon');
    const regimen = document.getElementById('caFactTab-regimen');
    const uso = document.getElementById('caFactTab-usoCfdi');
    const c = _caData.find(x => x.id === window._caDetalleId);
    if(checked){
        if(rfc) rfc.value = 'XAXX010101000';
        if(razon) razon.value = 'PUBLICO EN GENERAL';
        if(regimen) regimen.value = '616';
        if(uso) uso.value = 'S01';
    } else if(c){
        if(rfc) rfc.value = c.rfc || 'XAXX010101000';
        if(razon) razon.value = c.razon_social || 'PUBLICO EN GENERAL';
        if(regimen) regimen.value = c.regimen_fiscal || '616';
        if(uso) uso.value = c.uso_cfdi || 'S01';
    }
}

// ── Motor de Simulación SAT en Vivo para Tab ─────────────────
function calcularPreviewCfdiCentroTab(){
    const data = window._caFactTabData;
    if(!data || !data.notas) return;

    const checkedBoxes = Array.from(document.querySelectorAll('.caFactTab-check:checked'));
    const selectedIds = checkedBoxes.map(ch => ch.value);
    const selectedNotas = data.notas.filter(n => selectedIds.includes(String(n.id)));

    const inputMonto = document.getElementById('caFactTab-montoActivosInput');
    let montoActivosAplicar = parseFloat(inputMonto?.value);
    if(isNaN(montoActivosAplicar) || montoActivosAplicar < 0) montoActivosAplicar = 0;
    if(montoActivosAplicar > data.totalActivosDisponibles) montoActivosAplicar = data.totalActivosDisponibles;
    // Cap al total de notas seleccionadas — no aplicar más activos que el valor de las notas
    const totalNotasMonto_cap = selectedNotas.reduce((s,n) => s + Number(n.total||0), 0);
    if(totalNotasMonto_cap > 0 && montoActivosAplicar > totalNotasMonto_cap) {
        montoActivosAplicar = totalNotasMonto_cap;
        if(inputMonto) inputMonto.value = montoActivosAplicar.toFixed(2);
    }

    const totalNotasMonto = selectedNotas.reduce((s,n) => s + Number(n.total||0), 0);
    
    // Sumar solo los pagos marcados con checkbox
    const pagoChecks = document.querySelectorAll('.caFactTab-check-pago:checked');
    let totalPagos = 0;
    pagoChecks.forEach(ch => { totalPagos += parseFloat(ch.getAttribute('data-monto') || 0) || 0; });

    const saldoPendiente = totalNotasMonto - montoActivosAplicar - totalPagos;

    // Actualizar KPI Cards en el Tab
    const kpiNotas = document.getElementById('caFactTab-kpiNotas');
    const kpiNotasSub = document.getElementById('caFactTab-kpiNotasSub');
    if(kpiNotas) kpiNotas.textContent = _caMoneyFmt(totalNotasMonto, 'MXN');
    if(kpiNotasSub) kpiNotasSub.textContent = `${selectedNotas.length} de ${data.notas.length} notas`;

    const kpiActivos = document.getElementById('caFactTab-kpiActivos');
    const kpiActivosSub = document.getElementById('caFactTab-kpiActivosSub');
    if(kpiActivos) kpiActivos.textContent = _caMoneyFmt(montoActivosAplicar, 'MXN');
    if(kpiActivosSub) kpiActivosSub.textContent = `$${data.totalActivosDisponibles.toLocaleString('es-MX',{minimumFractionDigits:2})} disp.`;

    const kpiPagos = document.getElementById('caFactTab-kpiPagos');
    if(kpiPagos) kpiPagos.textContent = _caMoneyFmt(totalPagos, 'MXN');

    const kpiSaldo = document.getElementById('caFactTab-kpiSaldo');
    const kpiSaldoLabel = document.getElementById('caFactTab-kpiSaldoLabel');
    const kpiSaldoSub = document.getElementById('caFactTab-kpiSaldoSub');
    if(kpiSaldo){
        kpiSaldo.textContent = _caMoneyFmt(Math.abs(saldoPendiente), 'MXN');
        if(saldoPendiente <= 0){
            kpiSaldo.style.color = '#22c55e';
            if(kpiSaldoLabel) { kpiSaldoLabel.textContent = 'Saldo Cubierto'; kpiSaldoLabel.style.color = '#22c55e'; }
            if(kpiSaldoSub) kpiSaldoSub.textContent = saldoPendiente < 0 ? `Superávit: ${_caMoneyFmt(Math.abs(saldoPendiente),'MXN')}` : 'Balance en $0.00';
        } else {
            kpiSaldo.style.color = '#ef4444';
            if(kpiSaldoLabel) { kpiSaldoLabel.textContent = 'Saldo Crédito'; kpiSaldoLabel.style.color = '#ef4444'; }
            if(kpiSaldoSub) kpiSaldoSub.textContent = 'Resta por cubrir';
        }
    }

    // Desglose consolidado de piezas de productos
    const compMap = {};
    (data.componentes || []).forEach(c => {
        if(!compMap[c.paquete_id]) compMap[c.paquete_id] = [];
        compMap[c.paquete_id].push(c);
    });

    const consolidated = {};

    for(const nota of selectedNotas){
        let pzas = nota.piezas;
        if(typeof pzas === 'string'){
            try { pzas = JSON.parse(pzas); } catch(_) { pzas = null; }
        }

        let foundAny = false;

        if(Array.isArray(pzas) && pzas.length > 0){
            for(const pz of pzas){
                const prodId = String(pz.producto_id || pz.sku || pz.id || '');
                if(!prodId) continue;
                const qty = parseInt(pz.cantidad) || 1;
                if(qty <= 0) continue;
                foundAny = true;

                const catProd = (data.productos || []).find(c => String(c.id) === prodId || String(c.sku) === prodId);
                if(catProd && catProd.categoria === 'paquete' && compMap[catProd.id]){
                    compMap[catProd.id].forEach(cmp => {
                        const subId = String(cmp.componente_id);
                        const subProd = (data.productos || []).find(c => String(c.id) === subId) || { id: subId, nombre: cmp.comp_nombre || 'Componente', precio: parseFloat(cmp.comp_precio) || 500, tasa_iva: 0.16 };
                        const subQty = (parseInt(cmp.cantidad) || 1) * qty;
                        if(!consolidated[subId]) consolidated[subId] = { prod: subProd, qty: 0 };
                        consolidated[subId].qty += subQty;
                    });
                } else {
                    const k = catProd ? String(catProd.id) : prodId;
                    const prodObj = catProd || { id: k, nombre: pz.nombre || pz.producto || 'Producto', precio: parseFloat(pz.precio_unitario) || 500, tasa_iva: 0.16 };
                    if(!consolidated[k]) consolidated[k] = { prod: prodObj, qty: 0 };
                    consolidated[k].qty += qty;
                }
            }
        } else if(pzas && typeof pzas === 'object' && Object.keys(pzas).length > 0){
            for(const [skuOrId, cant] of Object.entries(pzas)){
                const qty = Number(cant) || 0;
                if(qty <= 0) continue;
                foundAny = true;
                const catProd = (data.productos || []).find(c => String(c.id) === String(skuOrId) || String(c.sku) === String(skuOrId));
                if(catProd && catProd.categoria === 'paquete' && compMap[catProd.id]){
                    compMap[catProd.id].forEach(cmp => {
                        const subId = String(cmp.componente_id);
                        const subProd = (data.productos || []).find(c => String(c.id) === subId) || { id: subId, nombre: cmp.comp_nombre || 'Componente', precio: parseFloat(cmp.comp_precio) || 500, tasa_iva: 0.16 };
                        const subQty = (parseInt(cmp.cantidad) || 1) * qty;
                        if(!consolidated[subId]) consolidated[subId] = { prod: subProd, qty: 0 };
                        consolidated[subId].qty += subQty;
                    });
                } else {
                    const k = catProd ? String(catProd.id) : String(skuOrId);
                    const prodObj = catProd || { id: k, nombre: String(skuOrId), precio: 500, tasa_iva: 0.16 };
                    if(!consolidated[k]) consolidated[k] = { prod: prodObj, qty: 0 };
                    consolidated[k].qty += qty;
                }
            }
        }

        // Fallback: si la nota no trae desglose de piezas en JSON, inferir del total
        if(!foundAny && Number(nota.total || 0) > 0){
            const defPrice = 500;
            const cantEst = Math.max(1, Math.round(Number(nota.total) / defPrice));
            const defProd = (data.productos || []).find(p => String(p.id) === '1' || p.sku === 'DUL-01') || 
                            { id: '1', nombre: 'Suero de Leche Dulet', precio: defPrice, tasa_iva: 0.00 };
            const k = String(defProd.id);
            if(!consolidated[k]) consolidated[k] = { prod: defProd, qty: 0 };
            consolidated[k].qty += cantEst;
        }
    }

    const items16 = [];
    const items0 = [];
    Object.values(consolidated).forEach(item => {
        const tasa = Number(item.prod.tasa_iva || 0);
        if(tasa > 0) items16.push(item);
        else items0.push(item);
    });

    let saldoActivosRestante = montoActivosAplicar;
    const renglonesCfdi = [];

    function procesarBolsa(items){
        items.forEach(item => {
            const precioConIva = Number(item.prod.precio || 0) || 500;
            const tasaRaw = Number(item.prod.tasa_iva || 0);
            const tasa = tasaRaw > 1 ? tasaRaw / 100 : tasaRaw; // DB almacena 16, no 0.16
            // El precio del catálogo YA incluye IVA → descomponer
            const precioBase = tasa > 0 ? +(precioConIva / (1 + tasa)).toFixed(6) : precioConIva;
            let cantSubsidio = 0;
            let cantRegular = item.qty;

            if(saldoActivosRestante > 0 && precioConIva > 0){
                const maxPiezasSubsidio = Math.floor(saldoActivosRestante / precioConIva);
                cantSubsidio = Math.min(item.qty, maxPiezasSubsidio);
                if(cantSubsidio > 0){
                    saldoActivosRestante -= (cantSubsidio * precioConIva);
                    cantRegular = item.qty - cantSubsidio;
                }
            }

            if(cantSubsidio > 0){
                const pSubsidio = 0.01;
                const impSubsidio = pSubsidio * cantSubsidio;
                renglonesCfdi.push({
                    nombre: item.prod.nombre || item.prod.sku || 'Producto',
                    cantidad: cantSubsidio,
                    precioUnit: pSubsidio,
                    importe: impSubsidio,
                    descuento: 0,
                    iva: 0,
                    tasaIva: 0,
                    esSubsidio: true,
                    totalRenglon: impSubsidio
                });
            }

            if(cantRegular > 0){
                const impRegular = Math.round(precioBase * cantRegular * 100) / 100;
                const ivaRegular = tasa > 0 ? Math.round(impRegular * tasa * 100) / 100 : 0;
                renglonesCfdi.push({
                    nombre: item.prod.nombre || item.prod.sku || 'Producto',
                    cantidad: cantRegular,
                    precioUnit: precioBase,
                    importe: impRegular,
                    descuento: 0,
                    iva: ivaRegular,
                    tasaIva: tasa,
                    esSubsidio: false,
                    totalRenglon: Math.round((impRegular + ivaRegular) * 100) / 100
                });
            }
        });
    }

    procesarBolsa(items16);
    procesarBolsa(items0);

    const subtotal = renglonesCfdi.reduce((s,r) => s + r.importe, 0);
    const totalDescuento = renglonesCfdi.reduce((s,r) => s + r.descuento, 0);
    const totalIva = renglonesCfdi.reduce((s,r) => s + r.iva, 0);
    const totalCfdi = (subtotal - totalDescuento) + totalIva;

    window._caFactTabCfdi = {
        selectedIds,
        montoActivosAplicar,
        totalActivosDisponibles: data.totalActivosDisponibles,
        totalNotasMonto,
        totalPagos,
        saldoPendiente,
        renglonesCfdi,
        subtotal,
        totalDescuento,
        totalIva,
        totalCfdi
    };

    const subsidioBadge = document.getElementById('caFactTab-subsidioBadge');
    if(subsidioBadge){
        subsidioBadge.textContent = `Activos aplicados: $${montoActivosAplicar.toLocaleString('es-MX',{minimumFractionDigits:2})}`;
    }

    const previewEl = document.getElementById('caFactTab-cfdiPreview');
    if(previewEl){
        if(!selectedNotas.length || !renglonesCfdi.length){
            previewEl.innerHTML = '<div style="text-align:center;color:var(--text-dim);padding:.5rem;">Selecciona al menos una nota para generar la simulación CFDI.</div>';
        } else {
            let html = `<table style="width:100%;font-size:.75rem;border-collapse:collapse;margin-bottom:.5rem;">
                <thead>
                    <tr style="border-bottom:1px solid rgba(255,255,255,.1);color:var(--text-dim);text-align:left;">
                        <th style="padding:4px 6px;">Cant</th>
                        <th style="padding:4px 6px;">Concepto</th>
                        <th style="padding:4px 6px;text-align:right;">P. Unit</th>
                        <th style="padding:4px 6px;text-align:right;">Importe</th>
                        <th style="padding:4px 6px;text-align:right;">Desc / IVA</th>
                        <th style="padding:4px 6px;text-align:right;">Total SAT</th>
                    </tr>
                </thead>
                <tbody>`;
            renglonesCfdi.forEach(r => {
                const badge = r.esSubsidio
                    ? `<span style="background:rgba(34,197,94,.15);color:#22c55e;padding:1px 5px;border-radius:4px;font-size:.65rem;margin-left:4px;font-weight:700;">⚡ activos $0.01</span>`
                    : '';
                html += `<tr style="border-bottom:1px solid rgba(255,255,255,.04);${r.esSubsidio?'color:#86efac;':''}">
                    <td style="padding:4px 6px;font-weight:700;">${r.cantidad}</td>
                    <td style="padding:4px 6px;">${r.nombre} ${badge}</td>
                    <td style="padding:4px 6px;text-align:right;">$${r.precioUnit.toFixed(2)}</td>
                    <td style="padding:4px 6px;text-align:right;">$${r.importe.toFixed(2)}</td>
                    <td style="padding:4px 6px;text-align:right;color:var(--text-dim);">${r.esSubsidio ? '$0.00' : (r.tasaIva>0?'+16%':'0%')}</td>
                    <td style="padding:4px 6px;text-align:right;font-weight:700;">$${r.totalRenglon.toFixed(2)}</td>
                </tr>`;
            });
            html += `</tbody></table>
            <div style="display:flex;justify-content:space-between;align-items:center;padding-top:.4rem;border-top:1px dashed rgba(255,255,255,.15);font-size:.8rem;">
                <span style="color:var(--text-dim);">Subtotal: $${subtotal.toLocaleString('es-MX',{minimumFractionDigits:2})} | Desc: -$${totalDescuento.toLocaleString('es-MX',{minimumFractionDigits:2})} | IVA: +$${totalIva.toLocaleString('es-MX',{minimumFractionDigits:2})}</span>
                <span style="font-weight:800;color:var(--accent-gold);font-size:.95rem;">Total CFDI: $${totalCfdi.toLocaleString('es-MX',{minimumFractionDigits:2})} MXN</span>
            </div>`;
            previewEl.innerHTML = html;
        }
    }
}

// ── Acciones: Enviar a Pendientes & Descargar Reporte CSV ────
async function enviarFacturaCentroTabPendiente(){
    const data = window._caFactTabCfdi;
    if(!data || !data.selectedIds.length) return BO_AUTH.showAlert('No hay notas seleccionadas', 'warning');

    const centroId = window._caDetalleId;
    const c = _caData.find(x => x.id === centroId);
    if(!c) return BO_AUTH.showAlert('Centro no encontrado', 'danger');

    const mes = document.getElementById('caFactMes')?.value || '';
    if(!mes) return BO_AUTH.showAlert('Selecciona un periodo', 'warning');

    const rfc = document.getElementById('caFactTab-rfc')?.value?.trim() || 'XAXX010101000';
    const razon = document.getElementById('caFactTab-razon')?.value?.trim() || 'PUBLICO EN GENERAL';
    const cp = document.getElementById('caFactTab-cp')?.value?.trim() || '44100';
    const regimen = document.getElementById('caFactTab-regimen')?.value || '616';
    const usoCfdi = document.getElementById('caFactTab-usoCfdi')?.value || 'S01';
    const formaPago = document.getElementById('caFactTab-formaPago')?.value || '99';
    const esPublicoGeneral = document.getElementById('caFactTab-publicoGral')?.checked || false;
    const cedisId = c.cedis_origen || '0';

    const regimenCode = (regimen||'616').split(' ')[0].trim();
    const usoCfdiCode = (usoCfdi||'S01').split(' ')[0].trim();
    const formaPagoCode = (formaPago||'99').split(' ')[0].trim();
    const globalRef = 'CENTRO-' + centroId + '-' + mes;

    const btn = document.getElementById('caFactTab-btnEnviar');
    if(btn) { btn.disabled = true; btn.textContent = '⏳ Enviando...'; }

    try {
        const esc = v => (v === null || v === undefined) ? 'NULL' : "'" + String(v).replace(/'/g, "''") + "'";
        const userId = (typeof CURRENT_USER !== 'undefined' && CURRENT_USER?.id) || '0';
        const ventasIdsJson = JSON.stringify(data.selectedIds);

        // 1. Crear solicitud en facturas_solicitadas
        const sqlIns = `INSERT INTO facturas_solicitadas 
            (usuario_id, rfc, razon_social, cp, regimen, uso_cfdi, forma_pago, numero_nota, estado, cedis_id, ventas_ids) 
            VALUES (${esc(String(userId))}, ${esc(rfc)}, ${esc(razon)}, ${esc(cp)}, ${esc(regimenCode)}, ${esc(usoCfdiCode)}, ${esc(formaPagoCode)}, ${esc(globalRef)}, 'solicitada', ${esc(String(cedisId))}, ${esc(ventasIdsJson)}) 
            RETURNING id`;
        const insRes = await _caSql(sqlIns);
        let solicitudId = insRes[0]?.id;
        if (!solicitudId) {
            const sel = await _caSql(`SELECT id FROM facturas_solicitadas WHERE numero_nota=${esc(globalRef)} ORDER BY id DESC LIMIT 1`);
            solicitudId = sel[0]?.id;
        }
        if (!solicitudId) throw new Error('No se pudo registrar la solicitud en facturas_solicitadas');

        // 1b. Guardar metadata completa en comentario
        const metaStr = `act=${data.montoActivosAplicar};act_disp=${data.totalActivosDisponibles};pg=${esPublicoGeneral?1:0};mes=${mes};cfdi=${data.totalCfdi};n=${data.selectedIds.length}`;
        await _caSql(`UPDATE facturas_solicitadas SET comentario=${esc(metaStr)} WHERE id=${solicitudId}`).catch(()=>{});

        // 2. Marcar las notas seleccionadas como requiere_factura=true
        const inClause = data.selectedIds.map(id => "'" + id + "'").join(',');
        await _caSql(`UPDATE venta SET requiere_factura=true WHERE id IN (${inClause})`);

        BO_AUTH.showAlert(`✅ Factura de Centro enviada a Facturación Pendiente\nSolicitud #${solicitudId} · ${globalRef}\nNotas: ${data.selectedIds.length} · Activos aplicados: $${data.montoActivosAplicar.toLocaleString('es-MX',{minimumFractionDigits:2})}`, 'success');
        loadFacturarCentro(centroId);
        if(typeof loadHistorialFacturas === 'function') loadHistorialFacturas();
    } catch(e){
        BO_AUTH.showAlert('Error al enviar a facturación: ' + e.message, 'danger');
    } finally {
        if(btn) { btn.disabled = false; btn.textContent = '🧾 Enviar a Facturación Pendiente'; }
    }
}

function descargarReporteFacturaCentroTab(){
    const data = window._caFactTabCfdi;
    if(!data || !data.renglonesCfdi || !data.renglonesCfdi.length) return BO_AUTH.showAlert('No hay datos para exportar', 'warning');
    const centroId = window._caDetalleId;
    const mes = document.getElementById('caFactMes')?.value || 'periodo';

    let csv = '\uFEFF';
    csv += `REPORTE PRE-FACTURA CENTRO DE APOYO #${centroId} - ${mes}\n\n`;
    csv += `Notas seleccionadas,${data.selectedIds.length}\n`;
    csv += `Total Notas,$${data.totalNotasMonto.toFixed(2)}\n`;
    csv += `Activos Aplicados,$${data.montoActivosAplicar.toFixed(2)}\n`;
    csv += `Total CFDI Estimado,$${data.totalCfdi.toFixed(2)}\n\n`;
    csv += 'Cantidad,Concepto,Precio Unitario,Importe,Descuento,IVA,Total Renglon,Tipo\n';
    data.renglonesCfdi.forEach(r => {
        csv += `"${r.cantidad}","${r.nombre.replace(/"/g,'""')}","${r.precioUnit.toFixed(2)}","${r.importe.toFixed(2)}","${r.descuento.toFixed(2)}","${r.iva.toFixed(2)}","${r.totalRenglon.toFixed(2)}","${r.esSubsidio?'Subsidio Activos $0.01':'Regular'}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `PreFactura_Centro_${centroId}_${mes}.csv`;
    link.click();
}

// ── Cobrar Activos (batch con FIFO) ──────────────────────────
let _caCobrarCentroId=null;
let _caCobrarIdx=0;
let _caCobrarSaldos={}; // idx → saldo FIFO disponible
function abrirModalCobrarActivos(centroId){
    _caCobrarCentroId=centroId;
    _caCobrarIdx=0;
    _caCobrarSaldos={};
    document.getElementById('caActivos-lideres-list').innerHTML='';
    document.getElementById('caActivos-desc').value='';
    if(document.getElementById('caActivos-fecha')){
        document.getElementById('caActivos-fecha').value=new Date().toISOString().slice(0,10);
    }
    agregarLiderCobro();
    document.getElementById('modalCobrarActivos').style.display='flex';
}

function agregarLiderCobro(){
    const list=document.getElementById('caActivos-lideres-list');
    const idx=_caCobrarIdx++;
    const row=document.createElement('div');
    row.style.cssText='background:rgba(255,255,255,.03);border:1px solid var(--border);border-radius:10px;padding:.75rem;position:relative;overflow:visible;';
    row.innerHTML=`
        <div style="display:grid;grid-template-columns:1fr 120px;gap:.5rem;align-items:end;">
            <div class="form-group" style="margin:0;position:relative;">
                <label class="form-label" style="font-size:.72rem;">Líder #${idx+1}</label>
                <input type="text" class="form-control ca-cobro-search" data-idx="${idx}" placeholder="Buscar por nombre o registro…" style="font-size:.82rem;" oninput="buscarLiderCobro(this.value,${idx})" autocomplete="off">
                <input type="hidden" class="ca-cobro-registro" data-idx="${idx}">
                <input type="hidden" class="ca-cobro-lider" data-idx="${idx}">
                <div class="ca-cobro-results" data-idx="${idx}" style="position:absolute;left:0;right:0;top:100%;background:#1e293b;border:1px solid var(--border);border-radius:6px;max-height:140px;overflow-y:auto;display:none;z-index:50;box-shadow:0 8px 24px rgba(0,0,0,.5);"></div>
            </div>
            <div class="form-group" style="margin:0;">
                <label class="form-label" style="font-size:.72rem;">Monto</label>
                <input type="number" class="form-control ca-cobro-monto" data-idx="${idx}" placeholder="0.00" min="0" step="100" style="font-size:.82rem;" oninput="actualizarMontoFeedback(${idx})">
            </div>
        </div>
        <div class="ca-cobro-info" data-idx="${idx}" style="margin-top:.5rem;display:none;"></div>
        <div class="ca-cobro-feedback" data-idx="${idx}" style="display:none;margin-top:.35rem;"></div>`;
    list.appendChild(row);
}

let _caCobrarTimer=null;
async function buscarLiderCobro(q,idx){
    clearTimeout(_caCobrarTimer);
    const box=document.querySelector(`.ca-cobro-results[data-idx="${idx}"]`);
    if(q.length<2){box.style.display='none';return;}
    _caCobrarTimer=setTimeout(async()=>{
        try{
            const safe=q.replace(/'/g,"''");
            const rows=await _caSql(`SELECT id, nombre, COALESCE(ape_p,'') AS apellido, registro FROM cliente WHERE (COALESCE(nombre,'')||' '||COALESCE(ape_p,'')) ILIKE '%${safe}%' OR registro::text ILIKE '%${safe}%' LIMIT 8`);
            if(!rows.length){box.style.display='none';return;}
            box.style.display='block';
            box.innerHTML=rows.map(r=>`<div style="padding:.5rem .75rem;cursor:pointer;font-size:.82rem;border-bottom:1px solid rgba(255,255,255,.05);" onmouseover="this.style.background='rgba(99,102,241,.15)'" onmouseout="this.style.background=''" onclick="seleccionarLiderCobro(${idx},'${r.registro||r.id}','${(r.nombre+' '+r.apellido).replace(/'/g,"\\'")}')">
                ${r.nombre} ${r.apellido} <span style="color:var(--text-dim)">#${r.registro||r.id}</span>
            </div>`).join('');
        }catch(e){console.error(e);}
    },300);
}

async function seleccionarLiderCobro(idx,registro,nombre){
    const searchEl=document.querySelector(`.ca-cobro-search[data-idx="${idx}"]`);
    const regEl=document.querySelector(`.ca-cobro-registro[data-idx="${idx}"]`);
    const liderEl=document.querySelector(`.ca-cobro-lider[data-idx="${idx}"]`);
    const box=document.querySelector(`.ca-cobro-results[data-idx="${idx}"]`);
    const infoEl=document.querySelector(`.ca-cobro-info[data-idx="${idx}"]`);
    
    searchEl.value=nombre+' (#'+registro+')';
    regEl.value=registro;
    liderEl.value=nombre;
    box.style.display='none';
    
    infoEl.style.display='block';
    infoEl.innerHTML='<span style="color:var(--text-dim);font-size:.78rem;">⏳ Calculando saldo FIFO (red completa)…</span>';
    
    try{
        const liderReg=registro;
        // 1. Resolver UUID
        let liderUUID='';
        try{
            const rUUID=await _caSql("SELECT id FROM cliente WHERE registro = "+liderReg+" LIMIT 1");
            liderUUID=rUUID[0]?.id||'';
        }catch(e){}
        if(!liderUUID){infoEl.innerHTML='<span style="color:#ef4444;font-size:.78rem;">⚠ No se pudo resolver UUID del líder</span>';return;}

        // 2. Fecha actual y rango (SSOT: serie histórica desde 2026-03 a mes actual)
        const now=new Date();
        const mesActual=now.getFullYear()+'-'+String(now.getMonth()+1).padStart(2,'0');
        const [anioNum,mesNum]=mesActual.split('-').map(Number);
        const _numMesesHist=Math.max(4,(anioNum-2026)*12+(mesNum-3));
        const dLim=new Date(anioNum,mesNum-1,1);
        dLim.setMonth(dLim.getMonth()-_numMesesHist);
        const mesLimStr=dLim.getFullYear()+'-'+String(dLim.getMonth()+1).padStart(2,'0');

        // 3. Ventas de la red recursiva (4 niveles) — con cajas_calc seguro
        const _safeCajasCalc = "COALESCE((SELECT SUM(CASE WHEN COALESCE(pr.categoria,pr2.categoria)='paquete' AND EXISTS(SELECT 1 FROM producto_componente pc2 WHERE pc2.paquete_id=COALESCE(pr.id,pr2.id)) THEN COALESCE((SELECT SUM(CASE WHEN comp_p.comisiona=true THEN COALESCE(comp_p.puntos_equivalencia,1)*pc.cantidad*COALESCE(CASE WHEN (pz.value->>'cantidad') ~ '^[0-9]' THEN (pz.value->>'cantidad')::int ELSE 1 END, 1) ELSE 0 END) FROM producto_componente pc JOIN productos comp_p ON comp_p.id=pc.componente_id WHERE pc.paquete_id=COALESCE(pr.id,pr2.id)),0) WHEN COALESCE(pr.comisiona,pr2.comisiona)=true THEN COALESCE(COALESCE(pr.puntos_equivalencia,pr2.puntos_equivalencia),1)*COALESCE(CASE WHEN (pz.value->>'cantidad') ~ '^[0-9]' THEN (pz.value->>'cantidad')::int ELSE 1 END, 1) ELSE 0 END) FROM jsonb_array_elements(CASE WHEN v.piezas IS NOT NULL AND v.piezas::text LIKE '[%' THEN v.piezas::jsonb ELSE '[]'::jsonb END) pz(value) LEFT JOIN productos pr ON pr.id = CASE WHEN (pz.value->>'producto_id') ~ '^[0-9]' THEN (pz.value->>'producto_id')::int ELSE NULL END LEFT JOIN productos pr2 ON pr.id IS NULL AND pr2.sku=(pz.value->>'sku') AND pr2.activo=true)/24.0, v.cantidad)";

        const sqlVentas=
            "WITH RECURSIVE red AS ("+
            "  SELECT id, 0 AS nivel FROM cliente WHERE id = '"+liderUUID+"'"+
            "  UNION ALL"+
            "  SELECT c.id, r.nivel + 1 FROM cliente c INNER JOIN red r ON c.patrocinador = r.id WHERE r.nivel < 4"+
            ") "+
            "SELECT v.total, "+
            _safeCajasCalc + " AS cajas_calc, "+
            "v.id, v.cliente_id, v.fecha_venta, red.nivel, c.registro AS cliente_registro "+
            "FROM venta v JOIN cliente c ON v.cliente_id = c.id JOIN red ON red.id = v.cliente_id "+
            "WHERE v.approved = 1 "+
            "AND TO_CHAR(v.fecha_venta,'YYYY-MM') >= '"+mesLimStr+"' "+
            "AND TO_CHAR(v.fecha_venta,'YYYY-MM') <= '"+mesActual+"'";
        const rVentas=await _caSql(sqlVentas);
        const comprasAll=(rVentas||[]).map(v=>{
            const fechaD=v.fecha_venta?new Date(String(v.fecha_venta).substring(0,10)+'T12:00:00'):null;
            return {
                id:v.id||'',
                cliente_id:v.cliente_id||'',
                totalNum:parseFloat(v.total||0),
                cajas:parseFloat(v.cajas_calc)||0,
                nivel:parseInt(v.nivel??0),
                clienteReg:v.cliente_registro||'',
                mesStr:fechaD?fechaD.getFullYear()+'-'+String(fechaD.getMonth()+1).padStart(2,'0'):''
            };
        });

        // 4. Datos del líder (galardón, empresario_led, país)
        let galardon=null,esEmpresarioLed=false,paisCodigo='MEX';
        try{
            const rLid=await _caSql("SELECT c.galardon, c.es_empresario_led, COALESCE(p.codigo,'MEX') AS pais_codigo FROM cliente c LEFT JOIN pais p ON c.pais_id = p.id WHERE c.registro = "+liderReg+" LIMIT 1");
            galardon=rLid[0]?.galardon||null;
            esEmpresarioLed=rLid[0]?.es_empresario_led===true;
            paisCodigo=rLid[0]?.pais_codigo||'MEX';
        }catch(e){}
        const monedaCfg=typeof MONEDAS!=='undefined'?(MONEDAS[paisCodigo.toUpperCase()]||MONEDAS.DEFAULT||{codigo:'MXN'}):{codigo:'MXN'};
        const precioCaja=(typeof COMMISSION_CONFIG!=='undefined'&&COMMISSION_CONFIG.precio_caja)?
            (COMMISSION_CONFIG.precio_caja[monedaCfg.codigo]||COMMISSION_CONFIG.precio_caja.MXN||4500):4500;

        // 5. Ajustes maestro (credito_ajuste) por mes
        let ajustesPorMes={};
        try{
            const rAj=await _caSql("SELECT mes, SUM(monto) as total FROM activos_transacciones WHERE user_id = "+liderReg+" AND tipo = 'credito_ajuste' GROUP BY mes");
            (rAj||[]).forEach(r=>{ajustesPorMes[r.mes]=parseFloat(r.total||0);});
        }catch(e){}

        // 6. Historial desde 2026-03 a M actual
        const historialBolsa=[];
        for(let i=_numMesesHist;i>=0;i--){
            const dIt=new Date(anioNum,mesNum-1,1);dIt.setMonth(dIt.getMonth()-i);
            const mIt=dIt.getFullYear()+'-'+String(dIt.getMonth()+1).padStart(2,'0');
            const iterCompras=comprasAll.filter(v=>v.mesStr===mIt);
            // compro = ¿hizo alguna compra personal (N0) en el MISMO mes?
            // Usamos length > 0 (igual que n8n: has_sale > 0) para alinearse con Control,
            // ya que ventas pagadas parcialmente con activos pueden tener total < precioCaja
            const iterComprasN0=comprasAll.filter(v=>v.nivel===0&&v.mesStr===mIt);
            const iterCompro=iterComprasN0.length>0;
            const iterN1Act=(typeof contarFrontalesActivos==='function')
                ? contarFrontalesActivos(iterCompras)
                : (function(){ const c={}; iterCompras.filter(v=>v.nivel===1).forEach(v=>{ const r=v.clienteReg; if(r) c[r]=(c[r]||0)+parseFloat(v.cajas||0); }); return Object.values(c).filter(x=>Math.floor(x)>=1).length; })();
            const iterCajas=(typeof calcularCajasCalificadasPorNivel==='function')
                ? calcularCajasCalificadasPorNivel(iterCompras, mIt)
                : (function(){ const c={n1:0,n2:0,n3:0,n4:0}; iterCompras.forEach(v=>{if(v.nivel>0)c['n'+v.nivel]+=(v.cajas||0);}); return c; })();
            const calcIter=(typeof calcularPlanDulet==='function')?calcularPlanDulet(iterCompro,iterN1Act,galardon,esEmpresarioLed,iterCajas,precioCaja):{final:0};
            const ajusteIter=ajustesPorMes[mIt]||0;
            const genConAjuste=Math.max(0,calcIter.final+ajusteIter);
            historialBolsa.push({mes:mIt,generado:genConAjuste,generadoOriginal:calcIter.final,ajusteMaestro:Math.abs(ajusteIter),compro:iterCompro});
        }

        // 7. Gasto total de débitos
        let montoGastado=0;
        try{
            const rGast=await _caSql("SELECT COALESCE(SUM(CASE WHEN tipo = 'debito' THEN monto ELSE 0 END),0) as gastado FROM activos_transacciones WHERE user_id = "+liderReg);
            montoGastado=parseFloat(rGast[0]?.gastado||0);
        }catch(e){}

        // 8. Débitos por mes
        let debitosPorMes={};
        try{
            const rDeb=await _caSql("SELECT mes, COALESCE(SUM(monto),0) as total FROM activos_transacciones WHERE user_id = "+liderReg+" AND tipo = 'debito' GROUP BY mes");
            (rDeb||[]).forEach(r=>{debitosPorMes[r.mes]=parseFloat(r.total||0);});
        }catch(e){}

        // 9. Calcular Bolsa Global FIFO
        let saldo=0;
        const totalGen=historialBolsa.reduce((s,h)=>s+h.generado,0);
        if(typeof calcularBolsaGlobal==='function'){
            if(!window._cierreActivosConfig?.ultimo_mes_liberado){
                try{
                    const rCierre=await _caSql("SELECT valor FROM sistema_config WHERE clave = 'cierre_activos_config' LIMIT 1");
                    const rowC=rCierre?.[0]?.valor;
                    if(rowC){
                        window._cierreActivosConfig=typeof rowC==='string'?JSON.parse(rowC):rowC;
                    }
                }catch(e){}
            }
            const _uLibBolsa = (typeof window !== 'undefined' && window._cierreActivosConfig?.ultimo_mes_liberado) || null;
            const globalLedger=calcularBolsaGlobal(historialBolsa,montoGastado,mesActual,debitosPorMes,mesActual,_uLibBolsa);
            saldo=globalLedger.saldoDisponible;
        }else{
            saldo=totalGen-montoGastado;
        }
        saldo=Math.max(0,saldo);
        _caCobrarSaldos[idx]=saldo;

        const montoEl=document.querySelector(`.ca-cobro-monto[data-idx="${idx}"]`);
        montoEl.max=saldo;

        if(saldo>0){
            infoEl.innerHTML=`<div style="display:flex;justify-content:space-between;align-items:center;background:rgba(34,197,94,.08);border:1px solid rgba(34,197,94,.2);border-radius:6px;padding:.5rem .75rem;">
                <div>
                    <div style="font-size:.72rem;color:#22c55e;font-weight:700;">✓ SALDO FIFO DISPONIBLE</div>
                    <div style="font-size:1.1rem;font-weight:800;color:#22c55e;">$${saldo.toLocaleString('es-MX',{minimumFractionDigits:2})}</div>
                    <div style="font-size:.68rem;color:var(--text-dim);">Generado: $${totalGen.toLocaleString('es-MX',{minimumFractionDigits:2})} · Usado: $${montoGastado.toLocaleString('es-MX',{minimumFractionDigits:2})}</div>
                </div>
                <button class="btn btn-sm" style="background:#22c55e;font-size:.72rem;padding:.3rem .6rem;" onclick="document.querySelector('.ca-cobro-monto[data-idx=\\'${idx}\\']').value=${saldo.toFixed(2)};actualizarMontoFeedback(${idx})">Usar todo</button>
            </div>`;
        }else{
            infoEl.innerHTML=`<div style="background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.2);border-radius:6px;padding:.5rem .75rem;">
                <div style="font-size:.78rem;color:#ef4444;font-weight:600;">⚠ Sin saldo de activos disponible</div>
                <div style="font-size:.68rem;color:var(--text-dim);">Generado: $${totalGen.toLocaleString('es-MX',{minimumFractionDigits:2})} · Usado: $${montoGastado.toLocaleString('es-MX',{minimumFractionDigits:2})}</div>
            </div>`;
        }
    }catch(e){
        infoEl.innerHTML=`<span style="color:#ef4444;font-size:.78rem;">Error: ${e.message}</span>`;
    }
}

function actualizarMontoFeedback(idx){
    const saldoDisp=_caCobrarSaldos[idx];
    if(saldoDisp===undefined)return;
    const montoEl=document.querySelector(`.ca-cobro-monto[data-idx="${idx}"]`);
    const fbEl=document.querySelector(`.ca-cobro-feedback[data-idx="${idx}"]`);
    if(!montoEl||!fbEl)return;
    const monto=Number(montoEl.value)||0;
    if(!monto){fbEl.style.display='none';return;}
    fbEl.style.display='block';
    const restante=Math.max(0,saldoDisp-monto);
    const pct=Math.min(100,(monto/saldoDisp)*100);
    const excede=monto>saldoDisp;
    const barColor=excede?'#ef4444':pct>80?'#f59e0b':'#22c55e';
    montoEl.style.borderColor=excede?'#ef4444':'var(--border)';
    montoEl.style.color=excede?'#ef4444':'var(--text-primary)';
    fbEl.innerHTML=`
        <div style="height:4px;background:rgba(255,255,255,.08);border-radius:2px;overflow:hidden;margin-bottom:.3rem;">
            <div style="height:100%;width:${Math.min(pct,100)}%;background:${barColor};border-radius:2px;transition:width .15s;"></div>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:.7rem;">
            <span style="color:${barColor};font-weight:600;">Usando: $${monto.toLocaleString('es-MX',{minimumFractionDigits:2})} de $${saldoDisp.toLocaleString('es-MX',{minimumFractionDigits:2})}</span>
            <span style="color:${excede?'#ef4444':'var(--text-dim)'}">${excede?'⚠ EXCEDE $'+(monto-saldoDisp).toLocaleString('es-MX',{minimumFractionDigits:2}):'Restante: $'+restante.toLocaleString('es-MX',{minimumFractionDigits:2})}</span>
        </div>`;
}

async function confirmarCobrarActivos(){
    const btnConfirmar = document.querySelector('#modalCobrarActivos .btn-primary') || event?.target;
    const registros=document.querySelectorAll('.ca-cobro-registro');
    const nombres=document.querySelectorAll('.ca-cobro-lider');
    const montos=document.querySelectorAll('.ca-cobro-monto');
    let totalAbono=0,liderIds=[],detalles=[];
    for(let i=0;i<registros.length;i++){
        const reg=registros[i].value.trim(),nom=nombres[i].value.trim(),m=Number(montos[i].value)||0;
        if(!reg||!m)continue;
        const idx=registros[i].dataset.idx;
        const saldoDisp=_caCobrarSaldos[idx];
        if(saldoDisp!==undefined && m>saldoDisp){
            return BO_AUTH.showAlert(`⚠ ${nom} (#${reg}): el monto $${m.toLocaleString()} excede su saldo FIFO de $${saldoDisp.toLocaleString('es-MX',{minimumFractionDigits:2})}`,'danger');
        }
        totalAbono+=m;liderIds.push(reg);detalles.push(nom+' (#'+reg+'): $'+m.toLocaleString());
    }
    if(!totalAbono)return BO_AUTH.showAlert('Agrega al menos un líder con monto','warning');

    // Bloqueo inmediato anti doble-clic
    if(btnConfirmar){
        btnConfirmar.disabled = true;
        btnConfirmar.dataset.origText = btnConfirmar.innerHTML;
        btnConfirmar.innerHTML = '⏳ Procesando cobro...';
    }

    const desc=document.getElementById('caActivos-desc').value.trim()||'Cobro activos';
    try{
        const user=CURRENT_USER?CURRENT_USER.nombre+' '+(CURRENT_USER.apellido||CURRENT_USER.ape_p||''):'admin';
        const _descEsc=(desc+' — '+detalles.join(', ')).replace(/'/g,"''");
        const _userEsc=user.replace(/'/g,"''");

        // 1. Registrar movimiento en centro PRIMERO para obtener su ID único (RETURNING id)
        const movRes = await _caSql(`INSERT INTO centro_apoyo_movimiento(centro_id,tipo,monto,descripcion,lider_ids,registrado_por) VALUES(${_caCobrarCentroId},'abono',${totalAbono},'${_descEsc}','${liderIds.join(',')}','${_userEsc}') RETURNING id`);
        const newMovId = movRes?.[0]?.id || '';
        const caRefTag = newMovId ? ` [CAMOV:${newMovId}]` : '';
        const vIdCol = newMovId ? `'CAMOV-${newMovId}'` : 'NULL';

        // 2. Registrar débito en activos_transacciones para cada líder con enlace único
        const fechaCobro=document.getElementById('caActivos-fecha')?.value;
        const mesActual=(fechaCobro || new Date().toISOString()).slice(0,7);
        for(let i=0;i<registros.length;i++){
            const reg=registros[i].value.trim(),m=Number(montos[i].value)||0;
            if(!reg||!m)continue;
            const descConTag = (_descEsc + caRefTag).replace(/'/g,"''");
            await _caSql(`INSERT INTO activos_transacciones(user_id,tipo,monto,mes,descripcion,registrado_por,venta_id) VALUES(${reg},'debito',${m},'${mesActual}','${descConTag}','${_userEsc}',${vIdCol})`);
        }
        
        // 3. Actualizar balance de crédito del centro
        await _caSql(`UPDATE centro_apoyo SET credito_usado=credito_usado-${totalAbono},actualizado_en=NOW() WHERE id=${_caCobrarCentroId}`);

        // 4. Registrar como pago para que cuente en activaciones con referencia vinculada
        const pagoRef = newMovId ? `'CAMOV-${newMovId}'` : 'NULL';
        await _caSql(`INSERT INTO centro_apoyo_pago(centro_id,monto,tipo_pago,referencia,descripcion,registrado_por) VALUES(${_caCobrarCentroId},${totalAbono},'Cobro Activos',${pagoRef},'${_descEsc}','${_userEsc}')`);

        BO_AUTH.showAlert('✅ Cobro registrado · Crédito -'+_caMoneyFmt(totalAbono)+'\\nActivos debitados correctamente','success');
        document.getElementById('modalCobrarActivos').style.display='none';
        await loadCentrosApoyo();
        verDetalleCentro(_caCobrarCentroId);
    }catch(e){
        BO_AUTH.showAlert('Error: '+e.message,'danger');
    }finally{
        if(btnConfirmar){
            btnConfirmar.disabled = false;
            btnConfirmar.innerHTML = btnConfirmar.dataset.origText || '✅ Confirmar Cobro';
        }
    }
}


// ── Aumentar Límite ───────────────────────────────────────────
function abrirModalAumentarLimite(centroId){
    const c=_caData.find(x=>x.id===centroId);if(!c)return;
    document.getElementById('caAumento-centroId').value=centroId;
    document.getElementById('caAumento-limite').value='';
    document.getElementById('caAumento-actual').textContent='Límite actual: '+_caMoneyFmt(c.limite_credito,c.moneda);
    document.getElementById('caAumento-motivo').value='';
    document.getElementById('modalAumentarLimite').style.display='flex';
}
async function confirmarAumentarLimite(){
    const centroId=document.getElementById('caAumento-centroId').value;
    const nuevoLimite=Number(document.getElementById('caAumento-limite').value);
    if(!nuevoLimite)return BO_AUTH.showAlert('Ingresa el nuevo límite','warning');
    const motivo=document.getElementById('caAumento-motivo').value.trim()||'Aumento de crédito';
    try{
        const c=_caData.find(x=>x.id==centroId);
        const limAnterior=c?Number(c.limite_credito||0):0;
        const user=CURRENT_USER?CURRENT_USER.nombre+' '+CURRENT_USER.apellido:'admin';
        const _motivoEsc=(motivo+' ('+_caMoneyFmt(limAnterior)+' → '+_caMoneyFmt(nuevoLimite)+')').replace(/'/g,"''");
        const _uEsc=user.replace(/'/g,"''");
        await _caSql(`UPDATE centro_apoyo SET limite_credito=${nuevoLimite},actualizado_en=NOW() WHERE id=${centroId}`);
        await _caSql(`INSERT INTO centro_apoyo_movimiento(centro_id,tipo,monto,descripcion,registrado_por) VALUES(${centroId},'aumento_limite',${nuevoLimite-limAnterior},'${_motivoEsc}','${_uEsc}')`);
        BO_AUTH.showAlert('Límite actualizado ✓','success');
        document.getElementById('modalAumentarLimite').style.display='none';
        await loadCentrosApoyo();
        verDetalleCentro(Number(centroId));
    }catch(e){BO_AUTH.showAlert('Error: '+e.message,'danger');}
}

// ── Depósitos / Pagos ────────────────────────────────────────
async function loadDepositosCentro(centroId){
    const container=document.getElementById('caTabContent-depositos');
    if(!container)return;
    const c=_caData.find(x=>x.id===centroId);if(!c)return;
    
    try{
        // ── Cajas reales usando CAJAS_CALC (productos comisionables, igual que bo-activos.js) ──
        const CAJAS_CALC_EXPR =
            "COALESCE(" +
            "  (SELECT SUM(" +
            "    CASE" +
            "      WHEN COALESCE(pr.categoria, pr2.categoria) = 'paquete' AND EXISTS (SELECT 1 FROM producto_componente pc2 WHERE pc2.paquete_id = COALESCE(pr.id, pr2.id)) THEN" +
            "        COALESCE((SELECT SUM(CASE WHEN comp_p.comisiona = true THEN COALESCE(comp_p.puntos_equivalencia, 1) * pc.cantidad * (pz.value->>'cantidad')::int ELSE 0 END)" +
            "          FROM producto_componente pc JOIN productos comp_p ON comp_p.id = pc.componente_id WHERE pc.paquete_id = COALESCE(pr.id, pr2.id)), 0)" +
            "      WHEN COALESCE(pr.comisiona, pr2.comisiona) = true THEN" +
            "        COALESCE(COALESCE(pr.puntos_equivalencia, pr2.puntos_equivalencia), 1) * (pz.value->>'cantidad')::int" +
            "      ELSE 0" +
            "    END" +
            "  ) FROM jsonb_array_elements(v.piezas::jsonb) pz(value)" +
            "  LEFT JOIN productos pr ON pr.id = (pz.value->>'producto_id')::int" +
            "  LEFT JOIN productos pr2 ON pr.id IS NULL AND pr2.sku = (pz.value->>'sku') AND pr2.activo = true" +
            "  ) / 24.0, v.cantidad) AS cajas_calc";

        // Get linked ventas with payment status + cajas reales
        const ventas=await _caSql(`SELECT v.id, v.total, v.fecha_venta, COALESCE(c.nombre||' '||COALESCE(c.ape_p,''),'Público General') AS cliente_nombre, COALESCE((SELECT SUM(pv.monto_aplicado) FROM centro_apoyo_pago_venta pv WHERE pv.venta_id::text=v.id::text),0) AS pagado, ${CAJAS_CALC_EXPR} FROM venta v LEFT JOIN cliente c ON c.id=v.cliente_id WHERE v.centro_apoyo_id=${centroId} AND v.approved=1 ORDER BY v.fecha_venta DESC`);
        const pagos=await _caSql(`SELECT p.*, (SELECT string_agg(pv.venta_id::text,',') FROM centro_apoyo_pago_venta pv WHERE pv.pago_id=p.id) AS notas_aplicadas FROM centro_apoyo_pago p WHERE p.centro_id=${centroId} ORDER BY p.creado_en DESC`);
        
        const totalCredito=ventas.reduce((s,v)=>s+Number(v.total||0),0);
        const totalPagado=ventas.reduce((s,v)=>s+Number(v.pagado||0),0);
        const saldoPendiente=totalCredito-totalPagado;
        // ── Cajas basadas en cajas_calc reales (no en total monetario) ──
        const _precioCajaCA = (typeof COMMISSION_CONFIG!=='undefined' && COMMISSION_CONFIG.precio_caja)
            ? (COMMISSION_CONFIG.precio_caja[c.moneda||'MXN'] || COMMISSION_CONFIG.precio_caja.MXN || 4500)
            : 4500;
        // Total de cajas comisionables de todas las notas del centro
        const totalCajasReales = ventas.reduce((s,v)=>s+Math.floor(parseFloat(v.cajas_calc)||0),0);
        // Cajas YA cobradas (pagos tipo 'Cobro Activos')
        const pagosActivos = pagos.filter(p=>(p.tipo_pago||'').toLowerCase().includes('activos'));
        const montoActivosCobrado = pagosActivos.reduce((s,p)=>s+Number(p.monto||0),0);
        const cajasCobradas = Math.floor(montoActivosCobrado / _precioCajaCA);
        const cajasDisponibles = Math.max(0, totalCajasReales - cajasCobradas);
        // ── Saldo a favor: cuando pagaron de más contra sus notas ──
        const saldoAFavor = Math.max(0, -saldoPendiente);
        const haySaldoFavor = saldoAFavor > 0.01;
        
        let html=`
        <div style="display:grid;grid-template-columns:repeat(${haySaldoFavor?5:4},1fr);gap:.75rem;margin-bottom:1.25rem;">
            <div style="background:rgba(239,68,68,.06);border:1px solid rgba(239,68,68,.2);border-radius:10px;padding:.75rem;text-align:center;">
                <div style="font-size:.68rem;color:#ef4444;text-transform:uppercase;letter-spacing:.04em;">Crédito Total</div>
                <div style="font-size:1.2rem;font-weight:800;color:#ef4444;">${_caMoneyFmt(totalCredito,c.moneda)}</div>
            </div>
            <div style="background:rgba(34,197,94,.06);border:1px solid rgba(34,197,94,.2);border-radius:10px;padding:.75rem;text-align:center;">
                <div style="font-size:.68rem;color:#22c55e;text-transform:uppercase;letter-spacing:.04em;">Total Pagado</div>
                <div style="font-size:1.2rem;font-weight:800;color:#22c55e;">${_caMoneyFmt(totalPagado,c.moneda)}</div>
            </div>
            ${haySaldoFavor
                ? `<div style="background:rgba(34,197,94,.12);border:2px solid rgba(34,197,94,.5);border-radius:10px;padding:.75rem;text-align:center;">
                    <div style="font-size:.68rem;color:#4ade80;text-transform:uppercase;letter-spacing:.04em;">✅ Saldo a Favor</div>
                    <div style="font-size:1.2rem;font-weight:800;color:#4ade80;">${_caMoneyFmt(saldoAFavor,c.moneda)}</div>
                </div>`
                : `<div style="background:rgba(245,158,11,.06);border:1px solid rgba(245,158,11,.2);border-radius:10px;padding:.75rem;text-align:center;">
                    <div style="font-size:.68rem;color:#f59e0b;text-transform:uppercase;letter-spacing:.04em;">Saldo Pendiente</div>
                    <div style="font-size:1.2rem;font-weight:800;color:#f59e0b;">${_caMoneyFmt(Math.max(0,saldoPendiente),c.moneda)}</div>
                </div>`
            }
            <div style="background:rgba(99,102,241,.06);border:1px solid rgba(99,102,241,.2);border-radius:10px;padding:.75rem;text-align:center;">
                <div style="font-size:.68rem;color:#a5b4fc;text-transform:uppercase;letter-spacing:.04em;">Cajas Comisionables</div>
                <div style="font-size:1.2rem;font-weight:800;color:#a5b4fc;">${totalCajasReales}</div>
            </div>
            <div style="background:rgba(99,102,241,.06);border:1px solid rgba(99,102,241,.2);border-radius:10px;padding:.75rem;text-align:center;">
                <div style="font-size:.68rem;color:#a5b4fc;text-transform:uppercase;letter-spacing:.04em;">Cajas p/ Activar</div>
                <div style="font-size:1.2rem;font-weight:800;color:${cajasDisponibles>0?'#a5b4fc':'#6b7280'};">${cajasDisponibles}</div>
            </div>
        </div>`;
        
        // Notas con estado de pago
        html+=`<div style="font-size:.82rem;font-weight:700;margin-bottom:.5rem;">📋 Estado de Notas</div>
        <div style="overflow-x:auto;margin-bottom:1.25rem;">
        <table class="bo-table"><thead><tr>
            <th>Nota</th><th>Cliente</th><th>Fecha</th><th style="text-align:right;">Total</th><th style="text-align:right;">Pagado</th><th style="text-align:right;">Saldo</th><th>Estado</th>
        </tr></thead><tbody>`;
        for(const v of ventas){
            const total=Number(v.total||0),pagado=Number(v.pagado||0),saldo=total-pagado;
            const estado=saldo<=0?'<span style="color:#22c55e;">✅ Liquidada</span>':pagado>0?'<span style="color:#f59e0b;">🟡 Parcial</span>':'<span style="color:#ef4444;">🔴 Pendiente</span>';
            html+=`<tr>
                <td><span style="font-family:monospace;font-size:.78rem;">${(v.id||'').toString().substring(0,12)}</span></td>
                <td>${v.cliente_nombre||'—'}</td>
                <td>${v.fecha_venta?new Date(v.fecha_venta).toLocaleDateString('es-MX'):'—'}</td>
                <td style="text-align:right;">${_caMoneyFmt(total,c.moneda)}</td>
                <td style="text-align:right;color:#22c55e;">${_caMoneyFmt(pagado,c.moneda)}</td>
                <td style="text-align:right;font-weight:600;">${_caMoneyFmt(saldo,c.moneda)}</td>
                <td>${estado}</td>
            </tr>`;
        }
        html+='</tbody></table></div>';
        
        // Historial de pagos — unificar banco_plataforma Y tipo_pago para mostrar ambos schemas
        html+=`<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:.5rem;">
            <div style="font-size:.82rem;font-weight:700;">💳 Historial de Depósitos</div>
            <button class="btn btn-primary btn-sm" style="font-size:.78rem;" onclick="abrirModalDeposito()">➕ Registrar Depósito</button>
        </div>`;
        if(!pagos.length){
            html+='<div style="text-align:center;color:var(--text-dim);padding:1rem;font-size:.85rem;">Sin depósitos registrados</div>';
        }else{
            html+='<div style="overflow-x:auto;"><table class="bo-table"><thead><tr><th>Fecha</th><th>Tipo / Banco</th><th style="text-align:right;">Monto</th><th>Referencia</th><th>Descripción</th><th>Por</th></tr></thead><tbody>';
            for(const p of pagos){
                // Unificar banco_plataforma (nuevo schema) con tipo_pago (schema viejo)
                const bancoLabel = p.banco_plataforma || p.tipo_pago || '—';
                const tipoBadge={'transferencia':'🏦 Transferencia','deposito':'💵 Depósito','efectivo':'💰 Efectivo','tarjeta':'💳 Tarjeta','BBVA':'🏦 BBVA','SANTANDER':'🏦 Santander','MERCADOPAGO':'💳 MercadoPago','PAYPAL':'💳 PayPal'}[bancoLabel]||(bancoLabel||'—');
                const montoP = Number(p.monto||0);
                const monedaP = p.moneda || c.moneda || 'MXN';
                html+=`<tr>
                    <td>${(p.fecha_deposito||p.creado_en)?new Date(p.fecha_deposito||p.creado_en).toLocaleDateString('es-MX'):'—'}</td>
                    <td>${tipoBadge}</td>
                    <td style="text-align:right;font-weight:600;color:#22c55e;">${_caMoneyFmt(montoP,monedaP)}</td>
                    <td style="font-size:.78rem;">${p.referencia||'—'}</td>
                    <td style="font-size:.78rem;">${p.descripcion||p.notas||'—'}</td>
                    <td style="font-size:.78rem;">${p.registrado_por||'—'}</td>
                </tr>`;
            }
            html+='</tbody></table></div>';
        }
        
        container.innerHTML=html;
    }catch(e){container.innerHTML='<div style="color:#ef4444;">Error: '+e.message+'</div>';}
}

function abrirModalDeposito(){
    const centroId=window._caDetalleId;
    document.getElementById('caDepo-monto').value='';
    document.getElementById('caDepo-tipo').value='transferencia';
    document.getElementById('caDepo-ref').value='';
    document.getElementById('caDepo-desc').value='';
    // Load pending ventas for selection
    _loadVentasParaDeposito(centroId);
    document.getElementById('modalRegistrarDeposito').style.display='flex';
}

async function _loadVentasParaDeposito(centroId){
    const tb=document.getElementById('caDepo-ventasBody');
    try{
        const rows=await _caSql(`SELECT v.id, v.total, COALESCE(c.nombre||' '||COALESCE(c.ape_p,''),'Público General') AS cliente_nombre, COALESCE((SELECT SUM(pv.monto_aplicado) FROM centro_apoyo_pago_venta pv WHERE pv.venta_id::text=v.id::text),0) AS pagado FROM venta v LEFT JOIN cliente c ON c.id=v.cliente_id WHERE v.centro_apoyo_id=${centroId} AND v.total > COALESCE((SELECT SUM(pv.monto_aplicado) FROM centro_apoyo_pago_venta pv WHERE pv.venta_id::text=v.id::text),0) ORDER BY v.fecha_venta`);
        if(!rows.length){tb.innerHTML='<tr><td colspan="4" style="text-align:center;color:var(--text-dim);">Todas las notas están liquidadas</td></tr>';return;}
        tb.innerHTML=rows.map(r=>{
            const saldo=Number(r.total||0)-Number(r.pagado||0);
            return `<tr>
                <td><input type="checkbox" class="ca-depo-check" value="${r.id}" data-saldo="${saldo}" checked></td>
                <td><span style="font-family:monospace;font-size:.78rem;">${(r.id||'').toString().substring(0,12)}</span></td>
                <td>${r.cliente_nombre||'—'}</td>
                <td style="text-align:right;color:#f59e0b;font-weight:600;">${_caMoneyFmt(saldo,'MXN')}</td>
            </tr>`;
        }).join('');
    }catch(e){tb.innerHTML='<tr><td colspan="4" style="color:#ef4444;">Error: '+e.message+'</td></tr>';}
}

async function confirmarDeposito(){
    const centroId=window._caDetalleId;
    const monto=Number(document.getElementById('caDepo-monto').value)||0;
    if(!monto)return BO_AUTH.showAlert('Ingresa el monto del depósito','warning');
    const tipo=document.getElementById('caDepo-tipo').value;
    const ref=document.getElementById('caDepo-ref').value.trim();
    const desc=document.getElementById('caDepo-desc').value.trim()||'Depósito';
    const user=CURRENT_USER?(CURRENT_USER.nombre+' '+(CURRENT_USER.apellido||CURRENT_USER.ape_p||'')):'admin';
    
    // Get selected ventas
    const checks=[...document.querySelectorAll('.ca-depo-check:checked')];
    
    try{
        const esc=v=>(v===null||v===undefined)?'NULL':"'"+String(v).replace(/'/g,"''")+"'";
        // 1. Insert pago
        const res=await _caSql(`INSERT INTO centro_apoyo_pago(centro_id,monto,tipo_pago,referencia,descripcion,registrado_por) VALUES(${centroId},${monto},${esc(tipo)},${esc(ref)},${esc(desc)},${esc(user)}) RETURNING id`);
        const pagoId=res[0]?.id;
        if(!pagoId)throw new Error('No se pudo registrar el pago');
        
        // 2. Apply to selected ventas (FIFO)
        let restante=monto;
        for(const cb of checks){
            if(restante<=0)break;
            const ventaId=cb.value;
            const saldo=Number(cb.dataset.saldo||0);
            const aplicar=Math.min(restante,saldo);
            if(aplicar>0){
                await _caSql(`INSERT INTO centro_apoyo_pago_venta(pago_id,venta_id,monto_aplicado) VALUES(${pagoId},'${ventaId}',${aplicar})`);
                restante-=aplicar;
            }
        }
        
        // 3. Decrementar crédito (sin GREATEST, permite a favor)
        await _recalcCreditoUsado(centroId, -monto);
        
        // 4. Register movimiento
        await _caSql(`INSERT INTO centro_apoyo_movimiento(centro_id,tipo,monto,descripcion,registrado_por) VALUES(${centroId},'abono',${monto},${esc('Depósito: '+desc+(ref?' (Ref: '+ref+')':''))},${esc(user)})`);
        
        BO_AUTH.showAlert('✅ Depósito de '+_caMoneyFmt(monto)+' registrado correctamente','success');
        document.getElementById('modalRegistrarDeposito').style.display='none';
        await loadCentrosApoyo();
        loadDepositosCentro(centroId);
    }catch(e){BO_AUTH.showAlert('Error: '+e.message,'danger');}
}

// ── Exportar CSV ──────────────────────────────────────────────
function exportarCentrosCSV(){
    if(!_caData.length)return BO_AUTH.showAlert('Sin datos para exportar','warning');
    let csv='Centro,Líder,Admin,Crédito Usado,Límite,Moneda,Estado,Notas Vinculadas\n';
    _caData.forEach(c=>{csv+=`"${c.nombre}","${c.lider_id}","${c.admin_id||''}",${c.credito_usado||0},${c.limite_credito||0},${c.moneda||'MXN'},${c.estado},${c._notas||0}\n`;});
    const blob=new Blob([csv],{type:'text/csv'});
    const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download='centros_apoyo_'+new Date().toISOString().slice(0,10)+'.csv';a.click();
    BO_AUTH.showAlert('CSV exportado ✓','success');
}

// ── Rendimiento / Analytics ───────────────────────────────────
let _caChartVentas=null, _caChartCredito=null, _caChartProductos=null;

async function loadRendimientoCentro(centroId){
    if(!centroId)return;
    const c=_caData.find(x=>x.id===centroId);if(!c)return;

    const periodoVal=document.getElementById('caRendPeriodo')?.value||'6';
    let dateFilter='';
    if(periodoVal!=='all'){
        dateFilter=` AND v.created_at >= NOW() - INTERVAL '${parseInt(periodoVal)} months'`;
    }

    try{
        // ── 1. Ventas agrupadas por mes ──
        const ventasMes=await _caSql(`
            SELECT TO_CHAR(v.created_at,'YYYY-MM') as mes,
                   COUNT(*)::int as notas,
                   COALESCE(SUM(v.total),0) as total
            FROM venta v
            WHERE v.centro_apoyo_id=${centroId} ${dateFilter}
            GROUP BY mes ORDER BY mes`);

        // ── 2. Movimientos de crédito agrupados por mes ──
        const creditoMes=await _caSql(`
            SELECT TO_CHAR(creado_en,'YYYY-MM') as mes,
                   SUM(CASE WHEN tipo='cargo' THEN monto ELSE 0 END) as cargos,
                   SUM(CASE WHEN tipo='abono' THEN monto ELSE 0 END) as abonos,
                   SUM(CASE WHEN tipo='aumento_limite' THEN monto ELSE 0 END) as aumentos
            FROM centro_apoyo_movimiento
            WHERE centro_id=${centroId} ${dateFilter.replace('v.created_at','creado_en')}
            GROUP BY mes ORDER BY mes`);

        // ── 3. Distribución de productos ──
        const productos=await _caSql(`
            SELECT COALESCE(p.nombre, 'Otro') as producto,
                   SUM((pi->>'cantidad')::int) as total
            FROM venta v, jsonb_array_elements(v.piezas::jsonb) pi
            LEFT JOIN productos p ON p.id=(pi->>'producto_id')::int
            WHERE v.centro_apoyo_id=${centroId} ${dateFilter}
            GROUP BY p.nombre ORDER BY total DESC LIMIT 8`);

        // ── KPIs ──
        const totalVentas=ventasMes.reduce((s,r)=>s+Number(r.total||0),0);
        const totalNotas=ventasMes.reduce((s,r)=>s+Number(r.notas||0),0);
        const mesesConData=ventasMes.length||1;
        const promedioMes=totalVentas/mesesConData;

        document.getElementById('caRendTotalVentas').textContent=_caMoneyFmt(totalVentas,c.moneda);
        document.getElementById('caRendTotalNotas').textContent=totalNotas;
        document.getElementById('caRendPromedio').textContent=_caMoneyFmt(promedioMes,c.moneda);

        // Tendencia: comparar últimos 2 meses
        let tendencia='—',tendColor='#71717a';
        if(ventasMes.length>=2){
            const ult=Number(ventasMes[ventasMes.length-1].total||0);
            const prev=Number(ventasMes[ventasMes.length-2].total||0);
            if(prev>0){
                const pctCambio=Math.round(((ult-prev)/prev)*100);
                if(pctCambio>0){tendencia='↑ +'+pctCambio+'%';tendColor='#22c55e';}
                else if(pctCambio<0){tendencia='↓ '+pctCambio+'%';tendColor='#ef4444';}
                else{tendencia='→ 0%';tendColor='#f59e0b';}
            }else{
                tendencia=ult>0?'↑ Nuevo':'→ Sin datos';
                tendColor=ult>0?'#22c55e':'#71717a';
            }
        }
        const tendEl=document.getElementById('caRendTendencia');
        tendEl.textContent=tendencia;
        tendEl.style.color=tendColor;

        document.getElementById('caRendResumen').textContent=
            `${mesesConData} mes(es) con actividad · Creado: ${c.creado_en?new Date(c.creado_en).toLocaleDateString('es-MX'):'—'}`;

        // ── Gráfica 1: Ventas por Mes (Bar + Line) ──
        const meses=ventasMes.map(r=>{
            const [y,m]=r.mes.split('-');
            return ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][parseInt(m)-1]+' '+y.slice(2);
        });
        const totales=ventasMes.map(r=>Number(r.total||0));
        const notasCnt=ventasMes.map(r=>Number(r.notas||0));

        if(_caChartVentas){_caChartVentas.destroy();_caChartVentas=null;}
        const ctx1=document.getElementById('caChartVentasMes')?.getContext('2d');
        if(ctx1){
            ctx1.canvas.style.maxHeight='220px';
            _caChartVentas=new Chart(ctx1,{
                type:'bar',
                data:{
                    labels:meses,
                    datasets:[
                        {label:'Ventas ($)',data:totales,backgroundColor:'rgba(99,102,241,.35)',borderColor:'#6366f1',borderWidth:1,borderRadius:6,order:2},
                        {label:'# Notas',data:notasCnt,type:'line',borderColor:'#22c55e',backgroundColor:'rgba(34,197,94,.1)',pointBackgroundColor:'#22c55e',pointRadius:4,tension:.3,yAxisID:'y1',order:1}
                    ]
                },
                options:{
                    responsive:true,maintainAspectRatio:false,
                    plugins:{legend:{labels:{color:'#a1a1aa',font:{size:11}}}},
                    scales:{
                        x:{ticks:{color:'#71717a',font:{size:10}},grid:{color:'rgba(255,255,255,.04)'}},
                        y:{position:'left',ticks:{color:'#a5b4fc',font:{size:10},callback:v=>'$'+v.toLocaleString()},grid:{color:'rgba(255,255,255,.06)'}},
                        y1:{position:'right',ticks:{color:'#22c55e',font:{size:10}},grid:{display:false}}
                    }
                }
            });
        }

        // ── Gráfica 2: Evolución de Crédito ──
        const creditoLabels=creditoMes.map(r=>{
            const [y,m]=r.mes.split('-');
            return ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][parseInt(m)-1]+' '+y.slice(2);
        });
        let acumCredito=0;
        const creditoAcum=creditoMes.map(r=>{
            acumCredito+=(Number(r.cargos||0)-Number(r.abonos||0));
            return acumCredito;
        });
        const limites=creditoMes.map(()=>Number(c.limite_credito||0));

        if(_caChartCredito){_caChartCredito.destroy();_caChartCredito=null;}
        const ctx2=document.getElementById('caChartCredito')?.getContext('2d');
        if(ctx2){
            ctx2.canvas.style.maxHeight='180px';
            _caChartCredito=new Chart(ctx2,{
                type:'line',
                data:{
                    labels:creditoLabels,
                    datasets:[
                        {label:'Crédito Usado',data:creditoAcum,borderColor:'#f59e0b',backgroundColor:'rgba(245,158,11,.1)',fill:true,tension:.3,pointRadius:3},
                        {label:'Límite',data:limites,borderColor:'#ef4444',borderDash:[6,4],borderWidth:2,pointRadius:0,fill:false}
                    ]
                },
                options:{
                    responsive:true,maintainAspectRatio:false,
                    plugins:{legend:{labels:{color:'#a1a1aa',font:{size:11}}}},
                    scales:{
                        x:{ticks:{color:'#71717a',font:{size:10}},grid:{color:'rgba(255,255,255,.04)'}},
                        y:{ticks:{color:'#f59e0b',font:{size:10},callback:v=>'$'+v.toLocaleString()},grid:{color:'rgba(255,255,255,.06)'}}
                    }
                }
            });
        }

        // ── Gráfica 3: Distribución de Productos (Doughnut) ──
        const prodColors=['#6366f1','#22c55e','#f59e0b','#ef4444','#a5b4fc','#38bdf8','#f472b6','#a3e635'];
        if(_caChartProductos){_caChartProductos.destroy();_caChartProductos=null;}
        const ctx3=document.getElementById('caChartProductos')?.getContext('2d');
        if(ctx3){
            _caChartProductos=new Chart(ctx3,{
                type:'doughnut',
                data:{
                    labels:productos.map(r=>r.producto),
                    datasets:[{
                        data:productos.map(r=>Number(r.total||0)),
                        backgroundColor:prodColors.slice(0,productos.length),
                        borderWidth:0
                    }]
                },
                options:{
                    responsive:true,maintainAspectRatio:false,
                    plugins:{
                        legend:{position:'right',labels:{color:'#a1a1aa',font:{size:10},boxWidth:12,padding:8}}
                    }
                }
            });
        }

        // ── Recomendación automática ──
        const limCredito=Number(c.limite_credito||0);
        const usoCredito=Number(c.credito_usado||0);
        const pctUso=limCredito>0?Math.round((usoCredito/limCredito)*100):0;
        let recom='';

        // Tendencia de ventas
        if(ventasMes.length>=3){
            const last3=ventasMes.slice(-3).map(r=>Number(r.total||0));
            const crecimiento=last3[2]>last3[0];
            const decrecimiento=last3[2]<last3[0]&&last3[1]<last3[0];

            if(crecimiento&&pctUso>=80){
                recom+=`<div style="padding:.6rem;background:rgba(34,197,94,.08);border-radius:8px;margin-bottom:.5rem;border-left:3px solid #22c55e;">
                    <strong style="color:#22c55e;">✅ Recomendación: AUMENTAR CRÉDITO</strong><br>
                    El centro muestra crecimiento sostenido en los últimos 3 meses y está al ${pctUso}% de su límite. Considera aumentar el límite para no frenar el crecimiento.
                </div>`;
            } else if(decrecimiento&&promedioMes<(limCredito*0.3)){
                recom+=`<div style="padding:.6rem;background:rgba(239,68,68,.08);border-radius:8px;margin-bottom:.5rem;border-left:3px solid #ef4444;">
                    <strong style="color:#ef4444;">⚠️ Alerta: EVALUAR CIERRE</strong><br>
                    Las ventas muestran tendencia a la baja por 3 meses consecutivos. El promedio mensual (${_caMoneyFmt(promedioMes,c.moneda)}) es menor al 30% del límite. Evalúa reducir crédito o cerrar el centro.
                </div>`;
            } else if(crecimiento){
                recom+=`<div style="padding:.6rem;background:rgba(99,102,241,.08);border-radius:8px;margin-bottom:.5rem;border-left:3px solid #6366f1;">
                    <strong style="color:#a5b4fc;">📈 Centro en crecimiento</strong><br>
                    Tendencia positiva. Uso de crédito al ${pctUso}%. Mantener operación actual.
                </div>`;
            } else {
                recom+=`<div style="padding:.6rem;background:rgba(245,158,11,.08);border-radius:8px;margin-bottom:.5rem;border-left:3px solid #f59e0b;">
                    <strong style="color:#f59e0b;">📊 Centro estable</strong><br>
                    Sin cambios significativos. Monitorear próximo mes.
                </div>`;
            }
        } else {
            recom+=`<div style="padding:.6rem;background:rgba(113,113,122,.08);border-radius:8px;border-left:3px solid #71717a;">
                <strong style="color:#a1a1aa;">📋 Datos insuficientes</strong><br>
                Se necesitan al menos 3 meses de historial para generar una recomendación automática. Meses actuales: ${ventasMes.length}.
            </div>`;
        }

        // Métricas adicionales
        recom+=`<div style="margin-top:.75rem;font-size:.78rem;color:var(--text-dim);">
            <div>📊 Uso de crédito: <strong style="color:${pctUso>=90?'#ef4444':pctUso>=70?'#f59e0b':'#22c55e'};">${pctUso}%</strong> (${_caMoneyFmt(usoCredito,c.moneda)} / ${_caMoneyFmt(limCredito,c.moneda)})</div>
            <div>📦 Top producto: <strong>${productos[0]?.producto||'—'}</strong> (${productos[0]?.total||0} uds)</div>
            <div>📅 Mejor mes: <strong>${ventasMes.length?meses[totales.indexOf(Math.max(...totales))]:'—'}</strong> (${_caMoneyFmt(Math.max(...totales,0),c.moneda)})</div>
        </div>`;

        document.getElementById('caRendRecomendacion').innerHTML=recom;

    }catch(e){
        console.error('loadRendimientoCentro',e);
        document.getElementById('caRendRecomendacion').innerHTML='<span style="color:#ef4444;">Error cargando datos: '+e.message+'</span>';
    }
}

// ── Exportar Rendimiento como PDF ─────────────────────────────
async function exportarRendimientoPDF(){
    const centroId=window._caDetalleId;
    if(!centroId)return;
    const c=_caData.find(x=>x.id===centroId);
    if(!c)return;

    // jsPDF no soporta emojis — eliminar todo carácter fuera de Latin-1
    const stripPdf=str=>(str||'')
        .replace(/[\u{1F000}-\u{1FFFF}]/gu,'')   // emojis
        .replace(/[^\x00-\x7E\xA0-\xFF]/g,'')    // fuera de latin-1
        .replace(/\s+/g,' ').trim();

    const periodoVal=document.getElementById('caRendPeriodo')?.value||'6';
    const periodoLabel={'3':'Ultimos 3 meses','6':'Ultimos 6 meses','12':'Ultimos 12 meses','all':'Todo el historial'}[periodoVal]||periodoVal+' meses';
    const moneda=c.moneda||'MXN';

    // Recoger KPIs del DOM — limpiar emojis
    const kpiTotal=stripPdf(document.getElementById('caRendTotalVentas')?.textContent||'—');
    const kpiNotas=stripPdf(document.getElementById('caRendTotalNotas')?.textContent||'—');
    const kpiProm=stripPdf(document.getElementById('caRendPromedio')?.textContent||'—');
    // Tendencia: convertir "📈 +43%" → "+43%"
    const kpiTendRaw=(document.getElementById('caRendTendencia')?.textContent||'').trim();
    const kpiTend=stripPdf(kpiTendRaw).replace(/^\s*['"!?]+\s*/,'').trim()||stripPdf(kpiTendRaw)||'—';

    // Recoger recomendación — strip HTML tags y emojis
    const recomEl=document.getElementById('caRendRecomendacion');
    const recomRaw=recomEl?recomEl.innerText||recomEl.textContent:'';
    const recomText=stripPdf(recomRaw.replace(/Recomendación:\s*/i,'').replace(/\n+/g,' '));

    // Líder — ya viene cargado en _caData desde showCentroDetalle
    const liderNombreDB=stripPdf(c._lider_nombre||c.lider_nombre||('ID: '+(c.lider_id||'—')));

    // Administrativo a cargo — campo admin del centro
    const adminNombreDB=stripPdf(c._admin_nombre||c.admin_nombre||('ID: '+(c.admin_id||'—')));

    // Datos mensuales
    let dateFilter='';
    if(periodoVal!=='all') dateFilter=` AND v.created_at >= NOW() - INTERVAL '${parseInt(periodoVal)} months'`;
    const ventasMes=await _caSql(`
        SELECT TO_CHAR(v.created_at,'YYYY-MM') as mes,
               COUNT(*)::int as notas,
               COALESCE(SUM(v.total),0) as total
        FROM venta v
        WHERE v.centro_apoyo_id=${centroId}${dateFilter}
        GROUP BY mes ORDER BY mes`);

    // Construir PDF
    const {jsPDF}=window.jspdf||window;
    if(!jsPDF){return BO_AUTH.showAlert('jsPDF no disponible','warning');}
    const doc=new jsPDF({orientation:'portrait',unit:'mm',format:'a4'});
    const W=210, mg=15;

    // ── Header ──
    doc.setFillColor(30,27,75);
    doc.rect(0,0,W,32,'F');
    doc.setTextColor(255,255,255);
    doc.setFontSize(18);doc.setFont('helvetica','bold');
    doc.text('Reporte de Rendimiento',mg,13);
    doc.setFontSize(10);doc.setFont('helvetica','normal');
    doc.text(stripPdf(c.nombre||'Centro de Apoyo'),mg,21);
    doc.text('Periodo: '+periodoLabel,mg,28);
    doc.text('Generado: '+new Date().toLocaleDateString('es-MX',{day:'2-digit',month:'long',year:'numeric'}),W-mg,28,{align:'right'});

    // Línea separadora
    doc.setDrawColor(99,102,241);doc.setLineWidth(0.5);
    doc.line(mg,35,W-mg,35);

    // ── KPIs ──
    doc.setTextColor(30,27,75);
    doc.setFontSize(12);doc.setFont('helvetica','bold');
    doc.text('Resumen del Periodo',mg,42);

    doc.autoTable({
        startY:46,
        margin:{left:mg,right:mg},
        head:[['KPI','Valor']],
        body:[
            ['Total Ventas', kpiTotal],
            ['Notas / Pedidos', kpiNotas],
            ['Promedio Mensual', kpiProm],
            ['Tendencia', kpiTend],
            ['Lider', liderNombreDB],
            ['Administrativo a cargo', adminNombreDB],
            ['Credito Usado', _caMoneyFmt(Number(c.credito_usado||0),moneda)+' / '+_caMoneyFmt(Number(c.limite_credito||0),moneda)],
        ],
        styles:{fontSize:9.5,cellPadding:3.5},
        headStyles:{fillColor:[99,102,241],textColor:255,fontStyle:'bold'},
        alternateRowStyles:{fillColor:[240,240,255]},
        columnStyles:{0:{fontStyle:'bold',cellWidth:72},1:{cellWidth:'auto'}},
    });

    // ── Tabla mensual ──
    const afterKpi=doc.lastAutoTable.finalY+10;
    doc.setFontSize(12);doc.setFont('helvetica','bold');doc.setTextColor(30,27,75);
    doc.text('Ventas por Mes',mg,afterKpi);

    const meses=['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    doc.autoTable({
        startY:afterKpi+4,
        margin:{left:mg,right:mg},
        head:[['Mes','Notas','Total']],
        body:ventasMes.map(r=>{
            const [y,m]=r.mes.split('-');
            return [meses[parseInt(m)-1]+' '+y, String(r.notas), _caMoneyFmt(Number(r.total||0),moneda)];
        }),
        styles:{fontSize:9.5,cellPadding:3.5},
        headStyles:{fillColor:[99,102,241],textColor:255,fontStyle:'bold'},
        alternateRowStyles:{fillColor:[240,240,255]},
        columnStyles:{1:{halign:'center'},2:{halign:'right'}},
    });

    // ── Recomendación ──
    const afterTable=doc.lastAutoTable.finalY+10;
    if(recomText && afterTable<265){
        doc.setFontSize(12);doc.setFont('helvetica','bold');doc.setTextColor(30,27,75);
        doc.text('Recomendacion IA',mg,afterTable);
        doc.setFontSize(8.5);doc.setFont('helvetica','normal');doc.setTextColor(70,70,70);
        const lines=doc.splitTextToSize(recomText,W-mg*2);
        doc.text(lines,mg,afterTable+7);
    }

    // ── Pie ──
    doc.setDrawColor(200,200,200);doc.setLineWidth(0.3);
    doc.line(mg,286,W-mg,286);
    doc.setFontSize(7.5);doc.setTextColor(150,150,150);
    doc.text('Duletia Backoffice  |  '+new Date().toISOString().slice(0,10),W/2,291,{align:'center'});

    const fileName='rendimiento_'+stripPdf(c.nombre||String(centroId)).replace(/\s+/g,'_')+'_'+new Date().toISOString().slice(0,7)+'.pdf';
    doc.save(fileName);
    BO_AUTH.showAlert('PDF generado: '+fileName,'success');
}

// ── showView handler ──────────────────────────────────────────
(function(){
    const orig=window.showView;
    if(!orig||window._caShowViewPatched)return;
    window._caShowViewPatched=true;
    window.showView=function(id,...args){
        orig.call(this,id,...args);
        if(id==='centros-apoyo')loadCentrosApoyo();
    };
})();

// ══════════════════════════════════════════════════════════════
// Tab: PAGOS — Registro, historial y KPIs
// ══════════════════════════════════════════════════════════════

// ── Crear tablas en BD si no existen ─────────────────────────
(async function _initCaPagoTables(){
    try{
        await _caSql(`CREATE TABLE IF NOT EXISTS centro_apoyo_pago (
            id              SERIAL PRIMARY KEY,
            centro_id       INTEGER NOT NULL,
            lider_id        TEXT NOT NULL,
            lider_nombre    TEXT,
            monto           NUMERIC(12,2) NOT NULL,
            moneda          VARCHAR(5) DEFAULT 'MXN',
            banco_plataforma VARCHAR(50),
            metodo_pago     VARCHAR(50) DEFAULT 'Transferencia',
            referencia      TEXT,
            es_activacion   BOOLEAN DEFAULT FALSE,
            mes_activacion  VARCHAR(7),
            registrado_por  TEXT,
            fecha           TIMESTAMPTZ DEFAULT NOW(),
            notas           TEXT
        )`);
        // Agregar columnas si la tabla ya existía con esquema viejo
        const alts=['banco_plataforma VARCHAR(50)','metodo_pago VARCHAR(50)','lider_id TEXT','lider_nombre TEXT','moneda VARCHAR(5)','notas TEXT','es_activacion BOOLEAN DEFAULT FALSE','mes_activacion VARCHAR(7)','tipo_pago VARCHAR(50)','descripcion TEXT'];
        for(const col of alts){ await _caSql(`ALTER TABLE centro_apoyo_pago ADD COLUMN IF NOT EXISTS ${col}`); }
        await _caSql(`CREATE TABLE IF NOT EXISTS activacion_mensual (
            id              SERIAL PRIMARY KEY,
            lider_id        TEXT NOT NULL,
            mes             VARCHAR(7) NOT NULL,
            monto           NUMERIC(12,2),
            origen          VARCHAR(30) NOT NULL DEFAULT 'pago_centro',
            centro_id       INTEGER,
            pago_id         INTEGER,
            registrado_por  TEXT,
            fecha           TIMESTAMPTZ DEFAULT NOW(),
            UNIQUE(lider_id, mes)
        )`);
        await _caSql(`CREATE TABLE IF NOT EXISTS centro_apoyo_pago_venta (
            id              SERIAL PRIMARY KEY,
            pago_id         INTEGER NOT NULL,
            venta_id        TEXT NOT NULL,
            monto_aplicado  NUMERIC(12,2) NOT NULL DEFAULT 0
        )`);
        // Agregar venta_id a activacion_mensual para vincular con nota sintética
        await _caSql(`ALTER TABLE activacion_mensual ADD COLUMN IF NOT EXISTS venta_id VARCHAR(60)`);
        // Control de capacidad por nota: cuántas activaciones ha consumido ya esta nota
        await _caSql(`ALTER TABLE venta ADD COLUMN IF NOT EXISTS activaciones_usadas INTEGER DEFAULT 0`);
        // Fix Eucament: caja de 48 frascos → puntos_equivalencia=0.5 para que 48×0.5=24pts=1 caja
        await _caSql(`UPDATE productos SET puntos_equivalencia=0.5 WHERE nombre ILIKE '%eucament%' AND (puntos_equivalencia IS NULL OR puntos_equivalencia=1)`);
        console.log('✅ Tablas centro_apoyo_pago + activacion_mensual + pago_venta OK + Eucament pts fix');
    }catch(e){console.warn('_initCaPagoTables:',e.message);}
})();

// ── Autocomplete: buscar líder que paga ──────────────────────
let _caPagoLiderTimer=null;
function buscarLiderParaPago(q){
    clearTimeout(_caPagoLiderTimer);
    const box=document.getElementById('caPago-lider-results');
    if(!box)return;
    if(!q||q.length<2){box.style.display='none';return;}
    _caPagoLiderTimer=setTimeout(async()=>{
        try{
            const qEsc=q.replace(/'/g,"''");
            const rows=await _caSql(`SELECT DISTINCT c.id, c.registro, c.nombre, c.ape_p, c.ape_m
                FROM cliente c
                WHERE (c.nombre||' '||COALESCE(c.ape_p,'')||' '||COALESCE(c.ape_m,'')) ILIKE '%${qEsc}%'
                   OR c.registro::text ILIKE '%${qEsc}%'
                ORDER BY c.nombre LIMIT 12`);
            if(!rows.length){
                box.innerHTML='<div style="padding:.5rem;font-size:.78rem;color:var(--text-dim);">Sin resultados</div>';
                box.style.display='block';
                return;
            }
            box.innerHTML=rows.map(r=>{
                const nom=(r.nombre||'').replace(/'/g,"\\'");
                const ap=(r.ape_p||'').replace(/'/g,"\\'");
                return `<div style="padding:.45rem .75rem;font-size:.82rem;cursor:pointer;border-bottom:1px solid rgba(255,255,255,.04);" onmouseover="this.style.background='rgba(34,197,94,.12)'" onmouseout="this.style.background=''" onclick="seleccionarLiderParaPago('${r.id}','${nom}','${ap}',${r.registro||0})"><strong>#${r.registro||'?'}</strong> — ${r.nombre||''} ${r.ape_p||''} ${r.ape_m||''}</div>`;
            }).join('');
            box.style.display='block';
        }catch(e){
            box.innerHTML='<div style="color:#ef4444;padding:.5rem;font-size:.78rem;">Error: '+e.message+'</div>';
            box.style.display='block';
        }
    },300);
}

function seleccionarLiderParaPago(id,nombre,ape,reg){
    const idEl=document.getElementById('caPago-lider-id');
    const searchEl=document.getElementById('caPago-lider-search');
    const selEl=document.getElementById('caPago-lider-selected');
    const boxEl=document.getElementById('caPago-lider-results');
    if(idEl) idEl.value=id;
    if(searchEl) searchEl.value='#'+reg+' — '+nombre+' '+ape;
    if(selEl) selEl.textContent='✓ '+nombre+' '+ape+' (#'+reg+')';
    if(boxEl) boxEl.style.display='none';
}

// ── Autocomplete: buscar líder que paga ──────────────────────
function caPagoActivacionToggle(checked){
    const wrap=document.getElementById('caPago-activacion-mes-wrap');
    const mesInput=document.getElementById('caPago-activacion-mes');
    if(wrap) wrap.style.display=checked?'block':'none';
    if(checked && mesInput && !mesInput.value){
        const fInput=document.getElementById('caPago-fecha')?.value;
        const mFiltro=document.getElementById('caPagoMesFiltro')?.value;
        mesInput.value=fInput ? fInput.slice(0,7) : (mFiltro || new Date().toISOString().slice(0,7));
    }
}
window.caPagoActivacionToggle = caPagoActivacionToggle;

// ── Registrar pago + aplicar FIFO a notas pendientes ─────────
async function registrarPagoCentro(){
    const centroId=window._caDetalleId;
    if(!centroId)return;
    const c=_caData.find(x=>x.id===centroId);
    // Usar el líder del buscador si fue seleccionado, sino el líder asignado al centro
    const liderIdInput=(document.getElementById('caPago-lider-id')||{}).value||'';
    const liderId=liderIdInput||c?.lider_id||'';
    const liderNombreInput=(document.getElementById('caPago-lider-search')||{}).value||'';
    const liderNombre=(liderNombreInput||c?.lider_nombre||'Lider del centro').replace(/'/g,"''");
    const monto=Number(document.getElementById('caPago-monto').value);
    const banco=document.getElementById('caPago-banco').value;
    const metodo=document.getElementById('caPago-metodo').value;
    const referencia=(document.getElementById('caPago-referencia').value||'').trim().replace(/'/g,"''");
    const notas=(document.getElementById('caPago-notas').value||'').trim().replace(/'/g,"''");

    // Fecha de depósito (respetar la fecha indicada en el input o el mes configurado)
    const fechaInput=(document.getElementById('caPago-fecha')?.value||'').trim();
    const mesFiltroActual=document.getElementById('caPagoMesFiltro')?.value||'';
    let fechaDeposito=fechaInput;
    if(!fechaDeposito && mesFiltroActual){
        const [y,m]=mesFiltroActual.split('-').map(Number);
        const d=new Date(y, m, 0); // último día del mes del filtro
        fechaDeposito=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    }
    if(!fechaDeposito){
        fechaDeposito=new Date().toISOString().slice(0,10);
    }
    const fechaDepSql=`'${fechaDeposito}'`;
    const creadoEnSql=`'${fechaDeposito} 12:00:00'`;

    const esActivacion=document.getElementById('caPago-activacion')?.checked||false;
    const mesActivacion=document.getElementById('caPago-activacion-mes')?.value || fechaDeposito.slice(0,7);
    const esActSql=esActivacion?'true':'false';
    const mesActSql=`'${mesActivacion}'`;

    if(!liderId)return BO_AUTH.showAlert('Este centro no tiene lider asignado','warning');
    if(!banco)return BO_AUTH.showAlert('Selecciona un banco / plataforma','warning');
    if(!metodo)return BO_AUTH.showAlert('Selecciona un metodo de pago','warning');
    if(!monto||monto<=0)return BO_AUTH.showAlert('Ingresa un monto valido','warning');

    const moneda=c?.moneda||'MXN';
    const user=CURRENT_USER?(CURRENT_USER.nombre+' '+CURRENT_USER.apellido).replace(/'/g,"''"):'admin';
    const bancoEsc=banco.replace(/'/g,"''");

    try{
        // 1. Insertar pago incluyendo fecha_deposito, creado_en y metadatos de activación
        const descPago=banco+' '+metodo+(referencia?' Ref:'+referencia:'');
        const res=await _caSql(`INSERT INTO centro_apoyo_pago(centro_id,lider_id,lider_nombre,monto,moneda,banco_plataforma,metodo_pago,referencia,registrado_por,notas,tipo_pago,descripcion,fecha_deposito,creado_en,es_activacion,mes_activacion) VALUES(${centroId},'${liderId}','${liderNombre}',${monto},'${moneda}','${bancoEsc}','${metodo}','${referencia}','${user}','${notas}','${metodo}','${descPago.replace(/'/g,"''")}',${fechaDepSql},${creadoEnSql},${esActSql},${mesActSql}) RETURNING id`);
        let pagoId=res[0]?.id||null;
        // Fallback: si RETURNING no funcionó, buscar el último pago insertado
        if(!pagoId){
            const last=await _caSql(`SELECT id FROM centro_apoyo_pago WHERE centro_id=${centroId} ORDER BY id DESC LIMIT 1`);
            pagoId=last[0]?.id||null;
        }

        // 2. Aplicar FIFO a notas seleccionadas
        const checks=[...document.querySelectorAll('.caPago-notaCheck:checked')];
        let restante=monto;
        for(const cb of checks){
            if(restante<=0)break;
            const ventaId=cb.value;
            const saldo=Number(cb.dataset.saldo||0);
            const aplicar=Math.min(restante,saldo);
            if(aplicar>0 && pagoId){
                await _caSql(`INSERT INTO centro_apoyo_pago_venta(pago_id,venta_id,monto_aplicado) VALUES(${pagoId},'${ventaId}',${aplicar})`);
                restante-=aplicar;
            }
        }

        // 3. Decrementar crédito (sin GREATEST, permite a favor)
        await _recalcCreditoUsado(centroId, -monto);

        // 4. Movimiento con la fecha seleccionada
        const descMov='Pago '+banco+' '+metodo+': '+liderNombre+(referencia?' - Ref: '+referencia:'')+(notas?' - '+notas:'');
        await _caSql(`INSERT INTO centro_apoyo_movimiento(centro_id,tipo,monto,moneda,descripcion,registrado_por,creado_en) VALUES(${centroId},'abono',${monto},'${moneda}','${descMov.replace(/'/g,"''")}','${user}',${creadoEnSql})`);

        // 5. Si cuenta como activación, registrar en activacion_mensual
        if(esActivacion && liderId){
            try{
                const montoA=getMontoActivacion(moneda);
                await _caSql(`INSERT INTO activacion_mensual(lider_id,mes,monto,origen,centro_id,pago_id,registrado_por,fecha) VALUES('${liderId}','${mesActivacion}',${montoA},'pago_centro',${pagoId || 'NULL'},'${user}',${creadoEnSql}) ON CONFLICT(lider_id,mes) DO UPDATE SET monto=EXCLUDED.monto,origen=EXCLUDED.origen,centro_id=EXCLUDED.centro_id,pago_id=EXCLUDED.pago_id,registrado_por=EXCLUDED.registrado_por,fecha=${creadoEnSql}`);
            }catch(actErr){
                console.warn('[Activacion Mensual Error]', actErr);
            }
        }

        BO_AUTH.showAlert('Pago de '+_caMoneyFmt(monto,moneda)+' registrado ('+banco+' / '+metodo+') para '+fechaDeposito,'success');

        document.getElementById('caPago-monto').value='';
        document.getElementById('caPago-banco').value='';
        document.getElementById('caPago-metodo').value='';
        document.getElementById('caPago-referencia').value='';
        document.getElementById('caPago-notas').value='';
        if(document.getElementById('caPago-fecha')) document.getElementById('caPago-fecha').value='';
        if(document.getElementById('caPago-activacion')){
            document.getElementById('caPago-activacion').checked=false;
            caPagoActivacionToggle(false);
        }
        // Limpiar también el buscador de líder
        const lSrch=document.getElementById('caPago-lider-search');
        const lId=document.getElementById('caPago-lider-id');
        const lSel=document.getElementById('caPago-lider-selected');
        if(lSrch) lSrch.value='';
        if(lId) lId.value='';
        if(lSel) lSel.textContent='';

        await loadCentrosApoyo();
        const cUpd=_caData.find(x=>x.id===centroId);
        if(cUpd){
            const lim=Number(cUpd.limite_credito||0),uso=Number(cUpd.credito_usado||0);
            const pct=lim>0?Math.min(100,Math.round(uso/lim*100)):0;
            const barColor=pct>=90?'#ef4444':pct>=70?'#f59e0b':'#22c55e';
            document.getElementById('caCreditBar').style.width=pct+'%';
            document.getElementById('caCreditBar').style.background=barColor;
            document.getElementById('caBarraPct').textContent=pct+'%';
            document.getElementById('caBarraUsado').textContent=_caMoneyFmt(uso,cUpd.moneda);
        }
        loadPagosCentro(centroId);
    }catch(e){
        console.error('registrarPagoCentro',e);
        BO_AUTH.showAlert('Error al registrar pago: '+e.message,'danger');
    }
}

// ── Monto de activación por moneda (lee de Configuración global) ──
function getMontoActivacion(moneda){
    if(typeof COMMISSION_CONFIG!=='undefined' && COMMISSION_CONFIG.precio_caja){
        return Number(COMMISSION_CONFIG.precio_caja[moneda] || COMMISSION_CONFIG.precio_caja.MXN || 3000);
    }
    const defaults={MXN:4500, USD:300, GTQ:2250, CAD:415, SLV_USD:300};
    return defaults[moneda]||defaults.MXN;
}

// ── Cargar pagos + estado de notas + notas pendientes ────────
async function loadPagosCentro(centroId){
    if(!centroId)return;
    const tb=document.getElementById('caPagosBody');
    const c=_caData.find(x=>x.id===centroId);
    const moneda=c?.moneda||'MXN';

    // ── Filtro de mes ──────────────────────────────────────────
    const mesInput=document.getElementById('caPagoMesFiltro');
    const verTodoEl=document.getElementById('caPagoVerTodo');
    // Primera carga: marcar "Ver todo" por defecto para que el usuario vea todo
    if(mesInput && !mesInput.value){
        const now=new Date();
        mesInput.value=now.getFullYear()+'-'+String(now.getMonth()+1).padStart(2,'0');
        if(verTodoEl && !verTodoEl.dataset.touched) { verTodoEl.checked=true; verTodoEl.dataset.touched='1'; }
    }
    const verTodo=verTodoEl?.checked||false;
    const mesFiltro=(!verTodo && mesInput?.value) ? mesInput.value : null;
    const whereClauseMes=mesFiltro ? ` AND TO_CHAR(COALESCE(p.fecha_deposito::timestamptz,p.creado_en),'YYYY-MM')='${mesFiltro}'` : '';

    // Si caPago-fecha está vacío, sugerir la fecha correspondiente al período seleccionado
    const fInputEl = document.getElementById('caPago-fecha');
    if (fInputEl && !fInputEl.value) {
        if (mesFiltro) {
            const [y, m] = mesFiltro.split('-').map(Number);
            const d = new Date(y, m, 0);
            fInputEl.value = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        } else {
            fInputEl.value = new Date().toISOString().slice(0, 10);
        }
    }


    try{
        // Pagos — con filtro de mes opcional
        let rows=await _caSql(`SELECT p.*, (SELECT string_agg(pv.venta_id::text,', ') FROM centro_apoyo_pago_venta pv WHERE pv.pago_id=p.id) AS notas_aplicadas FROM centro_apoyo_pago p WHERE p.centro_id=${centroId}${whereClauseMes} ORDER BY COALESCE(p.fecha_deposito::timestamptz,p.creado_en) DESC`);
        if(!rows.length && !whereClauseMes) rows=await _caSql(`SELECT * FROM centro_apoyo_pago WHERE centro_id=${centroId} ORDER BY id DESC`);
        const totalMonto=rows.reduce((s,r)=>s+Number(r.monto||0),0);
        const montoActiv=getMontoActivacion(moneda);
        // ── KPI activaciones: usar cajas comisionables reales, no monto/$$ ──
        // Traer notas del centro con cajas_calc para calcular activaciones correctas
        const CAJAS_CALC_EXPR_P =
            "COALESCE(" +
            "  (SELECT SUM(" +
            "    CASE" +
            "      WHEN COALESCE(pr.categoria,pr2.categoria)='paquete' AND EXISTS(SELECT 1 FROM producto_componente pc2 WHERE pc2.paquete_id=COALESCE(pr.id,pr2.id)) THEN" +
            "        COALESCE((SELECT SUM(CASE WHEN comp_p.comisiona=true THEN COALESCE(comp_p.puntos_equivalencia,1)*pc.cantidad*(pz.value->>'cantidad')::int ELSE 0 END)" +
            "          FROM producto_componente pc JOIN productos comp_p ON comp_p.id=pc.componente_id WHERE pc.paquete_id=COALESCE(pr.id,pr2.id)),0)" +
            "      WHEN COALESCE(pr.comisiona,pr2.comisiona)=true THEN" +
            "        COALESCE(COALESCE(pr.puntos_equivalencia,pr2.puntos_equivalencia),1)*(pz.value->>'cantidad')::int" +
            "      ELSE 0" +
            "    END" +
            "  ) FROM jsonb_array_elements(v.piezas::jsonb) pz(value)" +
            "  LEFT JOIN productos pr ON pr.id=(pz.value->>'producto_id')::int" +
            "  LEFT JOIN productos pr2 ON pr.id IS NULL AND pr2.sku=(pz.value->>'sku') AND pr2.activo=true" +
            "  )/24.0, v.cantidad) AS cajas_calc";
        const ventasPagos=await _caSql(`SELECT v.id, v.total, ${CAJAS_CALC_EXPR_P} FROM venta v WHERE v.centro_apoyo_id=${centroId} AND v.approved=1`);
        const totalCajasKpi = ventasPagos.reduce((s,v)=>s+Math.floor(parseFloat(v.cajas_calc)||0),0);
        // Cajas pagadas con activos (para restar)
        const pagosTodos=await _caSql(`SELECT monto,banco_plataforma,tipo_pago FROM centro_apoyo_pago WHERE centro_id=${centroId}`);
        const montoActivosCobradoKpi=pagosTodos.filter(p=>(p.banco_plataforma||p.tipo_pago||'').toLowerCase().includes('activos')).reduce((s,p)=>s+Number(p.monto||0),0);
        const cajasCobradaKpi=Math.floor(montoActivosCobradoKpi/montoActiv);
        const activaciones=Math.max(0, totalCajasKpi - cajasCobradaKpi);

        // Notas con estado de pago
        const ventasMesFiltro = mesFiltro ? ` AND TO_CHAR(v.fecha_venta, 'YYYY-MM')='${mesFiltro}'` : '';
        let ventas=await _caSql(`SELECT v.id, v.total, v.fecha_venta, COALESCE(c2.nombre||' '||COALESCE(c2.ape_p,''),'Publico General') AS cliente_nombre, COALESCE((SELECT SUM(pv.monto_aplicado) FROM centro_apoyo_pago_venta pv WHERE pv.venta_id::text=v.id::text),0) AS pagado FROM venta v LEFT JOIN cliente c2 ON c2.id=v.cliente_id WHERE v.centro_apoyo_id=${centroId}${ventasMesFiltro} ORDER BY v.fecha_venta DESC`);
        if(!ventas.length) ventas=await _caSql(`SELECT v.id, v.total, v.fecha_venta, COALESCE(c2.nombre||' '||COALESCE(c2.ape_p,''),'Publico General') AS cliente_nombre, 0 AS pagado FROM venta v LEFT JOIN cliente c2 ON c2.id=v.cliente_id WHERE v.centro_apoyo_id=${centroId}${ventasMesFiltro} ORDER BY v.fecha_venta DESC`);
        const totalCredito=ventas.reduce((s,v)=>s+Number(v.total||0),0);
        const totalPagado=ventas.reduce((s,v)=>s+Number(v.pagado||0),0);
        const saldoPendiente=totalCredito-totalPagado;

        // KPIs — reflejan el período filtrado
        const set=(id,val)=>{const el=document.getElementById(id);if(el)el.textContent=val;};
        set('caPagoKpi-total',_caMoneyFmt(totalMonto,moneda));
        set('caPagoKpi-saldo',_caMoneyFmt(Math.max(0,saldoPendiente),moneda));
        set('caPagoKpi-activaciones',activaciones);
        set('caPagoKpi-count',rows.length);

        // Indicador de período en el encabezado del historial
        const periodoLabelEl=document.getElementById('caPago-periodo-label');
        if(periodoLabelEl){
            const meses=['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
            if(mesFiltro){
                const [y,m]=mesFiltro.split('-');
                periodoLabelEl.textContent=meses[parseInt(m)-1]+' '+y;
                periodoLabelEl.style.display='inline-block';
            } else {
                periodoLabelEl.textContent='Histórico completo';
                periodoLabelEl.style.display='inline-block';
            }
        }

        // Estado de Notas
        const tbNotas=document.getElementById('caPago-estadoNotas');
        if(tbNotas){
            if(!ventas.length){
                tbNotas.innerHTML='<tr><td colspan="7" style="text-align:center;color:var(--text-dim);padding:1rem;">Sin notas vinculadas</td></tr>';
            } else {
                tbNotas.innerHTML=ventas.map(v=>{
                    const total=Number(v.total||0),pagado=Number(v.pagado||0),saldo=total-pagado;
                    const estado=saldo<=0?'<span style="color:#22c55e;">✅ Liquidada</span>':pagado>0?'<span style="color:#f59e0b;">🟡 Parcial</span>':'<span style="color:#ef4444;">🔴 Pendiente</span>';
                    return `<tr>
                        <td><span style="font-family:monospace;font-size:.78rem;">${(v.id||'').toString().substring(0,12)}</span></td>
                        <td>${v.cliente_nombre||'--'}</td>
                        <td>${v.fecha_venta?new Date(v.fecha_venta).toLocaleDateString('es-MX'):'--'}</td>
                        <td style="text-align:right;">${_caMoneyFmt(total,moneda)}</td>
                        <td style="text-align:right;color:#22c55e;">${_caMoneyFmt(pagado,moneda)}</td>
                        <td style="text-align:right;font-weight:600;">${_caMoneyFmt(saldo,moneda)}</td>
                        <td>${estado}</td>
                    </tr>`;
                }).join('');
            }
        }

        // Notas pendientes (checkboxes para FIFO)
        const pendientes=ventas.filter(v=>Number(v.total||0)-Number(v.pagado||0)>0);
        const wrapPend=document.getElementById('caPago-notasPendientes');
        const tbPend=document.getElementById('caPago-notasBody');
        if(wrapPend && tbPend){
            if(!pendientes.length){
                wrapPend.style.display='none';
            } else {
                wrapPend.style.display='';
                tbPend.innerHTML=pendientes.map(v=>{
                    const saldo=Number(v.total||0)-Number(v.pagado||0);
                    return `<tr>
                        <td><input type="checkbox" class="caPago-notaCheck" value="${v.id}" data-saldo="${saldo}" checked></td>
                        <td style="font-family:monospace;font-size:.78rem;">${(v.id||'').toString().substring(0,12)}</td>
                        <td>${v.cliente_nombre||'--'}</td>
                        <td style="text-align:right;color:#f59e0b;font-weight:600;">${_caMoneyFmt(saldo,moneda)}</td>
                    </tr>`;
                }).join('');
            }
        }

        // Historial de pagos
        if(!rows.length){
            tb.innerHTML='<tr><td colspan="7" style="text-align:center;color:var(--text-dim);padding:1.5rem;">No hay pagos registrados aun</td></tr>';
            return;
        }
        tb.innerHTML=rows.map(r=>`<tr>
            <td style="white-space:nowrap;">${(r.fecha_deposito||r.creado_en)?new Date(r.fecha_deposito||r.creado_en).toLocaleDateString('es-MX',{day:'2-digit',month:'short',year:'numeric'}):'--'}</td>
            <td style="text-align:right;font-weight:600;color:#22c55e;">${_caMoneyFmt(r.monto,r.moneda||moneda)}</td>
            <td style="font-size:.78rem;font-weight:600;">${r.banco_plataforma||r.tipo_pago||'--'}</td>
            <td style="font-size:.78rem;">${r.metodo_pago||'--'}</td>
            <td style="font-size:.78rem;font-family:monospace;">${r.referencia||'--'}</td>
            <td style="font-size:.72rem;color:var(--text-dim);">${r.notas_aplicadas?'📋 '+r.notas_aplicadas.split(',').length+' nota(s)':'--'}</td>
            <td style="font-size:.72rem;color:var(--text-dim);">${r.registrado_por||'--'}</td>
        </tr>`).join('');
    }catch(e){
        tb.innerHTML='<tr><td colspan="7" style="color:#ef4444;padding:1rem;">Error: '+e.message+'</td></tr>';
    }
}

// ── Exportar CSV: Historial de Pagos ──────────────────────────
async function exportarCsvPagosCentro(centroId){
    if(!centroId) centroId = window._caDetalleId;
    if(!centroId) return;
    const c = _caData.find(x=>x.id===centroId);
    const centroNombre = c ? (c.nombre || '').replace(/[/\\?%*:|"<>]/g, '-').trim() : `Centro_${centroId}`;
    const moneda = c?.moneda || 'MXN';

    const mesInput = document.getElementById('caPagoMesFiltro');
    const verTodoEl = document.getElementById('caPagoVerTodo');
    const verTodo = verTodoEl?.checked || false;
    const mesFiltro = (!verTodo && mesInput?.value) ? mesInput.value : null;
    const whereClauseMes = mesFiltro ? ` AND TO_CHAR(COALESCE(p.fecha_deposito::timestamptz,p.creado_en),'YYYY-MM')='${mesFiltro}'` : '';

    try {
        let rows = await _caSql(`SELECT p.*, (SELECT string_agg(pv.venta_id::text,', ') FROM centro_apoyo_pago_venta pv WHERE pv.pago_id=p.id) AS notas_aplicadas FROM centro_apoyo_pago p WHERE p.centro_id=${centroId}${whereClauseMes} ORDER BY COALESCE(p.fecha_deposito::timestamptz,p.creado_en) DESC`);
        if(!rows.length && !whereClauseMes) rows = await _caSql(`SELECT * FROM centro_apoyo_pago WHERE centro_id=${centroId} ORDER BY id DESC`);
        
        if(!rows || !rows.length){
            BO_AUTH.showAlert('No hay pagos registrados para exportar en este período', 'warning');
            return;
        }

        const csvHeaders = [
            'ID',
            'Fecha Registro',
            'Monto',
            'Moneda',
            'Banco / Plataforma',
            'Método de Pago',
            'Fecha Depósito',
            'Referencia',
            'Líder / Cliente',
            'Es Activación',
            'Mes Activación',
            'Notas / Observaciones',
            'Notas Aplicadas',
            'Registrado Por'
        ];

        const csvEscape = (val) => {
            if (val === null || val === undefined) return '""';
            const str = String(val).replace(/"/g, '""');
            return `"${str}"`;
        };

        const csvLines = [csvHeaders.join(',')];

        for (const r of rows) {
            const fechaReg = (r.creado_en || r.fecha_deposito) ? new Date(r.creado_en || r.fecha_deposito).toLocaleString('es-MX') : '';
            const fechaDep = r.fecha_deposito ? new Date(r.fecha_deposito).toLocaleDateString('es-MX') : '';
            const monto = Number(r.monto || 0).toFixed(2);
            const esAct = r.es_activacion ? 'Sí' : 'No';
            const lider = r.lider_nombre || r.lider_id || '';
            const banco = r.banco_plataforma || r.tipo_pago || '';

            csvLines.push([
                r.id || '',
                csvEscape(fechaReg),
                monto,
                csvEscape(r.moneda || moneda),
                csvEscape(banco),
                csvEscape(r.metodo_pago || ''),
                csvEscape(fechaDep),
                csvEscape(r.referencia || ''),
                csvEscape(lider),
                csvEscape(esAct),
                csvEscape(r.mes_activacion || ''),
                csvEscape(r.notas || ''),
                csvEscape(r.notas_aplicadas || ''),
                csvEscape(r.registrado_por || '')
            ].join(','));
        }

        const csvContent = '\uFEFF' + csvLines.join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const periodoStr = mesFiltro ? mesFiltro : 'historico_completo';
        a.download = `pagos_${centroNombre}_${periodoStr}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);

        BO_AUTH.showAlert(`✓ CSV de Pagos descargado (${rows.length} registros)`, 'success');
    } catch(e) {
        console.error('Error exportando CSV de pagos:', e);
        BO_AUTH.showAlert('Error al generar CSV de pagos: ' + e.message, 'danger');
    }
}
window.exportarCsvPagosCentro = exportarCsvPagosCentro;

// ══════════════════════════════════════════════════════════════
// Tab: ACTIVACIONES — Derecho por pagos + asignación de líderes
// ══════════════════════════════════════════════════════════════

// ── Autocomplete: buscar líder para activar ──────────────────
let _caActivLiderTimer=null;
function buscarLiderParaActivar(q){
    clearTimeout(_caActivLiderTimer);
    const box=document.getElementById('caActiv-lider-results');
    if(!box)return;
    if(q.length<2){box.style.display='none';return;}
    _caActivLiderTimer=setTimeout(async()=>{
        try{
            const qEsc=q.replace(/'/g,"''");
            const rows=await _caSql(`SELECT DISTINCT c.id, c.registro, c.nombre, c.ape_p, c.ape_m
                FROM cliente c
                WHERE (c.nombre||' '||COALESCE(c.ape_p,'')||' '||COALESCE(c.ape_m,'')) ILIKE '%${qEsc}%'
                   OR c.registro::text ILIKE '%${qEsc}%'
                ORDER BY c.nombre LIMIT 12`);
            if(!rows.length){box.innerHTML='<div style="padding:.5rem;font-size:.78rem;color:var(--text-dim);">Sin resultados</div>';box.style.display='block';return;}
            box.innerHTML=rows.map(r=>{
                const nom=(r.nombre||'').replace(/'/g,"\\'");
                const ap=(r.ape_p||'').replace(/'/g,"\\'");
                return `<div style="padding:.45rem .75rem;font-size:.82rem;cursor:pointer;border-bottom:1px solid rgba(255,255,255,.04);" onmouseover="this.style.background='rgba(99,102,241,.15)'" onmouseout="this.style.background=''" onclick="seleccionarLiderActivar('${r.id}','${nom}','${ap}',${r.registro||0})"><strong>#${r.registro||'?'}</strong> — ${r.nombre||''} ${r.ape_p||''} ${r.ape_m||''}</div>`;
            }).join('');
            box.style.display='block';
        }catch(e){box.innerHTML='<div style="color:#ef4444;padding:.5rem;font-size:.78rem;">Error: '+e.message+'</div>';box.style.display='block';}
    },300);
}

function seleccionarLiderActivar(id,nombre,ape,reg){
    document.getElementById('caActiv-lider-id').value=id;
    document.getElementById('caActiv-lider-search').value='#'+reg+' — '+nombre+' '+ape;
    document.getElementById('caActiv-lider-selected').textContent='✓ '+nombre+' '+ape+' (#'+reg+')';
    document.getElementById('caActiv-lider-results').style.display='none';
}

// ── Helper: Crear nota de venta "Calificación del Mes" ───────
async function crearVentaCalificacion(liderUUID, centroId, mes, moneda, centroNombre){
    const ventaId = crypto.randomUUID();
    const montoCalif = getMontoActivacion(moneda);
    const mesDate = mes + '-01';
    const comentario = ('Calificacion del Mes - Centro de Apoyo ' + (centroNombre||'')).replace(/'/g,"''");

    const ok = await _caSql(`INSERT INTO venta(id,cantidad,total,comentario,fecha_venta,producto_id,cliente_id,approved,status_id,centro_apoyo_id) VALUES('${ventaId}',1,${montoCalif},'${comentario}','${mesDate}','calificacion-mes','${liderUUID}',1,1,${centroId}) RETURNING id`);

    if(ok.length){
        console.log('✅ Nota calificación creada:', ventaId.substring(0,12), 'para', liderUUID.substring(0,8), mes);
    } else {
        console.error('❌ No se pudo crear nota de calificación para', liderUUID.substring(0,8));
    }
    return ventaId;
}

// ── Diagnóstico: ejecutar desde consola con diagnosticoActivaciones() ──
window.diagnosticoActivaciones = async function(){
    console.log('=== DIAGNÓSTICO ACTIVACIONES ===');
    const activs = await _caSql(`SELECT am.*, c2.nombre as lider_nombre, c2.registro FROM activacion_mensual am LEFT JOIN cliente c2 ON c2.id=am.lider_id ORDER BY am.mes DESC`);
    console.table(activs);
    console.log('Total activaciones:', activs.length);
    const conVenta = activs.filter(a=>a.venta_id);
    console.log('Con venta_id:', conVenta.length, '| Sin venta_id:', activs.length - conVenta.length);
    for(const a of conVenta){
        const v = await _caSql(`SELECT id,total,approved,fecha_venta,comentario FROM venta WHERE id='${a.venta_id}'`);
        console.log(a.venta_id?.substring(0,8), v.length?'✅ Existe':'❌ No existe en venta', v[0]||'');
    }
    // Verificar estructura tabla venta
    const cols = await _caSql(`SELECT column_name, data_type FROM information_schema.columns WHERE table_name='venta' ORDER BY ordinal_position`);
    console.log('Columnas de venta:', cols.map(c=>c.column_name));
    console.log('=== FIN DIAGNÓSTICO ===');
};

// ── Retroactivo: crear ventas para activaciones sin nota ─────
(async function _fixActivacionesSinVenta(){
    try{
        // Esperar a que _initCaPagoTables termine (la columna venta_id debe existir)
        await new Promise(r=>setTimeout(r,2000));
        const rows=await _caSql(`SELECT am.lider_id, am.mes, am.centro_id, am.monto, ca.moneda, ca.nombre as centro_nombre FROM activacion_mensual am LEFT JOIN centro_apoyo ca ON ca.id=am.centro_id WHERE am.venta_id IS NULL`);
        if(!rows.length){console.log('🔧 Retroactivo: todas las activaciones ya tienen venta');return;}
        console.log('🔧 Retroactivo: '+rows.length+' activaciones sin nota de venta');
        for(const r of rows){
            const moneda=r.moneda||'MXN';
            const vid=await crearVentaCalificacion(r.lider_id, r.centro_id, r.mes, moneda, r.centro_nombre);
            await _caSql(`UPDATE activacion_mensual SET venta_id='${vid}' WHERE lider_id='${r.lider_id}' AND mes='${r.mes}'`);
        }
        console.log('🔧 Retroactivo completado');
    }catch(e){console.warn('_fixActivacionesSinVenta:',e.message);}
})();

// ── Activar líder (consume 1 activación disponible) ──────────
async function activarLiderCentro(){
    const centroId=window._caDetalleId;
    if(!centroId)return;
    const liderId=(document.getElementById('caActiv-lider-id')||{}).value||'';
    const liderNombre=(document.getElementById('caActiv-lider-search')||{}).value||'';
    const mes=(document.getElementById('caActiv-mes')||{}).value||'';

    if(!liderId)return BO_AUTH.showAlert('Busca y selecciona al lider que deseas activar','warning');
    if(!mes)return BO_AUTH.showAlert('Selecciona el mes de activacion','warning');

    // ── Verificar activaciones disponibles según cajas calificables aprobadas ──
    const c = _caData.find(x => x.id === centroId);
    const moneda = c?.moneda || 'MXN';

    const CAJAS_CALC_EXPR =
        "COALESCE(NULLIF(" +
        "  (SELECT SUM(" +
        "    CASE" +
        "      WHEN COALESCE(pr.categoria,pr2.categoria)='paquete' AND EXISTS(SELECT 1 FROM producto_componente pc2 WHERE pc2.paquete_id=COALESCE(pr.id,pr2.id)) THEN" +
        "        COALESCE((SELECT SUM(CASE WHEN comp_p.comisiona=true THEN COALESCE(comp_p.puntos_equivalencia,1)*pc.cantidad*(pz.value->>'cantidad')::int ELSE 0 END)" +
        "          FROM producto_componente pc JOIN productos comp_p ON comp_p.id=pc.componente_id WHERE pc.paquete_id=COALESCE(pr.id,pr2.id)),0)" +
        "      WHEN COALESCE(pr.comisiona,pr2.comisiona)=true THEN" +
        "        COALESCE(COALESCE(pr.puntos_equivalencia,pr2.puntos_equivalencia),1)*(pz.value->>'cantidad')::int" +
        "      ELSE 0" +
        "    END" +
        "  ) FROM jsonb_array_elements(v.piezas::jsonb) pz(value)" +
        "  LEFT JOIN productos pr ON pr.id=(pz.value->>'producto_id')::int" +
        "  LEFT JOIN productos pr2 ON pr.id IS NULL AND pr2.sku=(pz.value->>'sku') AND pr2.activo=true" +
        "  )/24.0" +
        ", 0), v.cantidad)";

    const cajasRes = await _caSql(`
        SELECT COALESCE(SUM(${CAJAS_CALC_EXPR}), 0) AS total_cajas
        FROM venta v
        WHERE v.centro_apoyo_id=${centroId}
          AND v.approved=1
          AND COALESCE(v.producto_id,'') != 'calificacion-mes'
    `);
    const derecho = Math.floor(Number(cajasRes[0]?.total_cajas || 0));
    const usadas = await _caSql(`SELECT COUNT(*) as n FROM activacion_mensual WHERE centro_id=${centroId}`);
    const totalUsadas = Number(usadas[0]?.n || 0);

    // Validación de límite de crédito
    const limiteCredito = Number(c?.limite_credito || 0);
    const creditoUsado = Number(c?.credito_usado || 0);
    if (limiteCredito > 0 && creditoUsado > limiteCredito) {
        return BO_AUTH.showAlert(`El centro ha excedido su límite de crédito autorizado (${_caMoneyFmt(creditoUsado, moneda)} usado vs ${_caMoneyFmt(limiteCredito, moneda)} límite). Debe registrar pagos antes de activar.`, 'warning');
    }

    if (totalUsadas >= derecho) {
        return BO_AUTH.showAlert(`No hay activaciones disponibles. Cajas calificables: ${derecho}, ${totalUsadas} ya utilizadas.`, 'warning');
    }

    const user=CURRENT_USER?(CURRENT_USER.nombre+' '+CURRENT_USER.apellido).replace(/'/g,"''"):'admin';
    const liderEsc=liderNombre.replace(/'/g,"''");

    try{
        const c=_caData.find(x=>x.id===centroId);
        const moneda=c?.moneda||'MXN';
        const montoA=getMontoActivacion(moneda);

        // 1. Registrar activación
        await _caSql(`INSERT INTO activacion_mensual(lider_id,mes,monto,origen,centro_id,registrado_por) VALUES('${liderId}','${mes}',${montoA},'pago_centro',${centroId},'${user}') ON CONFLICT(lider_id,mes) DO UPDATE SET monto=EXCLUDED.monto,origen=EXCLUDED.origen,centro_id=EXCLUDED.centro_id,registrado_por=EXCLUDED.registrado_por,fecha=NOW()`);

        // 2. Crear nota de venta sintética "Calificación del Mes"
        const ventaId=await crearVentaCalificacion(liderId, centroId, mes, moneda, c?.nombre||'');
        await _caSql(`UPDATE activacion_mensual SET venta_id='${ventaId}' WHERE lider_id='${liderId}' AND mes='${mes}'`);

        BO_AUTH.showAlert('⚡ '+liderNombre+' activado para '+mes+' (nota de calificación creada)','success');

        // Reset form
        document.getElementById('caActiv-lider-search').value='';
        document.getElementById('caActiv-lider-id').value='';
        document.getElementById('caActiv-lider-selected').textContent='';

        loadActivacionesCentro(centroId);
    }catch(e){
        BO_AUTH.showAlert('Error: '+e.message,'danger');
    }
}

// ── Desactivar (quitar activación) ───────────────────────────
async function quitarActivacionCentro(liderId,nombre,mes,centroId){

    try{
        // 1. Obtener venta_id para eliminar nota sintética
        const activ=await _caSql(`SELECT venta_id FROM activacion_mensual WHERE lider_id='${liderId}' AND mes='${mes}' AND centro_id=${centroId}`);
        if(activ[0]?.venta_id){
            await _caSql(`DELETE FROM venta WHERE id='${activ[0].venta_id}'`);
        }
        // 2. Eliminar activación
        await _caSql(`DELETE FROM activacion_mensual WHERE lider_id='${liderId}' AND mes='${mes}' AND centro_id=${centroId}`);
        BO_AUTH.showAlert('Activación de '+nombre+' removida (nota eliminada)','info');
        loadActivacionesCentro(centroId);
    }catch(e){
        BO_AUTH.showAlert('Error: '+e.message,'danger');
    }
}

// ── Cargar tab activaciones ──────────────────────────────────
async function loadActivacionesCentro(centroId){
    if(!centroId)return;
    const c=_caData.find(x=>x.id===centroId);
    const moneda=c?.moneda||'MXN';
    const tb=document.getElementById('caActivBody');

    // Default mes
    const mesInput=document.getElementById('caActivMes');
    const mesActivInput=document.getElementById('caActiv-mes');
    if(!mesInput.value){
        const now=new Date();
        mesInput.value=now.getFullYear()+'-'+String(now.getMonth()+1).padStart(2,'0');
    }
    if(mesActivInput&&!mesActivInput.value)mesActivInput.value=mesInput.value;
    const mesFiltro=mesInput.value;

    try{
        // 1. Derecho a activar = suma de cajas (cantidad) de notas vinculadas al centro
        // Usa NULLIF para que cuando piezas/comisiona da 0, caiga al fallback v.cantidad
        const CAJAS_CALC_EXPR =
            "COALESCE(NULLIF(" +
            "  (SELECT SUM(" +
            "    CASE" +
            "      WHEN COALESCE(pr.categoria,pr2.categoria)='paquete' AND EXISTS(SELECT 1 FROM producto_componente pc2 WHERE pc2.paquete_id=COALESCE(pr.id,pr2.id)) THEN" +
            "        COALESCE((SELECT SUM(CASE WHEN comp_p.comisiona=true THEN COALESCE(comp_p.puntos_equivalencia,1)*pc.cantidad*(pz.value->>'cantidad')::int ELSE 0 END)" +
            "          FROM producto_componente pc JOIN productos comp_p ON comp_p.id=pc.componente_id WHERE pc.paquete_id=COALESCE(pr.id,pr2.id)),0)" +
            "      WHEN COALESCE(pr.comisiona,pr2.comisiona)=true THEN" +
            "        COALESCE(COALESCE(pr.puntos_equivalencia,pr2.puntos_equivalencia),1)*(pz.value->>'cantidad')::int" +
            "      ELSE 0" +
            "    END" +
            "  ) FROM jsonb_array_elements(v.piezas::jsonb) pz(value)" +
            "  LEFT JOIN productos pr ON pr.id=(pz.value->>'producto_id')::int" +
            "  LEFT JOIN productos pr2 ON pr.id IS NULL AND pr2.sku=(pz.value->>'sku') AND pr2.activo=true" +
            "  )/24.0" +
            ", 0), v.cantidad)";

        const cajasRes = await _caSql(`
            SELECT COALESCE(SUM(${CAJAS_CALC_EXPR}), 0) AS total_cajas
            FROM venta v
            WHERE v.centro_apoyo_id=${centroId}
              AND v.approved=1
              AND COALESCE(v.producto_id,'') != 'calificacion-mes'
        `);
        const derecho = Math.floor(Number(cajasRes[0]?.total_cajas || 0));

        // 2. Activaciones ya registradas para este centro (global, todos los meses)
        const activGlobal=await _caSql(`SELECT COUNT(*) as n FROM activacion_mensual WHERE centro_id=${centroId}`);
        const totalUsadas=Number(activGlobal[0]?.n||0);
        // RESTANTES nunca puede ser negativo — si se usaron más que el derecho actual
        // significa que el centro ya consumió todo su cupo histórico
        const restantes = Math.max(0, derecho - totalUsadas);

        // 3. Activaciones del mes seleccionado
        const activMes=await _caSql(`SELECT am.*, c2.registro, c2.nombre as db_nombre, c2.ape_p, c2.ape_m
            FROM activacion_mensual am
            LEFT JOIN cliente c2 ON c2.id=am.lider_id
            WHERE am.centro_id=${centroId} AND am.mes='${mesFiltro}'
            ORDER BY am.fecha DESC`);

        // 4. Líderes vinculados sin activar este mes
        const lidVinculados=await _caSql(`SELECT DISTINCT v.cliente_id as id, c2.registro, c2.nombre, c2.ape_p, c2.ape_m
            FROM venta v LEFT JOIN cliente c2 ON c2.id=v.cliente_id
            WHERE v.centro_apoyo_id=${centroId}
            AND v.cliente_id IS NOT NULL
            AND v.cliente_id NOT IN (SELECT lider_id FROM activacion_mensual WHERE centro_id=${centroId} AND mes='${mesFiltro}' AND lider_id IS NOT NULL)
            ORDER BY c2.nombre`);

        // KPIs — colorear RESTANTES en rojo si es 0
        const setKpi=(id,val)=>{const el=document.getElementById(id);if(el)el.textContent=val;};
        setKpi('caActivKpi-derecho',derecho);
        setKpi('caActivKpi-ok',activMes.length);
        setKpi('caActivKpi-rest',restantes);
        setKpi('caActivKpi-no',lidVinculados.length);
        // Colorear RESTANTES: verde si >0, naranja si 0
        const restEl=document.getElementById('caActivKpi-rest');
        if(restEl) restEl.style.color = restantes > 0 ? '#22c55e' : '#f59e0b';

        // Tabla
        let html='';
        if(activMes.length){
            html+=activMes.map(a=>{
                const nombre=a.db_nombre?`${a.db_nombre} ${a.ape_p||''} ${a.ape_m||''}`.trim():(a.lider_id||'?');
                const reg=a.registro||'?';
                const nomEsc=nombre.replace(/'/g,"\\'");
                return `<tr>
                    <td style="font-weight:600;">${nombre}</td>
                    <td style="font-family:monospace;font-size:.82rem;">${reg}</td>
                    <td>${a.mes||mesFiltro}</td>
                    <td style="font-size:.78rem;">${{'pago_centro':'💵 Pago','compra_directa':'🛒 Compra','manual':'✏️ Manual'}[a.origen]||a.origen||'—'}</td>
                    <td style="font-size:.78rem;">${a.fecha?new Date(a.fecha).toLocaleDateString('es-MX'):'—'}</td>
                    <td style="text-align:center;"><span style="background:rgba(34,197,94,.15);color:#22c55e;padding:.2rem .6rem;border-radius:4px;font-size:.72rem;font-weight:700;">✅ ACTIVADO</span></td>
                    <td style="text-align:center;">
                        ${a.venta_id ? `<button class="btn btn-outline btn-sm" style="font-size:.68rem;padding:.15rem .4rem;color:#3b82f6;border-color:rgba(59,130,246,.3);margin-right:4px;" onclick="_downloadActivacionPdf('${a.venta_id}')">📄 PDF</button>` : ''}
                        <button class="btn btn-outline btn-sm" style="font-size:.68rem;padding:.15rem .4rem;color:#ef4444;border-color:rgba(239,68,68,.3);" onclick="quitarActivacionCentro('${a.lider_id}','${nomEsc}','${a.mes}',${centroId})">✕ Quitar</button>
                    </td>
                </tr>`;
            }).join('');
        }

        // Líderes vinculados sin activar
        if(lidVinculados.length && restantes>0){
            html+=`<tr><td colspan="7" style="font-size:.72rem;color:var(--text-dim);padding:.6rem;text-transform:uppercase;letter-spacing:.05em;background:rgba(239,68,68,.03);">Vinculados sin activar — ${restantes} activacion(es) disponible(s)</td></tr>`;
            html+=lidVinculados.map(l=>{
                const nombre=`${l.nombre||''} ${l.ape_p||''} ${l.ape_m||''}`.trim()||'?';
                const nomEsc=nombre.replace(/'/g,"\\'");
                return `<tr style="opacity:.7;">
                    <td>${nombre}</td>
                    <td style="font-family:monospace;font-size:.82rem;">${l.registro||'?'}</td>
                    <td>${mesFiltro}</td>
                    <td style="font-size:.78rem;color:var(--text-dim);">—</td>
                    <td style="font-size:.78rem;color:var(--text-dim);">—</td>
                    <td style="text-align:center;"><span style="background:rgba(239,68,68,.1);color:#ef4444;padding:.2rem .6rem;border-radius:4px;font-size:.72rem;font-weight:600;">❌ SIN ACTIVAR</span></td>
                    <td style="text-align:center;"><button class="btn btn-outline btn-sm" style="font-size:.7rem;padding:.2rem .5rem;color:#a5b4fc;border-color:rgba(99,102,241,.3);" onclick="activarLiderRapido('${l.id}','${nomEsc}',${centroId},'${mesFiltro}')">⚡ Activar</button></td>
                </tr>`;
            }).join('');
        } else if(!activMes.length){
            html='<tr><td colspan="7" style="text-align:center;color:var(--text-dim);padding:1.5rem;">No hay activaciones para '+mesFiltro+(derecho>0?' — usa el buscador de arriba para activar lideres':'')+'</td></tr>';
        }

        tb.innerHTML=html;

    }catch(e){
        console.error('loadActivacionesCentro',e);
        tb.innerHTML='<tr><td colspan="7" style="color:#ef4444;padding:1rem;">Error: '+e.message+'</td></tr>';
    }
}

// ── Activar rápido desde la tabla (sin buscador) ─────────────
async function activarLiderRapido(liderId,nombre,centroId,mes){
    try{
        const user=CURRENT_USER?(CURRENT_USER.nombre+' '+CURRENT_USER.apellido).replace(/'/g,"''"):'admin';
        const c=_caData.find(x=>x.id===centroId);
        const moneda=c?.moneda||'MXN';
        const montoA=getMontoActivacion(moneda);
        await _caSql(`INSERT INTO activacion_mensual(lider_id,mes,monto,origen,centro_id,registrado_por) VALUES('${liderId}','${mes}',${montoA},'pago_centro',${centroId},'${user}') ON CONFLICT(lider_id,mes) DO UPDATE SET monto=EXCLUDED.monto,origen=EXCLUDED.origen,centro_id=EXCLUDED.centro_id,registrado_por=EXCLUDED.registrado_por,fecha=NOW()`);

        // Crear nota de venta sintética
        const ventaId=await crearVentaCalificacion(liderId, centroId, mes, moneda, c?.nombre||'');
        await _caSql(`UPDATE activacion_mensual SET venta_id='${ventaId}' WHERE lider_id='${liderId}' AND mes='${mes}'`);

        BO_AUTH.showAlert('⚡ '+nombre+' activado para '+mes+' (nota creada)','success');
        loadActivacionesCentro(centroId);
    }catch(e){
        BO_AUTH.showAlert('Error: '+e.message,'danger');
    }
}

// ── Modal de Desglose Financiero de Activaciones (Calificable vs Fletes) ──
async function mostrarDesgloseActivaciones(centroId){
    centroId = centroId || window._caDetalleId;
    if(!centroId) return BO_AUTH.showAlert('Selecciona un Centro de Apoyo primero', 'warning');

    const c = _caData.find(x => x.id === centroId) || {};
    const moneda = c.moneda || 'MXN';
    const precioActivacion = getMontoActivacion(moneda); // $4,500 en MXN

    // Abrir modal o crear contenedor si no existe
    let modal = document.getElementById('modalDesgloseActivaciones');
    if(!modal){
        modal = document.createElement('div');
        modal.id = 'modalDesgloseActivaciones';
        modal.className = 'modal-backdrop';
        modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,.8);display:flex;align-items:center;justify-content:center;z-index:9999;backdrop-filter:blur(5px);';
        modal.innerHTML = `
            <div style="background:#13151f;border:1px solid rgba(99,102,241,.3);border-radius:14px;max-width:850px;width:95%;max-height:90vh;display:flex;flex-direction:column;box-shadow:0 20px 50px rgba(0,0,0,.7);overflow:hidden;animation:fadeIn .2s ease;">
                <div style="padding:1.1rem 1.5rem;border-bottom:1px solid rgba(255,255,255,.08);display:flex;justify-content:space-between;align-items:center;background:rgba(99,102,241,.08);">
                    <div>
                        <div style="font-size:1.05rem;font-weight:800;color:#fff;display:flex;align-items:center;gap:8px;">
                            <span>⚡ Desglose de Activaciones — Cajas Calificables vs Fletes</span>
                        </div>
                        <div id="mdaCentroNombre" style="font-size:.78rem;color:#a5b4fc;margin-top:2px;">Cargando...</div>
                    </div>
                    <button onclick="document.getElementById('modalDesgloseActivaciones').style.display='none'" style="background:none;border:none;color:#94a3b8;font-size:1.4rem;cursor:pointer;line-height:1;padding:0 .4rem;">✕</button>
                </div>
                <div id="mdaContenido" style="padding:1.25rem 1.5rem;overflow-y:auto;flex:1;">
                    <div style="text-align:center;color:var(--text-dim);padding:2rem;">Cargando desglose...</div>
                </div>
                <div style="padding:.75rem 1.5rem;border-top:1px solid rgba(255,255,255,.08);background:rgba(0,0,0,.25);display:flex;justify-content:flex-end;">
                    <button class="btn btn-secondary btn-sm" onclick="document.getElementById('modalDesgloseActivaciones').style.display='none'">Cerrar</button>
                </div>
            </div>
        `;
        document.body.appendChild(modal);
    }

    modal.style.display = 'flex';
    const nomEl = document.getElementById('mdaCentroNombre');
    if(nomEl) nomEl.textContent = `${c.nombre || 'Centro #' + centroId} (${moneda})`;
    const cont = document.getElementById('mdaContenido');
    cont.innerHTML = `<div style="text-align:center;color:#a5b4fc;padding:2.5rem;"><span class="spinner-border spinner-border-sm" style="margin-right:8px;"></span>Calculando cajas calificables, fletes y abonos...</div>`;

    try {
        const CAJAS_CALC_EXPR =
            "COALESCE(NULLIF(" +
            "  (SELECT SUM(" +
            "    CASE" +
            "      WHEN COALESCE(pr.categoria,pr2.categoria)='paquete' AND EXISTS(SELECT 1 FROM producto_componente pc2 WHERE pc2.paquete_id=COALESCE(pr.id,pr2.id)) THEN" +
            "        COALESCE((SELECT SUM(CASE WHEN comp_p.comisiona=true THEN COALESCE(comp_p.puntos_equivalencia,1)*pc.cantidad*(pz.value->>'cantidad')::int ELSE 0 END)" +
            "          FROM producto_componente pc JOIN productos comp_p ON comp_p.id=pc.componente_id WHERE pc.paquete_id=COALESCE(pr.id,pr2.id)),0)" +
            "      WHEN COALESCE(pr.comisiona,pr2.comisiona)=true THEN" +
            "        COALESCE(COALESCE(pr.puntos_equivalencia,pr2.puntos_equivalencia),1)*(pz.value->>'cantidad')::int" +
            "      ELSE 0" +
            "    END" +
            "  ) FROM jsonb_array_elements(v.piezas::jsonb) pz(value)" +
            "  LEFT JOIN productos pr ON pr.id=(pz.value->>'producto_id')::int" +
            "  LEFT JOIN productos pr2 ON pr.id IS NULL AND pr2.sku=(pz.value->>'sku') AND pr2.activo=true" +
            "  )/24.0" +
            ", 0), v.cantidad)";

        const [notas, abonosRes, activRes, centroDb] = await Promise.all([
            _caSql(`
                SELECT v.id, v.fecha_venta, v.total, v.comentario, ${CAJAS_CALC_EXPR} AS cajas_calificables
                FROM venta v
                WHERE v.centro_apoyo_id=${centroId}
                  AND v.approved=1
                  AND COALESCE(v.producto_id,'') != 'calificacion-mes'
                ORDER BY v.fecha_venta ASC
            `),
            _caSql(`SELECT COALESCE(SUM(monto),0) as total FROM centro_apoyo_movimiento WHERE centro_id=${centroId} AND tipo='abono'`),
            _caSql(`SELECT COUNT(*) as n FROM activacion_mensual WHERE centro_id=${centroId}`),
            _caSql(`SELECT credito_usado, limite_credito FROM centro_apoyo WHERE id=${centroId}`)
        ]);

        const totalAbonos = Number(abonosRes[0]?.total || 0);
        const totalUsadas = Number(activRes[0]?.n || 0);
        const creditoUsado = Number(centroDb[0]?.credito_usado ?? c.credito_usado ?? 0);
        const limiteCredito = Number(centroDb[0]?.limite_credito ?? c.limite_credito ?? 0);

        let totalCajas = 0;
        let totalMontoCalificable = 0;
        let totalFletes = 0;
        let totalNotas = 0;

        const tableRows = (notas || []).map(n => {
            const cajas = parseFloat(n.cajas_calificables) || 0;
            const montoTotal = parseFloat(n.total) || 0;
            const montoCalificable = cajas * precioActivacion;
            const flete = Math.max(0, montoTotal - montoCalificable);
            totalCajas += cajas;
            totalMontoCalificable += montoCalificable;
            totalFletes += flete;
            totalNotas += montoTotal;

            const shortId = (n.id || '').substring(0, 8);
            const fechaStr = n.fecha_venta ? String(n.fecha_venta).substring(0, 10) : '—';
            return `<tr>
                <td style="font-family:monospace;font-size:.78rem;color:#cbd5e1;padding:.4rem .6rem;">${shortId}</td>
                <td style="font-size:.78rem;padding:.4rem .6rem;">${fechaStr}</td>
                <td style="text-align:center;font-weight:700;color:#22c55e;padding:.4rem .6rem;">${cajas} ${cajas === 1 ? 'caja' : 'cajas'}</td>
                <td style="text-align:right;font-size:.78rem;padding:.4rem .6rem;">${_caMoneyFmt(montoCalificable, moneda)}</td>
                <td style="text-align:right;font-size:.78rem;color:#f59e0b;padding:.4rem .6rem;">${_caMoneyFmt(flete, moneda)}</td>
                <td style="text-align:right;font-weight:700;font-size:.8rem;padding:.4rem .6rem;">${_caMoneyFmt(montoTotal, moneda)}</td>
            </tr>`;
        }).join('');

        const derechoTotal = Math.floor(totalCajas);
        const restantes = Math.max(0, derechoTotal - totalUsadas);

        cont.innerHTML = `
            <!-- Explicación de la regla de negocio -->
            <div style="background:rgba(99,102,241,.1);border:1px solid rgba(99,102,241,.25);border-radius:8px;padding:.85rem 1.1rem;margin-bottom:1.25rem;font-size:.8rem;line-height:1.5;color:#e0e7ff;">
                <strong>💡 Separación Calificable vs No Calificable:</strong> Cada caja de producto con puntos comisionables otorga <strong>1 activación</strong> (${_caMoneyFmt(precioActivacion, moneda)}).
                Los fletes/envíos, folletos, trípticos, revistas, shakers y material promocional se cobran en el total de la nota, pero <strong>NO generan activaciones</strong>.
            </div>

            <!-- KPIs Principales -->
            <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:.75rem;margin-bottom:1.25rem;">
                <div style="background:rgba(34,197,94,.08);border:1px solid rgba(34,197,94,.25);border-radius:8px;padding:.75rem;text-align:center;">
                    <div style="font-size:.65rem;color:#22c55e;text-transform:uppercase;font-weight:700;">Cajas Calificables</div>
                    <div style="font-size:1.4rem;font-weight:800;color:#22c55e;">${derechoTotal}</div>
                    <div style="font-size:.72rem;color:var(--text-dim);">${_caMoneyFmt(totalMontoCalificable, moneda)}</div>
                </div>
                <div style="background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.25);border-radius:8px;padding:.75rem;text-align:center;">
                    <div style="font-size:.65rem;color:#f59e0b;text-transform:uppercase;font-weight:700;">Fletes / Folletos / Promos</div>
                    <div style="font-size:1.4rem;font-weight:800;color:#f59e0b;">${_caMoneyFmt(totalFletes, moneda)}</div>
                    <div style="font-size:.72rem;color:var(--text-dim);">(Se paga, no activa)</div>
                </div>
                <div style="background:rgba(59,130,246,.08);border:1px solid rgba(59,130,246,.25);border-radius:8px;padding:.75rem;text-align:center;">
                    <div style="font-size:.65rem;color:#60a5fa;text-transform:uppercase;font-weight:700;">Total Pagado (Abonos)</div>
                    <div style="font-size:1.4rem;font-weight:800;color:#60a5fa;">${_caMoneyFmt(totalAbonos, moneda)}</div>
                    <div style="font-size:.72rem;color:var(--text-dim);">Total notas: ${_caMoneyFmt(totalNotas, moneda)}</div>
                </div>
                <div style="background:rgba(168,85,247,.08);border:1px solid rgba(168,85,247,.25);border-radius:8px;padding:.75rem;text-align:center;">
                    <div style="font-size:.65rem;color:#c084fc;text-transform:uppercase;font-weight:700;">Activaciones Libres</div>
                    <div style="font-size:1.4rem;font-weight:800;color:#c084fc;">${restantes}</div>
                    <div style="font-size:.72rem;color:var(--text-dim);">${totalUsadas} ya utilizadas</div>
                </div>
            </div>

            <!-- Estado de Crédito -->
            <div style="background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);border-radius:8px;padding:.75rem 1rem;margin-bottom:1.25rem;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:.5rem;font-size:.82rem;">
                <div>
                    <span style="color:var(--text-dim);">Límite de Crédito:</span> <strong>${_caMoneyFmt(limiteCredito, moneda)}</strong>
                </div>
                <div>
                    <span style="color:var(--text-dim);">Crédito Usado (Balance):</span> <strong style="color:${creditoUsado <= 0 ? '#22c55e' : '#f59e0b'};">${creditoUsado <= 0 ? 'Sin Deuda (' + _caMoneyFmt(creditoUsado, moneda) + ')' : _caMoneyFmt(creditoUsado, moneda)}</strong>
                </div>
                <div>
                    <span style="color:var(--text-dim);">Crédito Disponible:</span> <strong style="color:#22c55e;">${_caMoneyFmt(Math.max(0, limiteCredito - creditoUsado), moneda)}</strong>
                </div>
            </div>

            <!-- Tabla de Notas Desglosadas -->
            <div style="font-size:.82rem;font-weight:700;color:#cbd5e1;margin-bottom:.5rem;">📋 Notas Aprobadas y Composición de Importe:</div>
            <div style="max-height:260px;overflow-y:auto;border:1px solid rgba(255,255,255,.08);border-radius:6px;">
                <table class="table" style="width:100%;margin:0;font-size:.78rem;">
                    <thead>
                        <tr style="background:rgba(255,255,255,.05);color:var(--text-dim);">
                            <th style="padding:.5rem .6rem;">Nota</th>
                            <th style="padding:.5rem .6rem;">Fecha</th>
                            <th style="text-align:center;padding:.5rem .6rem;">Cajas Calificables</th>
                            <th style="text-align:right;padding:.5rem .6rem;">Subtotal Producto</th>
                            <th style="text-align:right;padding:.5rem .6rem;">Fletes / Promos</th>
                            <th style="text-align:right;padding:.5rem .6rem;">Total Nota</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                    <tfoot style="background:rgba(255,255,255,.05);font-weight:700;">
                        <tr>
                            <td colspan="2" style="padding:.5rem .6rem;">TOTAL HISTÓRICO</td>
                            <td style="text-align:center;color:#22c55e;padding:.5rem .6rem;">${totalCajas} cajas</td>
                            <td style="text-align:right;padding:.5rem .6rem;">${_caMoneyFmt(totalMontoCalificable, moneda)}</td>
                            <td style="text-align:right;color:#f59e0b;padding:.5rem .6rem;">${_caMoneyFmt(totalFletes, moneda)}</td>
                            <td style="text-align:right;color:#fff;padding:.5rem .6rem;">${_caMoneyFmt(totalNotas, moneda)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        `;
    } catch (e) {
        console.error('[mostrarDesgloseActivaciones]', e);
        cont.innerHTML = `<div style="color:#ef4444;padding:1.5rem;text-align:center;">Error al cargar desglose: ${e.message}</div>`;
    }
}
window.mostrarDesgloseActivaciones = mostrarDesgloseActivaciones;

// ══════════════════════════════════════════════════════════════════
// ══ MODAL: Preparar Factura Centro (Completo) ════════════════════
// ══════════════════════════════════════════════════════════════════

// State for the full invoice modal
window._caFactFullData = { notas: [], activos: [], pagos: [], productos: [], componentes: [] };

function abrirModalFacturaCentro(centroId){
    const c = _caData.find(x => x.id === centroId);
    if(!c) return BO_AUTH.showAlert('Centro no encontrado', 'danger');
    
    // Set title
    const titulo = document.getElementById('caFactFull-titulo');
    if(titulo) titulo.textContent = c.nombre || 'Centro #' + centroId;
    
    // Sync month from tab picker
    const tabMes = document.getElementById('caFactMes');
    const modalMes = document.getElementById('caFactFull-mes');
    if(modalMes){
        if(tabMes && tabMes.value) modalMes.value = tabMes.value;
        else {
            const now = new Date();
            modalMes.value = now.getFullYear() + '-' + String(now.getMonth()+1).padStart(2,'0');
        }
    }
    
    // Pre-fill fiscal data from centro
    const setVal = (id, val) => { const el = document.getElementById(id); if(el) el.value = val; };
    setVal('caFactFull-rfc', c.rfc || 'XAXX010101000');
    setVal('caFactFull-razon', c.razon_social || 'PUBLICO EN GENERAL');
    setVal('caFactFull-cp', c.codigo_postal || '44100');
    setVal('caFactFull-regimen', c.regimen_fiscal || '616');
    setVal('caFactFull-usoCfdi', c.uso_cfdi || 'S01');
    setVal('caFactFull-formaPago', '99');
    
    // Reset publico general checkbox
    const pgCheck = document.getElementById('caFactFull-publicoGral');
    if(pgCheck) pgCheck.checked = false;
    
    // Show modal
    document.getElementById('modalFacturaCentroCompleto').style.display = 'flex';
    
    // Auto-load data
    cargarDatosFacturaCentro(centroId, modalMes?.value || '');
}

async function cargarDatosFacturaCentro(centroId, mes){
    if(!mes) return BO_AUTH.showAlert('Selecciona un periodo', 'warning');
    
    const notasBody = document.getElementById('caFactFull-notasBody');
    const activosBody = document.getElementById('caFactFull-activosBody');
    const pagosBody = document.getElementById('caFactFull-pagosBody');
    const statusBadge = document.getElementById('caFactFull-statusBadge');
    
    if(statusBadge) statusBadge.innerHTML = '<span style="color:#a5b4fc;">⏳ Cargando datos...</span>';
    notasBody.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:.75rem;color:var(--text-dim);">Cargando…</td></tr>';
    activosBody.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:.75rem;color:var(--text-dim);">Cargando…</td></tr>';
    pagosBody.innerHTML = '<tr><td colspan="4" style="text-align:center;padding:.75rem;color:var(--text-dim);">Cargando…</td></tr>';
    
    try {
        // Parallel queries
        const [notas, activos, pagos, prevSol, productos, componentes] = await Promise.all([
            // 1. Notas no facturadas del periodo (ESTRICTO: sin emitidas, sin uuid, sin solicitudes activas)
            _caSql(`SELECT v.id, v.fecha_venta, v.total, v.monto_facturable, v.piezas, v.comentario,
                COALESCE(c.nombre || ' ' || COALESCE(c.ape_p,''), 'Público General') AS cliente_nombre
                FROM venta v LEFT JOIN cliente c ON c.id = v.cliente_id
                WHERE v.centro_apoyo_id=${centroId} AND v.approved=1
                AND COALESCE(v.factura_emitida,false)=false
                AND (v.factura_uuid IS NULL OR v.factura_uuid='')
                AND COALESCE(v.requiere_factura,false)=false
                AND COALESCE(v.producto_id,'') != 'calificacion-mes'
                AND COALESCE(v.comentario,'') NOT ILIKE '%calificacion del mes%'
                AND v.id::text NOT IN (SELECT jsonb_array_elements_text(ventas_ids::jsonb) FROM facturas_solicitadas WHERE estado IN ('solicitada','en_proceso','facturada') AND ventas_ids IS NOT NULL AND ventas_ids <> '[]' AND ventas_ids <> 'null')
                AND v.id::text NOT IN (SELECT numero_nota::text FROM facturas_solicitadas WHERE estado IN ('solicitada','en_proceso','facturada') AND numero_nota IS NOT NULL)
                AND TO_CHAR(v.fecha_venta,'YYYY-MM')='${mes}'
                ORDER BY v.fecha_venta DESC`),
            // 2. Activos cobrados del mes
            _caSql(`SELECT id, monto, descripcion, registrado_por, creado_en
                FROM centro_apoyo_pago
                WHERE centro_id=${centroId} AND (tipo_pago='Cobro Activos' OR LOWER(tipo_pago) LIKE '%activo%')
                AND TO_CHAR(creado_en,'YYYY-MM')='${mes}'
                ORDER BY creado_en DESC`),
            // 3. Pagos/depósitos (no activos)
            _caSql(`SELECT id, monto, tipo_pago, referencia, descripcion, registrado_por, creado_en
                FROM centro_apoyo_pago
                WHERE centro_id=${centroId} AND COALESCE(tipo_pago,'') != 'Cobro Activos' AND LOWER(COALESCE(tipo_pago,'')) NOT LIKE '%activo%'
                AND TO_CHAR(creado_en,'YYYY-MM')='${mes}'
                ORDER BY creado_en DESC`),
            // 4. Activos ya aplicados previamente en solicitudes del mismo centro y mes
            _caSql(`SELECT id, numero_nota, comentario, estado
                FROM facturas_solicitadas
                WHERE (numero_nota LIKE 'CENTRO-${centroId}-%' OR numero_nota LIKE 'GLOBAL-%-CA_${centroId}')
                AND estado IN ('solicitada','en_proceso','facturada')
                AND (numero_nota LIKE '%${mes}%' OR comentario LIKE '%${mes}%')`),
            // 5. Catálogo de productos
            _caSql(`SELECT id, sku, nombre, precio, tasa_iva, categoria FROM productos`),
            // 6. Componentes de paquetes
            _caSql(`SELECT paquete_id, componente_id, cantidad FROM producto_componente`)
        ]);
        
        // Calcular activos ya consumidos previamente si existieran
        let activosYaConsumidos = 0;
        (prevSol || []).forEach(s => {
            const m = String(s.comentario||'').match(/act=([0-9.]+)/);
            if(m) activosYaConsumidos += parseFloat(m[1]) || 0;
        });
        
        const totalActivosBrutos = (activos || []).reduce((s,a) => s + Number(a.monto||0), 0);
        const totalActivosDisponibles = Math.max(0, totalActivosBrutos - activosYaConsumidos);
        
        // Guardar estado global
        window._caFactFullData = {
            notas,
            activos,
            pagos,
            productos,
            componentes,
            totalActivosBrutos,
            activosYaConsumidos,
            totalActivosDisponibles
        };
        
        // Render notas con checkboxes
        const nBadge = document.getElementById('caFactFull-notasCountBadge');
        if(nBadge) nBadge.textContent = String(notas.length);
        const nSelectAll = document.getElementById('caFactFull-selectAll');
        if(nSelectAll) nSelectAll.checked = true;
        
        if(!notas.length){
            notasBody.innerHTML = '<tr><td colspan="5" style="text-align:center;color:var(--success);padding:.75rem;">✅ Sin notas pendientes para este periodo</td></tr>';
        } else {
            notasBody.innerHTML = notas.map(r => `<tr>
                <td style="text-align:center;"><input type="checkbox" class="caFactFull-check" value="${r.id}" checked onchange="calcularPreviewCfdiCentro()"></td>
                <td style="font-family:monospace;font-size:.75rem;">${(r.id||'').toString().substring(0,12)}</td>
                <td style="font-size:.78rem;">${r.fecha_venta ? new Date(r.fecha_venta).toLocaleDateString('es-MX', { timeZone: 'UTC' }) : '—'}</td>
                <td style="font-size:.78rem;">${r.cliente_nombre || '—'}</td>
                <td style="text-align:right;font-size:.78rem;font-weight:600;">${_caMoneyFmt(r.total,'MXN')}</td>
            </tr>`).join('');
        }
        
        // Render activos cobrados con checkboxes individuales
        const aBadge = document.getElementById('caFactFull-activosCountBadge');
        if(aBadge) aBadge.textContent = String(activos.length);
        const aSelectAll = document.getElementById('caFactFull-selectAllActivos');
        if(aSelectAll) aSelectAll.checked = true;
        
        if(!activos.length){
            activosBody.innerHTML = '<tr><td colspan="5" style="text-align:center;color:var(--text-dim);padding:.75rem;font-size:.78rem;">Sin cobros de activos registrados en este periodo</td></tr>';
        } else {
            activosBody.innerHTML = activos.map(a => `<tr>
                <td style="text-align:center;"><input type="checkbox" class="caFactFull-check-activo" value="${a.id}" data-monto="${Number(a.monto||0)}" checked onchange="caFactFullOnActivoRowChange()"></td>
                <td style="font-size:.78rem;">${a.creado_en ? new Date(a.creado_en).toLocaleDateString('es-MX', { timeZone: 'UTC' }) : '—'}</td>
                <td style="font-size:.78rem;font-weight:600;color:#22c55e;">${_caMoneyFmt(a.monto,'MXN')}</td>
                <td style="font-size:.75rem;color:var(--text-dim);">${(a.descripcion||'').substring(0,35)}</td>
                <td style="font-size:.75rem;color:var(--text-dim);">${(a.registrado_por||'—').substring(0,18)}</td>
            </tr>`).join('') + `<tr style="border-top:2px solid var(--border);font-weight:700;">
                <td colspan="2" style="font-size:.78rem;">TOTAL COBROS</td>
                <td style="font-size:.78rem;color:#22c55e;">${_caMoneyFmt(totalActivosBrutos,'MXN')}</td>
                <td colspan="2" style="font-size:.72rem;color:var(--text-dim);">${activosYaConsumidos > 0 ? `(−${_caMoneyFmt(activosYaConsumidos,'MXN')} usados previamente)` : ''}</td>
            </tr>`;
        }
        
        // Inicializar input de monto manual con el total disponible
        const montoInput = document.getElementById('caFactFull-montoActivosInput');
        if(montoInput) montoInput.value = totalActivosDisponibles.toFixed(2);
        
        // Render pagos con checkboxes
        const pBadge = document.getElementById('caFactFull-pagosCountBadge');
        if(pBadge) pBadge.textContent = String(pagos.length);
        const pSelectAll = document.getElementById('caFactFull-selectAllPagos');
        if(pSelectAll) pSelectAll.checked = true;

        if(!pagos.length){
            pagosBody.innerHTML = '<tr><td colspan="5" style="text-align:center;color:var(--text-dim);padding:.75rem;font-size:.78rem;">Sin depósitos/pagos en este periodo</td></tr>';
        } else {
            pagosBody.innerHTML = pagos.map(p => `<tr>
                <td style="text-align:center;"><input type="checkbox" class="caFactFull-check-pago" value="${p.id}" data-monto="${Number(p.monto||0)}" checked onchange="caFactFullOnPagoRowChange()"></td>
                <td style="font-size:.78rem;">${p.creado_en ? new Date(p.creado_en).toLocaleDateString('es-MX', { timeZone: 'UTC' }) : '—'}</td>
                <td style="font-size:.75rem;">${p.tipo_pago || '—'}</td>
                <td style="font-size:.78rem;font-weight:600;color:#f59e0b;">${_caMoneyFmt(p.monto,'MXN')}</td>
                <td style="font-size:.75rem;color:var(--text-dim);">${(p.referencia||p.descripcion||'—').substring(0,30)}</td>
            </tr>`).join('');
        }
        
        if(statusBadge) statusBadge.innerHTML = `<span style="color:#22c55e;">✅ ${notas.length} notas cargadas</span>`;
        
        // Calcular y renderizar Pre-Factura y KPIs
        calcularPreviewCfdiCentro();
        
    } catch(e){
        console.error('cargarDatosFacturaCentro', e);
        if(statusBadge) statusBadge.innerHTML = `<span style="color:#ef4444;">Error de carga</span>`;
        notasBody.innerHTML = `<tr><td colspan="5" style="color:#ef4444;padding:.75rem;">Error: ${e.message}</td></tr>`;
    }
}

// ── Controladores reactivos de selección ──
function caFactFullToggleAllNotas(checked){
    document.querySelectorAll('.caFactFull-check').forEach(cb => { cb.checked = checked; });
    calcularPreviewCfdiCentro();
}

function caFactFullToggleAllActivos(checked){
    const data = window._caFactFullData;
    const maxDisponibles = data ? data.totalActivosDisponibles : 0;
    document.querySelectorAll('.caFactFull-check-activo').forEach(cb => { cb.checked = checked; });
    const montoInput = document.getElementById('caFactFull-montoActivosInput');
    if(montoInput) montoInput.value = checked ? maxDisponibles.toFixed(2) : '0.00';
    calcularPreviewCfdiCentro();
}

function caFactFullToggleAllPagos(checked){
    document.querySelectorAll('.caFactFull-check-pago').forEach(cb => { cb.checked = checked; });
    calcularPreviewCfdiCentro();
}

function caFactFullOnActivoRowChange(){
    const checks = document.querySelectorAll('.caFactFull-check-activo:checked');
    const allChecks = document.querySelectorAll('.caFactFull-check-activo');
    let sumChecked = 0;
    checks.forEach(cb => { sumChecked += parseFloat(cb.dataset.monto || 0) || 0; });
    
    const selectAll = document.getElementById('caFactFull-selectAllActivos');
    if(selectAll) selectAll.checked = (checks.length === allChecks.length && allChecks.length > 0);
    
    const montoInput = document.getElementById('caFactFull-montoActivosInput');
    if(montoInput) montoInput.value = sumChecked.toFixed(2);
    
    calcularPreviewCfdiCentro();
}

function caFactFullOnPagoRowChange(){
    const checks = document.querySelectorAll('.caFactFull-check-pago:checked');
    const allChecks = document.querySelectorAll('.caFactFull-check-pago');
    const selectAll = document.getElementById('caFactFull-selectAllPagos');
    if(selectAll) selectAll.checked = (checks.length === allChecks.length && allChecks.length > 0);
    
    calcularPreviewCfdiCentro();
}

function caFactFullOnMontoManualChange(){
    calcularPreviewCfdiCentro();
}

function caFactFullUsarTodoActivos(){
    const data = window._caFactFullData;
    const maxDisponibles = data ? data.totalActivosDisponibles : 0;
    document.querySelectorAll('.caFactFull-check-activo').forEach(cb => { cb.checked = true; });
    const selectAll = document.getElementById('caFactFull-selectAllActivos');
    if(selectAll) selectAll.checked = true;
    const montoInput = document.getElementById('caFactFull-montoActivosInput');
    if(montoInput) montoInput.value = maxDisponibles.toFixed(2);
    calcularPreviewCfdiCentro();
}

function caFactFullUsarCeroActivos(){
    document.querySelectorAll('.caFactFull-check-activo').forEach(cb => { cb.checked = false; });
    const selectAll = document.getElementById('caFactFull-selectAllActivos');
    if(selectAll) selectAll.checked = false;
    const montoInput = document.getElementById('caFactFull-montoActivosInput');
    if(montoInput) montoInput.value = '0.00';
    calcularPreviewCfdiCentro();
}

// ── Motor de Cálculo y Pre-Factura en Tiempo Real ──
function calcularPreviewCfdiCentro(){
    if(!window._caFactFullData) return;
    const { notas, activos, pagos, productos, componentes, totalActivosDisponibles } = window._caFactFullData;
    
    // 1. Obtener notas seleccionadas
    const noteChecks = document.querySelectorAll('.caFactFull-check:checked');
    const selectedIds = new Set([...noteChecks].map(cb => cb.value));
    const selectedNotas = notas.filter(n => selectedIds.has(String(n.id)));
    
    // 2. Obtener activos seleccionados / manuales
    const montoInput = document.getElementById('caFactFull-montoActivosInput');
    let montoActivosAplicar = montoInput ? parseFloat(montoInput.value) : 0;
    if(isNaN(montoActivosAplicar) || montoActivosAplicar < 0) montoActivosAplicar = 0;
    
    // Limitar al disponible real
    if(montoActivosAplicar > totalActivosDisponibles){
        montoActivosAplicar = totalActivosDisponibles;
        if(montoInput) montoInput.value = totalActivosDisponibles.toFixed(2);
    }
    
    // 3. Totales financieros
    const totalNotas = selectedNotas.reduce((s,n) => s + Number(n.total||0), 0);
    
    // Sumar solo pagos seleccionados
    const pagoChecks = document.querySelectorAll('.caFactFull-check-pago:checked');
    let totalPagos = 0;
    pagoChecks.forEach(cb => { totalPagos += parseFloat(cb.dataset.monto || 0) || 0; });

    const saldoCredito = totalNotas - montoActivosAplicar - totalPagos;
    
    // 4. Actualizar KPIs reactivos
    const $ = id => document.getElementById(id);
    const fmt = v => _caMoneyFmt(v,'MXN');
    
    if($('caFactFull-kpiNotas')) $('caFactFull-kpiNotas').textContent = fmt(totalNotas);
    if($('caFactFull-kpiNotasSub')) $('caFactFull-kpiNotasSub').textContent = `${selectedNotas.length} de ${notas.length} notas`;
    
    if($('caFactFull-kpiActivos')) $('caFactFull-kpiActivos').textContent = fmt(montoActivosAplicar);
    if($('caFactFull-kpiActivosSub')) $('caFactFull-kpiActivosSub').textContent = `de ${fmt(totalActivosDisponibles)} disp.`;
    
    if($('caFactFull-kpiPagos')) $('caFactFull-kpiPagos').textContent = fmt(totalPagos);
    if($('caFactFull-kpiPagosSub')) $('caFactFull-kpiPagosSub').textContent = `${pagoChecks.length} seleccionado(s)`;
    
    if($('caFactFull-kpiSaldo')){
        $('caFactFull-kpiSaldo').textContent = fmt(Math.abs(saldoCredito));
        $('caFactFull-kpiSaldo').style.color = saldoCredito <= 0 ? '#22c55e' : '#ef4444';
    }
    if($('caFactFull-kpiSaldoLabel')){
        $('caFactFull-kpiSaldoLabel').textContent = saldoCredito <= 0 ? 'SALDO CUBIERTO' : 'SALDO CRÉDITO';
        $('caFactFull-kpiSaldoLabel').style.color = saldoCredito <= 0 ? '#22c55e' : '#ef4444';
    }
    if($('caFactFull-kpiSaldoSub')){
        $('caFactFull-kpiSaldoSub').textContent = saldoCredito <= 0 ? '✅ Cubierto con cobros' : '⚠️ Pendiente por cubrir';
    }
    if($('caFactFull-subsidioBadge')){
        $('caFactFull-subsidioBadge').innerHTML = `💰 Activos a aplicar: <strong>${fmt(montoActivosAplicar)}</strong>`;
    }
    
    // 5. Consolidar productos de las notas seleccionadas
    const compMap = {};
    (componentes || []).forEach(c => {
        if(!compMap[c.paquete_id]) compMap[c.paquete_id] = [];
        compMap[c.paquete_id].push(c);
    });

    const consolidated = {};
    const ACTIVOS_PRICE = 0.01;
    
    for(const nota of selectedNotas){
        let pzas = nota.piezas;
        if(typeof pzas === 'string'){
            try { pzas = JSON.parse(pzas); } catch(_) { pzas = null; }
        }

        let foundAny = false;

        if(Array.isArray(pzas) && pzas.length > 0){
            for(const pz of pzas){
                const prodId = String(pz.producto_id || pz.sku || pz.id || '');
                if(!prodId) continue;
                const qty = parseInt(pz.cantidad) || 1;
                if(qty <= 0) continue;
                foundAny = true;

                const catProd = (productos || []).find(c => String(c.id) === prodId || String(c.sku) === prodId);
                if(catProd && catProd.categoria === 'paquete' && compMap[catProd.id]){
                    compMap[catProd.id].forEach(cmp => {
                        const subId = String(cmp.componente_id);
                        const subProd = (productos || []).find(c => String(c.id) === subId) || { id: subId, nombre: cmp.comp_nombre || 'Componente', precio: parseFloat(cmp.comp_precio) || 500, tasa_iva: 0.16 };
                        const subQty = (parseInt(cmp.cantidad) || 1) * qty;
                        if(!consolidated[subId]) consolidated[subId] = { prod: subProd, qty: 0 };
                        consolidated[subId].qty += subQty;
                    });
                } else {
                    const k = catProd ? String(catProd.id) : prodId;
                    const prodObj = catProd || { id: k, nombre: pz.nombre || pz.producto || 'Producto', precio: parseFloat(pz.precio_unitario) || 500, tasa_iva: 0.16 };
                    if(!consolidated[k]) consolidated[k] = { prod: prodObj, qty: 0 };
                    consolidated[k].qty += qty;
                }
            }
        } else if(pzas && typeof pzas === 'object' && Object.keys(pzas).length > 0){
            for(const [skuOrId, cant] of Object.entries(pzas)){
                const qty = Number(cant) || 0;
                if(qty <= 0) continue;
                foundAny = true;
                const catProd = (productos || []).find(c => String(c.id) === String(skuOrId) || String(c.sku) === String(skuOrId));
                if(catProd && catProd.categoria === 'paquete' && compMap[catProd.id]){
                    compMap[catProd.id].forEach(cmp => {
                        const subId = String(cmp.componente_id);
                        const subProd = (productos || []).find(c => String(c.id) === subId) || { id: subId, nombre: cmp.comp_nombre || 'Componente', precio: parseFloat(cmp.comp_precio) || 500, tasa_iva: 0.16 };
                        const subQty = (parseInt(cmp.cantidad) || 1) * qty;
                        if(!consolidated[subId]) consolidated[subId] = { prod: subProd, qty: 0 };
                        consolidated[subId].qty += subQty;
                    });
                } else {
                    const k = catProd ? String(catProd.id) : String(skuOrId);
                    const prodObj = catProd || { id: k, nombre: String(skuOrId), precio: 500, tasa_iva: 0.16 };
                    if(!consolidated[k]) consolidated[k] = { prod: prodObj, qty: 0 };
                    consolidated[k].qty += qty;
                }
            }
        }

        // Fallback si la nota no tiene JSON de piezas
        if(!foundAny && Number(nota.total || 0) > 0){
            const defPrice = 500;
            const cantEst = Math.max(1, Math.round(Number(nota.total) / defPrice));
            const defProd = (productos || []).find(p => String(p.id) === '1' || p.sku === 'DUL-01') || 
                            { id: '1', nombre: 'Suero de Leche Dulet', precio: defPrice, tasa_iva: 0.00 };
            const k = String(defProd.id);
            if(!consolidated[k]) consolidated[k] = { prod: defProd, qty: 0 };
            consolidated[k].qty += cantEst;
        }
    }
    
    // 6. Aplicar subsidio por activos con optimización fiscal (Bolsa IVA 16% primero)
    let activosRestantes = montoActivosAplicar;
    let items16 = [], items0 = [];
    
    for(const [prodId, data] of Object.entries(consolidated)){
        const prod = data.prod || {};
        const nombre = prod.nombre || prod.sku || prodId;
        const tasa = parseFloat(prod.tasa_iva || 0);
        const precioUnitario = parseFloat(prod.precio || 500);
        const item = { prodId, nombre, qty: data.qty, precio: precioUnitario, tasa, subsidized: 0 };
        if(tasa > 0) items16.push(item);
        else items0.push(item);
    }
    
    function processBucket(bucket){
        for(const item of bucket){
            if(item.qty <= 0 || activosRestantes <= 0) continue;
            const p = item.precio && !isNaN(item.precio) ? item.precio : 0;
            const subsidizable = p > 0 ? Math.floor(activosRestantes / p) : 0;
            const toSubsidize = Math.min(item.qty, subsidizable);
            if(toSubsidize > 0){
                item.subsidized = toSubsidize;
                activosRestantes -= toSubsidize * p;
            }
        }
    }
    processBucket(items16);
    processBucket(items0);
    
    // 7. Renderizar Vista Pre-Factura
    const allItems = [...items16, ...items0];
    const preview = document.getElementById('caFactFull-cfdiPreview');
    if(!preview) return;

    if(!selectedNotas.length || !allItems.length){
        preview.innerHTML = '<div style="text-align:center;color:var(--text-dim);padding:1rem;">⚠️ Selecciona al menos una nota para ver la simulación CFDI.</div>';
        return;
    }
    
    let html = '<table style="width:100%;font-size:.75rem;border-collapse:collapse;">';
    html += '<tr style="border-bottom:1px solid rgba(255,255,255,.1);font-weight:700;color:var(--text-secondary);"><td style="padding:.3rem .4rem;">Concepto / Producto</td><td style="text-align:center;padding:.3rem .4rem;">Cant</td><td style="text-align:right;padding:.3rem .4rem;">P.Unit</td><td style="text-align:center;padding:.3rem .4rem;">IVA</td><td style="text-align:right;padding:.3rem .4rem;">Subtotal</td></tr>';
    
    let totalSubtotal = 0;
    let totalIva = 0;
    let totalCfdi = 0;
    let piezasSubsid = 0;
    let piezasRegulares = 0;
    
    for(const item of allItems){
        // Renglones Subsidiados a $0.01
        if(item.subsidized > 0){
            const sub = item.subsidized * ACTIVOS_PRICE;
            totalSubtotal += sub;
            totalCfdi += sub;
            piezasSubsid += item.subsidized;
            html += `<tr style="color:#22c55e;border-bottom:1px dashed rgba(34,197,94,.15);">
                <td style="padding:.35rem .4rem;font-weight:600;">${item.nombre} <span class="pill" style="background:rgba(34,197,94,.15);color:#22c55e;font-size:.65rem;padding:1px 5px;font-weight:700;">⚡ activos $0.01</span></td>
                <td style="text-align:center;font-weight:700;padding:.35rem .4rem;">${item.subsidized}</td>
                <td style="text-align:right;padding:.35rem .4rem;">$0.01</td>
                <td style="text-align:center;padding:.35rem .4rem;"><span style="opacity:.8;">0%</span></td>
                <td style="text-align:right;font-weight:700;padding:.35rem .4rem;">${_caMoneyFmt(sub,'MXN')}</td>
            </tr>`;
        }
        // Renglones Regulares
        const regQty = item.qty - (item.subsidized || 0);
        if(regQty > 0){
            const p = item.precio && !isNaN(item.precio) ? item.precio : 0;
            const regSub = regQty * p;
            const ivaAmt = item.tasa > 0 ? regSub * (item.tasa > 1 ? item.tasa / 100 : item.tasa) : 0;
            totalSubtotal += regSub;
            totalIva += ivaAmt;
            totalCfdi += (regSub + ivaAmt);
            piezasRegulares += regQty;
            html += `<tr style="border-bottom:1px solid rgba(255,255,255,.05);">
                <td style="padding:.35rem .4rem;">${item.nombre}</td>
                <td style="text-align:center;padding:.35rem .4rem;">${regQty}</td>
                <td style="text-align:right;padding:.35rem .4rem;">${_caMoneyFmt(item.precio,'MXN')}</td>
                <td style="text-align:center;padding:.35rem .4rem;">${item.tasa > 0 ? '16%' : '0%'}</td>
                <td style="text-align:right;padding:.35rem .4rem;font-weight:600;">${_caMoneyFmt(regSub + ivaAmt,'MXN')}</td>
            </tr>`;
        }
    }
    
    html += `<tr style="border-top:2px solid var(--border);font-weight:800;background:rgba(255,255,255,.02);">
        <td colspan="4" style="padding:.45rem .4rem;font-size:.8rem;">TOTAL PRE-FACTURA CFDI (${piezasSubsid + piezasRegulares} piezas)</td>
        <td style="text-align:right;padding:.45rem .4rem;font-size:.85rem;color:#818cf8;">${_caMoneyFmt(totalCfdi,'MXN')}</td>
    </tr>`;
    html += '</table>';
    
    if(piezasSubsid > 0){
        html += `<div style="margin-top:.45rem;font-size:.72rem;color:#22c55e;background:rgba(34,197,94,.06);padding:.35rem .6rem;border-radius:6px;display:flex;justify-content:space-between;">
            <span>✨ <strong>${piezasSubsid} pieza(s)</strong> subsidiada(s) a $0.01 cubiertas con activos (${fmt(montoActivosAplicar)})</span>
            <span>Piezas regulares: <strong>${piezasRegulares}</strong></span>
        </div>`;
    }
    
    preview.innerHTML = html;
    
    // Obtener IDs de activos seleccionados
    const checkedActivos = document.querySelectorAll('.caFactFull-check-activo:checked');
    const selectedActivosIds = [...checkedActivos].map(cb => cb.value);
    
    // Guardar para exportar y enviar
    window._caFactFullCfdi = {
        allItems,
        totalCfdi,
        totalSubtotal,
        totalIva,
        montoActivosAplicar,
        totalActivosDisponibles,
        totalNotas,
        totalPagos,
        saldoCredito,
        selectedIds: [...selectedIds],
        selectedActivosIds
    };
}

function caFactFullTogglePublico(isPublico){
    const setVal = (id, val) => { const el = document.getElementById(id); if(el) el.value = val; };
    if(isPublico){
        setVal('caFactFull-rfc', 'XAXX010101000');
        setVal('caFactFull-razon', 'PUBLICO EN GENERAL');
        setVal('caFactFull-regimen', '616');
        setVal('caFactFull-usoCfdi', 'S01');
    } else {
        const centroId = window._caDetalleId;
        const c = _caData.find(x => x.id === centroId);
        if(c){
            setVal('caFactFull-rfc', c.rfc || 'XAXX010101000');
            setVal('caFactFull-razon', c.razon_social || 'PUBLICO EN GENERAL');
            setVal('caFactFull-regimen', c.regimen_fiscal || '616');
            setVal('caFactFull-usoCfdi', c.uso_cfdi || 'S01');
        }
    }
}

function descargarReporteFacturaCentro(formato){
    const data = window._caFactFullCfdi;
    if(!data || !data.selectedIds.length) return BO_AUTH.showAlert('No hay datos para exportar. Selecciona notas primero.', 'warning');
    
    const centroId = window._caDetalleId;
    const c = _caData.find(x => x.id === centroId);
    const mes = document.getElementById('caFactFull-mes')?.value || '';
    const rfc = document.getElementById('caFactFull-rfc')?.value || '';
    const razon = document.getElementById('caFactFull-razon')?.value || '';
    
    if(formato === 'csv'){
        // ── CSV Export ──
        let csv = 'REPORTE FACTURACIÓN CENTRO DE APOYO\n';
        csv += `Centro,${c?.nombre||''}\nPeriodo,${mes}\nRFC,${rfc}\nRazón Social,${razon}\n\n`;
        
        csv += 'NOTAS SELECCIONADAS\nID,Fecha,Cliente,Total\n';
        const { notas } = window._caFactFullData;
        for(const n of notas){
            if(!data.selectedIds.includes(String(n.id))) continue;
            csv += `${n.id},${n.fecha_venta||''},${(n.cliente_nombre||'').replace(/,/g,' ')},${n.total||0}\n`;
        }
        
        csv += `\nACTIVOS APLICADOS,${data.montoActivosAplicar}\n`;
        csv += `PAGOS/DEPÓSITOS,${data.totalPagos}\n`;
        csv += `SALDO CRÉDITO,${data.saldoCredito}\n`;
        csv += `TOTAL PRE-FACTURA CFDI,${data.totalCfdi}\n\n`;
        
        csv += `ITEMS CFDI\nProducto,Qty,Precio Unit,IVA,Tipo\n`;
        for(const item of data.allItems){
            if(item.subsidized > 0) csv += `${(item.nombre||'').replace(/,/g,' ')},${item.subsidized},0.01,desc 100%,activos\n`;
            const regQty = item.qty - item.subsidized;
            if(regQty > 0) csv += `${(item.nombre||'').replace(/,/g,' ')},${regQty},${item.precio},${item.tasa}%,normal\n`;
        }
        
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `Factura_Centro_${centroId}_${mes}.csv`; a.click();
        URL.revokeObjectURL(url);
        BO_AUTH.showAlert('✅ CSV descargado', 'success');
        
    } else {
        // ── PDF Export ──
        const { notas, activos, pagos } = window._caFactFullData;
        let html = `<div style="font-family:'Inter',Arial,sans-serif;max-width:780px;margin:0 auto;padding:2rem;color:#1e293b;">`;
        html += `<h2 style="margin:0 0 .5rem;font-size:1.3rem;">🧾 Reporte Pre-Factura — ${c?.nombre||'Centro'}</h2>`;
        html += `<p style="color:#64748b;font-size:.85rem;margin:0 0 1.5rem;">Periodo: ${mes} · RFC: ${rfc} · ${razon}</p>`;
        
        // Notas
        html += `<h3 style="font-size:.95rem;border-bottom:2px solid #e2e8f0;padding-bottom:.3rem;">📋 Notas Seleccionadas (${data.selectedIds.length})</h3>`;
        html += '<table style="width:100%;border-collapse:collapse;font-size:.82rem;margin-bottom:1.5rem;">';
        html += '<tr style="background:#f1f5f9;font-weight:600;"><td style="padding:.4rem;">ID</td><td>Fecha</td><td>Cliente</td><td style="text-align:right;">Total</td></tr>';
        for(const n of notas){
            if(!data.selectedIds.includes(String(n.id))) continue;
            html += `<tr style="border-bottom:1px solid #e2e8f0;"><td style="padding:.3rem;font-family:monospace;font-size:.75rem;">${String(n.id).substring(0,12)}</td><td>${n.fecha_venta?new Date(n.fecha_venta).toLocaleDateString('es-MX'):'—'}</td><td>${n.cliente_nombre||'—'}</td><td style="text-align:right;font-weight:600;">$${Number(n.total||0).toLocaleString('es-MX',{minimumFractionDigits:2})}</td></tr>`;
        }
        html += '</table>';
        
        // Resumen Financiero
        html += '<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:1rem;margin-bottom:1.5rem;">';
        html += '<h3 style="font-size:.95rem;margin:0 0 .5rem;">📊 Resumen Financiero</h3>';
        html += `<table style="width:60%;font-size:.85rem;"><tr><td>Total Notas Seleccionadas:</td><td style="text-align:right;font-weight:700;">$${data.totalNotas.toLocaleString('es-MX',{minimumFractionDigits:2})}</td></tr>`;
        html += `<tr><td>Activos Aplicados:</td><td style="text-align:right;color:#16a34a;font-weight:700;">−$${data.montoActivosAplicar.toLocaleString('es-MX',{minimumFractionDigits:2})}</td></tr>`;
        html += `<tr><td>Pagos / Depósitos Directos:</td><td style="text-align:right;color:#d97706;font-weight:700;">−$${data.totalPagos.toLocaleString('es-MX',{minimumFractionDigits:2})}</td></tr>`;
        html += `<tr style="border-top:2px solid #e2e8f0;font-weight:800;"><td>Saldo Crédito:</td><td style="text-align:right;color:${data.saldoCredito<=0?'#16a34a':'#ef4444'};">$${Math.abs(data.saldoCredito).toLocaleString('es-MX',{minimumFractionDigits:2})}</td></tr>`;
        html += `<tr><td>Total Pre-Factura CFDI:</td><td style="text-align:right;font-weight:800;color:#4f46e5;">$${data.totalCfdi.toLocaleString('es-MX',{minimumFractionDigits:2})}</td></tr></table></div>`;
        
        // Items CFDI
        html += `<h3 style="font-size:.95rem;border-bottom:2px solid #e2e8f0;padding-bottom:.3rem;">🧾 Items CFDI Pre-Factura</h3>`;
        html += '<table style="width:100%;border-collapse:collapse;font-size:.82rem;">';
        html += '<tr style="background:#f1f5f9;font-weight:600;"><td style="padding:.4rem;">Producto</td><td style="text-align:center;">Cant</td><td style="text-align:right;">P.Unit</td><td style="text-align:center;">IVA</td></tr>';
        for(const item of data.allItems){
            if(item.subsidized > 0) html += `<tr style="color:#16a34a;border-bottom:1px solid #e2e8f0;"><td style="padding:.3rem;">${item.nombre} (activos)</td><td style="text-align:center;">${item.subsidized}</td><td style="text-align:right;">$0.01</td><td style="text-align:center;">desc 100%</td></tr>`;
            const regQty = item.qty - item.subsidized;
            if(regQty > 0) html += `<tr style="border-bottom:1px solid #e2e8f0;"><td style="padding:.3rem;">${item.nombre}</td><td style="text-align:center;">${regQty}</td><td style="text-align:right;">$${item.precio.toLocaleString('es-MX',{minimumFractionDigits:2})}</td><td style="text-align:center;">${item.tasa>0?item.tasa+'%':'0%'}</td></tr>`;
        }
        html += '</table></div>';
        
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'position:fixed;top:-9999px;left:-9999px;';
        wrapper.innerHTML = html;
        document.body.appendChild(wrapper);
        
        if(typeof html2pdf !== 'undefined'){
            html2pdf().set({
                margin: 10, filename: `PreFactura_Centro_${centroId}_${mes}.pdf`,
                html2canvas: { scale: 2 }, jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' }
            }).from(wrapper.firstElementChild).save().then(() => {
                wrapper.remove();
                BO_AUTH.showAlert('✅ PDF generado', 'success');
            }).catch(err => { wrapper.remove(); BO_AUTH.showAlert('Error PDF: ' + err.message, 'error'); });
        } else {
            wrapper.remove();
            BO_AUTH.showAlert('⚠️ html2pdf no disponible. Usa CSV.', 'warning');
        }
    }
}

async function enviarFacturaCentroPendiente(){
    const data = window._caFactFullCfdi;
    if(!data || !data.selectedIds.length) return BO_AUTH.showAlert('No hay notas seleccionadas para facturar', 'warning');
    
    const centroId = window._caDetalleId;
    const c = _caData.find(x => x.id === centroId);
    if(!c) return BO_AUTH.showAlert('Centro no encontrado', 'danger');
    
    const mes = document.getElementById('caFactFull-mes')?.value || '';
    if(!mes) return BO_AUTH.showAlert('Selecciona un periodo', 'warning');
    
    const rfc = document.getElementById('caFactFull-rfc')?.value?.trim() || 'XAXX010101000';
    const razon = document.getElementById('caFactFull-razon')?.value?.trim() || 'PUBLICO EN GENERAL';
    const cp = document.getElementById('caFactFull-cp')?.value?.trim() || '44100';
    const regimen = document.getElementById('caFactFull-regimen')?.value || '616';
    const usoCfdi = document.getElementById('caFactFull-usoCfdi')?.value || 'S01';
    const formaPago = document.getElementById('caFactFull-formaPago')?.value || '99';
    const esPublicoGeneral = document.getElementById('caFactFull-publicoGral')?.checked || false;
    const cedisId = c.cedis_origen || '0';
    
    const regimenCode = (regimen||'616').split(' ')[0].trim();
    const usoCfdiCode = (usoCfdi||'S01').split(' ')[0].trim();
    const formaPagoCode = (formaPago||'99').split(' ')[0].trim();
    
    // Generar referencia única (si ya hubo solicitudes parciales, añadir timestamp)
    const globalRef = 'CENTRO-' + centroId + '-' + mes;
    
    try {
        const esc = v => (v === null || v === undefined) ? 'NULL' : "'" + String(v).replace(/'/g, "''") + "'";
        const userId = (typeof CURRENT_USER !== 'undefined' && CURRENT_USER?.id) || '0';
        const ventasIdsJson = JSON.stringify(data.selectedIds);
        
        // 1. Crear solicitud en facturas_solicitadas con ventas_ids
        const sqlIns = `INSERT INTO facturas_solicitadas 
            (usuario_id, rfc, razon_social, cp, regimen, uso_cfdi, forma_pago, numero_nota, estado, cedis_id, ventas_ids) 
            VALUES (${esc(String(userId))}, ${esc(rfc)}, ${esc(razon)}, ${esc(cp)}, ${esc(regimenCode)}, ${esc(usoCfdiCode)}, ${esc(formaPagoCode)}, ${esc(globalRef)}, 'solicitada', ${esc(String(cedisId))}, ${esc(ventasIdsJson)}) 
            RETURNING id`;
        const insRes = await _caSql(sqlIns);
        let solicitudId = insRes[0]?.id;
        if (!solicitudId) {
            const sel = await _caSql(`SELECT id FROM facturas_solicitadas WHERE numero_nota=${esc(globalRef)} ORDER BY id DESC LIMIT 1`);
            solicitudId = sel[0]?.id;
        }
        if (!solicitudId) throw new Error('No se pudo registrar la solicitud en facturas_solicitadas');

        // 1b. Guardar metadata completa en comentario
        const metaStr = `act=${data.montoActivosAplicar};act_disp=${data.totalActivosDisponibles};pg=${esPublicoGeneral?1:0};mes=${mes};cfdi=${data.totalCfdi};n=${data.selectedIds.length}`;
        await _caSql(`UPDATE facturas_solicitadas SET comentario=${esc(metaStr)} WHERE id=${solicitudId}`).catch(()=>{});
        
        // 2. Marcar las notas seleccionadas como requiere_factura=true
        const inClause = data.selectedIds.map(id => "'" + id + "'").join(',');
        await _caSql(`UPDATE venta SET requiere_factura=true WHERE id IN (${inClause})`);
        
        BO_AUTH.showAlert(`✅ Solicitud de Factura enviada a Pendientes\nSolicitud #${solicitudId} · ${globalRef}\nNotas: ${data.selectedIds.length} · Activos aplicados: $${data.montoActivosAplicar.toLocaleString('es-MX',{minimumFractionDigits:2})}`, 'success');
        document.getElementById('modalFacturaCentroCompleto').style.display = 'none';
        loadFacturarCentro(centroId);
        if(typeof loadHistorialFacturas === 'function') loadHistorialFacturas();
    } catch(e){
        BO_AUTH.showAlert('Error al enviar a facturación: ' + e.message, 'danger');
    }
}

async function enviarFacturaCentroGlobal(centroId){
    const data = window._caFactFullCfdi;
    if(!data || !data.selectedIds.length) return BO_AUTH.showAlert('No hay notas seleccionadas', 'warning');
    
    const c = _caData.find(x => x.id === centroId);
    if(!c) return BO_AUTH.showAlert('Centro no encontrado', 'danger');
    
    const mes = document.getElementById('caFactMes')?.value || '';
    if(!mes) return BO_AUTH.showAlert('Selecciona un periodo', 'warning');
    
    // Valores fijos para factura global
    const rfc = 'XAXX010101000';
    const razon = 'PUBLICO EN GENERAL';
    const cp = '44100';
    const regimenCode = '616';
    const usoCfdiCode = 'S01';
    const formaPagoCode = '99';
    const esPublicoGeneral = true;
    const cedisId = c.cedis_origen || '0';
    
    const globalRef = 'CENTRO-' + centroId + '-' + mes;
    
    try {
        const esc = v => (v === null || v === undefined) ? 'NULL' : "'" + String(v).replace(/'/g, "''") + "'";
        const userId = (typeof CURRENT_USER !== 'undefined' && CURRENT_USER?.id) || '0';
        
        const ventasIdsJson = JSON.stringify(data.selectedIds);
        // 1. PRIMERO crear la solicitud con ventas_ids exactos
        const sqlIns = `INSERT INTO facturas_solicitadas 
            (usuario_id, rfc, razon_social, cp, regimen, uso_cfdi, forma_pago, numero_nota, estado, cedis_id, ventas_ids) 
            VALUES (${esc(String(userId))}, ${esc(rfc)}, ${esc(razon)}, ${esc(cp)}, ${esc(regimenCode)}, ${esc(usoCfdiCode)}, ${esc(formaPagoCode)}, ${esc(globalRef)}, 'solicitada', ${esc(String(cedisId))}, ${esc(ventasIdsJson)}) 
            RETURNING id`;
        const insRes = await _caSql(sqlIns);
        let solicitudId = insRes[0]?.id;
        if (!solicitudId) {
            const sel = await _caSql(`SELECT id FROM facturas_solicitadas WHERE numero_nota=${esc(globalRef)} ORDER BY id DESC LIMIT 1`);
            solicitudId = sel[0]?.id;
        }
        if (!solicitudId) throw new Error('No se pudo crear la solicitud en la base de datos');

        // 1b. Actualizar comentario
        const metaStr = 'act=' + data.totalActivos + ';pg=' + (esPublicoGeneral?1:0) + ';mes=' + mes + ';cfdi=' + data.totalCfdi + ';n=' + data.selectedIds.length;
        await _caSql(`UPDATE facturas_solicitadas SET comentario=${esc(metaStr)} WHERE id=${solicitudId}`).catch(()=>{});

        // 2. Marcar las notas como requiere_factura
        const inClause = data.selectedIds.map(id => "'" + id + "'").join(',');
        await _caSql(`UPDATE venta SET requiere_factura=true WHERE id IN (${inClause})`);
        
        BO_AUTH.showAlert(`✅ Notas enviadas a Factura Global\nSolicitud #${solicitudId} · ${globalRef}\nActivos: $${data.totalActivos.toLocaleString('es-MX',{minimumFractionDigits:2})}`, 'success');
        loadFacturarCentro(centroId);
        if(typeof loadHistorialFacturas === 'function') loadHistorialFacturas();
    } catch(e){
        BO_AUTH.showAlert('Error al enviar a facturación global: ' + e.message, 'danger');
    }
}


// ══════════════════════════════════════════════════════════════
// Tab: NOTAS ACTIVADAS — Personas activadas (notas sintéticas)
// ══════════════════════════════════════════════════════════════

async function loadNotasActivadasCentro(centroId){
    if(!centroId)return;
    const c=_caData.find(x=>x.id===centroId);
    const moneda=c?.moneda||'MXN';

    // Filtro de mes
    const mesInput=document.getElementById('caNotasActivMes');
    const verTodoEl=document.getElementById('caNotasActivVerTodo');
    if(mesInput && !mesInput.value){
        const now=new Date();
        mesInput.value=now.getFullYear()+'-'+String(now.getMonth()+1).padStart(2,'0');
        if(verTodoEl && !verTodoEl.dataset.touched){ verTodoEl.checked=true; verTodoEl.dataset.touched='1'; }
    }
    const verTodo=verTodoEl?.checked||false;
    const mesFiltro=(!verTodo && mesInput?.value)?mesInput.value:null;
    const whereMes=mesFiltro?` AND TO_CHAR(v.fecha_venta,'YYYY-MM')='${mesFiltro}'`:'';

    const tb=document.getElementById('caNotasActivBody');
    if(tb) tb.innerHTML='<tr><td colspan="6" style="text-align:center;color:var(--text-dim);padding:.75rem;">Cargando…</td></tr>';

    try{
        // Consultar notas de activación (producto_id = 'calificacion-mes')
        const rows=await _caSql(`
            SELECT v.id, v.total, v.fecha_venta, v.comentario,
                   am.mes AS mes_activacion, am.lider_id,
                   COALESCE(cl.nombre||' '||COALESCE(cl.ape_p,''), 'Lider #'||am.lider_id) AS lider_nombre,
                   cl.registro AS lider_registro
            FROM venta v
            LEFT JOIN activacion_mensual am ON am.venta_id = v.id::text
            LEFT JOIN cliente cl ON cl.id::text = am.lider_id::text
            WHERE v.centro_apoyo_id=${centroId}
              AND v.producto_id='calificacion-mes'${whereMes}
            ORDER BY v.fecha_venta DESC`);

        // KPIs
        const totalNotas=rows.length;
        const totalMonto=rows.reduce((s,r)=>s+Number(r.total||0),0);
        const lideresUnicos=new Set(rows.map(r=>r.lider_id).filter(Boolean)).size;
        const set=(id,val)=>{const el=document.getElementById(id);if(el)el.textContent=val;};
        set('caNotasActiv-kpiNotas',totalNotas);
        set('caNotasActiv-kpiMonto',_caMoneyFmt(totalMonto,moneda));
        set('caNotasActiv-kpiLideres',lideresUnicos);

        // Periodo label
        const periodoLblEl=document.getElementById('caNotasActiv-periodoLabel');
        if(periodoLblEl){
            const meses=['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
            if(mesFiltro){
                const [y,m]=mesFiltro.split('-');
                periodoLblEl.textContent=meses[parseInt(m)-1]+' '+y;
            } else {
                periodoLblEl.textContent='Histórico completo';
            }
            periodoLblEl.style.display='inline-block';
        }

        // Tabla
        if(!rows.length){
            tb.innerHTML='<tr><td colspan="6" style="text-align:center;color:var(--text-dim);padding:1.5rem;">Sin notas de activación en este período</td></tr>';
            return;
        }
        const mesesNom=['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
        tb.innerHTML=rows.map(r=>{
            const fecha=r.fecha_venta?new Date(r.fecha_venta).toLocaleDateString('es-MX',{day:'2-digit',month:'short',year:'2-digit'}):'—';
            const mesLabel=r.mes_activacion?(()=>{const [y,m]=r.mes_activacion.split('-');return mesesNom[parseInt(m)-1]+' '+y;})():fecha;
            const reg=r.lider_registro?'#'+r.lider_registro:'';
            return `<tr>
                <td><span style="font-family:monospace;font-size:.78rem;color:var(--text-dim);">${(r.id||'').toString().substring(0,12)}</span></td>
                <td style="font-weight:600;">${r.lider_nombre||'—'} <span style="color:var(--text-dim);font-size:.72rem;">${reg}</span></td>
                <td style="white-space:nowrap;">${mesLabel}</td>
                <td style="text-align:right;font-weight:600;color:#f59e0b;">${_caMoneyFmt(r.total,moneda)}</td>
                <td style="white-space:nowrap;font-size:.8rem;">${fecha}</td>
                <td><span style="color:#22c55e;font-size:.78rem;">⚡ Activada</span></td>
            </tr>`;
        }).join('');
    }catch(e){
        if(tb) tb.innerHTML='<tr><td colspan="6" style="color:#ef4444;padding:1rem;">Error: '+e.message+'</td></tr>';
    }
}

// ══════════════════════════════════════════════════════════════
// Reporte de Deuda — Modal + Exportar PDF
// ══════════════════════════════════════════════════════════════

async function generarReporteDeuda(centroId){
    if(!centroId) centroId=window._caDetalleId;
    if(!centroId)return;
    const c=_caData.find(x=>x.id===centroId);
    if(!c)return;
    const moneda=c.moneda||'MXN';

    const container=document.getElementById('caReporteDeudaContent');
    if(container) container.innerHTML='<div style="text-align:center;padding:2rem;color:var(--text-dim);">⏳ Generando reporte…</div>';
    document.getElementById('modalReporteDeuda').style.display='flex';

    try{
        // 1. Todas las notas vinculadas
        let ventas=[];
        try{
            ventas=await _caSql(`SELECT v.id, v.total, v.fecha_venta, v.producto_id, v.comentario, COALESCE(c2.nombre||' '||COALESCE(c2.ape_p,''),'Publico General') AS cliente_nombre FROM venta v LEFT JOIN cliente c2 ON c2.id=v.cliente_id WHERE v.centro_apoyo_id=${centroId} ORDER BY v.fecha_venta DESC`);
        }catch(e1){ console.warn('[ReporteDeuda] Q1 failed:',e1.message); }
        if(!ventas.length){
            try{
                ventas=await _caSql(`SELECT v.id, v.total, v.fecha_venta, v.comentario, COALESCE(c2.nombre||' '||COALESCE(c2.ape_p,''),'Publico General') AS cliente_nombre FROM venta v LEFT JOIN cliente c2 ON c2.id=v.cliente_id WHERE v.centro_apoyo_id=${centroId} ORDER BY v.fecha_venta DESC`);
            }catch(e2){ console.warn('[ReporteDeuda] Q2 failed:',e2.message); }
        }

        // 2. Separar ventas reales vs activaciones
        const ventasReales=ventas.filter(v=>(v.producto_id||'')!=='calificacion-mes');
        const activaciones=ventas.filter(v=>v.producto_id==='calificacion-mes');

        // 3. Pagos globales
        let totalPagado=0;
        try{
            const rPagos=await _caSql(`SELECT COALESCE(SUM(monto),0) as total FROM centro_apoyo_pago WHERE centro_id=${centroId}`);
            totalPagado=Number(rPagos[0]?.total||0);
        }catch(ep){}

        // 4. La deuda = credito_usado (source of truth del sistema)
        const deuda=Number(c.credito_usado||0);
        const limiteCredito=Number(c.limite_credito||0);
        const totalNotasProducto=ventasReales.reduce((s,v)=>s+Number(v.total||0),0);
        const totalActivaciones=activaciones.reduce((s,v)=>s+Number(v.total||0),0);
        const personasActivadas=activaciones.length;

        // Store data for PDF
        window._caReporteDeudaData={centro:c,ventasReales,activaciones,totalNotasProducto,totalPagado,deuda,totalActivaciones,personasActivadas,moneda};

        // Render KPIs
        const deudaColor=deuda>0?'#ef4444':'#22c55e';
        const deudaLabel=deuda>0?'Deuda Pendiente':'Liquidado ✓';
        let html=`
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:.75rem;margin-bottom:1.25rem;">
            <div style="background:rgba(99,102,241,.06);border:1px solid rgba(99,102,241,.2);border-radius:10px;padding:.85rem;text-align:center;">
                <div style="font-size:.68rem;color:#a5b4fc;text-transform:uppercase;letter-spacing:.04em;">Crédito Otorgado</div>
                <div style="font-size:1.3rem;font-weight:800;color:#a5b4fc;">${_caMoneyFmt(totalNotasProducto,moneda)}</div>
                <div style="font-size:.68rem;color:var(--text-dim);">${ventasReales.length} notas de producto</div>
            </div>
            <div style="background:rgba(34,197,94,.06);border:1px solid rgba(34,197,94,.2);border-radius:10px;padding:.85rem;text-align:center;">
                <div style="font-size:.68rem;color:#22c55e;text-transform:uppercase;letter-spacing:.04em;">Total Pagado</div>
                <div style="font-size:1.3rem;font-weight:800;color:#22c55e;">${_caMoneyFmt(totalPagado,moneda)}</div>
                <div style="font-size:.68rem;color:var(--text-dim);">abonos registrados</div>
            </div>
            <div style="background:rgba(${deuda>0?'239,68,68':'34,197,94'},.06);border:1px solid rgba(${deuda>0?'239,68,68':'34,197,94'},.2);border-radius:10px;padding:.85rem;text-align:center;">
                <div style="font-size:.68rem;color:${deudaColor};text-transform:uppercase;letter-spacing:.04em;">${deudaLabel}</div>
                <div style="font-size:1.3rem;font-weight:800;color:${deudaColor};">${_caMoneyFmt(deuda,moneda)}</div>
                <div style="font-size:.68rem;color:var(--text-dim);">de ${_caMoneyFmt(limiteCredito,moneda)} límite</div>
            </div>
            <div style="background:rgba(168,85,247,.06);border:1px solid rgba(168,85,247,.2);border-radius:10px;padding:.85rem;text-align:center;">
                <div style="font-size:.68rem;color:#c084fc;text-transform:uppercase;letter-spacing:.04em;">Personas Activadas</div>
                <div style="font-size:1.3rem;font-weight:800;color:#c084fc;">${personasActivadas}</div>
                <div style="font-size:.68rem;color:var(--text-dim);">${_caMoneyFmt(totalActivaciones,moneda)}</div>
            </div>
        </div>`;

        // Desglose nota por nota — TODAS las notas vinculadas
        html+=`<div style="font-size:.85rem;font-weight:700;margin-bottom:.5rem;">📋 Notas Vinculadas (${ventas.length} notas)</div>
        <div style="overflow-x:auto;margin-bottom:1.25rem;">
        <table class="bo-table">
            <thead><tr>
                <th>Nota</th><th>Fecha</th><th>Cliente</th><th>Tipo</th>
                <th style="text-align:right;">Total</th>
            </tr></thead>
            <tbody>`;

        for(const v of ventas){
            const total=Number(v.total||0);
            const esActivacion=v.producto_id==='calificacion-mes';
            const tipo=esActivacion?'<span style="color:#c084fc;font-size:.72rem;">⚡ Activación</span>':'<span style="font-size:.72rem;">📦 Producto</span>';
            html+=`<tr${esActivacion?' style="opacity:.6;"':''}>
                <td><span style="font-family:monospace;font-size:.75rem;">${(v.id||'').toString().substring(0,12)}</span></td>
                <td style="white-space:nowrap;font-size:.8rem;">${v.fecha_venta?new Date(v.fecha_venta).toLocaleDateString('es-MX',{day:'2-digit',month:'short',year:'2-digit'}):'—'}</td>
                <td style="font-size:.82rem;">${v.cliente_nombre||'—'}</td>
                <td>${tipo}</td>
                <td style="text-align:right;">${_caMoneyFmt(total,moneda)}</td>
            </tr>`;
        }
        const totalTodasNotas=ventas.reduce((s,v)=>s+Number(v.total||0),0);
        html+=`<tr style="font-weight:700;background:rgba(255,255,255,.03);">
            <td colspan="4" style="text-align:right;">TOTAL NOTAS</td>
            <td style="text-align:right;">${_caMoneyFmt(totalTodasNotas,moneda)}</td>
        </tr>`;
        html+='</tbody></table></div>';

        // Sección de Activaciones (informativa)
        if(activaciones.length){
            html+=`<div style="font-size:.85rem;font-weight:700;margin-bottom:.5rem;">⚡ Personas Activadas (${activaciones.length} activaciones por ${_caMoneyFmt(totalActivaciones,moneda)})</div>
            <div style="overflow-x:auto;margin-bottom:1.25rem;max-height:200px;overflow-y:auto;">
            <table class="bo-table">
                <thead><tr><th>Fecha</th><th>Persona</th><th style="text-align:right;">Monto</th></tr></thead>
                <tbody>`;
            for(const a of activaciones){
                html+=`<tr style="opacity:.75;">
                    <td style="white-space:nowrap;font-size:.8rem;">${a.fecha_venta?new Date(a.fecha_venta).toLocaleDateString('es-MX',{day:'2-digit',month:'short',year:'2-digit'}):'—'}</td>
                    <td style="font-size:.82rem;">${(a.comentario||'').replace(/Calificacion del Mes - /i,'').substring(0,50)||a.cliente_nombre||'—'}</td>
                    <td style="text-align:right;">${_caMoneyFmt(Number(a.total||0),moneda)}</td>
                </tr>`;
            }
            html+='</tbody></table></div>';
        }

        // Resumen textual
        html+=`<div style="margin-top:1.25rem;padding:1rem;background:rgba(${deuda>0?'245,158,11':'34,197,94'},.06);border:1px solid rgba(${deuda>0?'245,158,11':'34,197,94'},.2);border-radius:10px;">
            <div style="font-size:.85rem;font-weight:700;color:${deuda>0?'#f59e0b':'#22c55e'};margin-bottom:.5rem;">📊 Resumen de la Deuda</div>
            <div style="font-size:.82rem;line-height:1.7;color:var(--text-secondary);">
                Al centro <strong>${c.nombre}</strong> se le otorgó crédito de producto por <strong>${_caMoneyFmt(totalNotasProducto,moneda)}</strong> (${ventasReales.length} notas).
                Ha pagado <strong>${_caMoneyFmt(totalPagado,moneda)}</strong>.
                ${deuda>0?'<strong style="color:#ef4444;">Deuda pendiente: '+_caMoneyFmt(deuda,moneda)+'</strong> de un límite de '+_caMoneyFmt(limiteCredito,moneda)+'.':'<strong style="color:#22c55e;">No tiene deuda pendiente.</strong>'}
                Se activaron <strong>${personasActivadas} personas</strong> por un total de ${_caMoneyFmt(totalActivaciones,moneda)}.
            </div>
        </div>`;

        container.innerHTML=html;
    }catch(e){
        container.innerHTML='<div style="color:#ef4444;padding:1rem;">Error al generar reporte: '+e.message+'</div>';
    }
}

async function exportarReporteDeudaPDF(){
    const data=window._caReporteDeudaData;
    if(!data){return BO_AUTH.showAlert('Primero genera el reporte','warning');}

    const stripPdf=str=>(str||'')
        .replace(/[\u{1F000}-\u{1FFFF}]/gu,'')
        .replace(/[^\x00-\x7E\xA0-\xFF]/g,'')
        .replace(/\s+/g,' ').trim();

    const {centro:c,ventasReales,activaciones,totalNotasProducto,totalPagado,deuda,totalActivaciones,personasActivadas,moneda}=data;

    const {jsPDF}=window.jspdf||window;
    if(!jsPDF){return BO_AUTH.showAlert('jsPDF no disponible','warning');}
    const doc=new jsPDF({orientation:'portrait',unit:'mm',format:'a4'});
    const W=210,mg=15;

    // Header
    doc.setFillColor(30,27,75);
    doc.rect(0,0,W,32,'F');
    doc.setTextColor(255,255,255);
    doc.setFontSize(18);doc.setFont('helvetica','bold');
    doc.text('Reporte de Deuda',mg,13);
    doc.setFontSize(10);doc.setFont('helvetica','normal');
    doc.text(stripPdf(c.nombre||'Centro de Apoyo'),mg,21);
    doc.text('Generado: '+new Date().toLocaleDateString('es-MX',{day:'2-digit',month:'long',year:'numeric'}),mg,28);
    const liderNom=stripPdf(c._lider_nombre||c.lider_nombre||('ID: '+(c.lider_id||'--')));
    doc.text('Lider: '+liderNom,W-mg,28,{align:'right'});

    doc.setDrawColor(99,102,241);doc.setLineWidth(0.5);
    doc.line(mg,35,W-mg,35);

    // Resumen Financiero
    doc.setTextColor(30,27,75);
    doc.setFontSize(12);doc.setFont('helvetica','bold');
    doc.text('Resumen Financiero',mg,42);

    const estadoDeuda=deuda>0?'DEUDA PENDIENTE: '+_caMoneyFmt(deuda,moneda):'LIQUIDADO - Sin deuda pendiente';

    doc.autoTable({
        startY:46,
        margin:{left:mg,right:mg},
        head:[['Concepto','Valor']],
        body:[
            ['Credito Otorgado (Producto)',_caMoneyFmt(totalNotasProducto,moneda)+' ('+ventasReales.length+' notas)'],
            ['Total Pagado',_caMoneyFmt(totalPagado,moneda)],
            ['Estado',estadoDeuda],
            ['Deuda (Credito Usado)',_caMoneyFmt(deuda,moneda)],
            ['Limite de Credito',_caMoneyFmt(Number(c.limite_credito||0),moneda)],
            ['Personas Activadas',String(personasActivadas)+' ('+_caMoneyFmt(totalActivaciones,moneda)+')'],
        ],
        styles:{fontSize:9.5,cellPadding:3.5},
        headStyles:{fillColor:deuda>0?[239,68,68]:[34,197,94],textColor:255,fontStyle:'bold'},
        alternateRowStyles:{fillColor:deuda>0?[255,240,240]:[240,255,240]},
        columnStyles:{0:{fontStyle:'bold',cellWidth:72},1:{cellWidth:'auto'}},
    });

    // Desglose: todas las notas vinculadas
    const allVentas=[...ventasReales,...activaciones].sort((a,b)=>new Date(b.fecha_venta||0)-new Date(a.fecha_venta||0));
    const afterKpi=doc.lastAutoTable.finalY+10;
    doc.setFontSize(12);doc.setFont('helvetica','bold');doc.setTextColor(30,27,75);
    doc.text('Notas Vinculadas ('+allVentas.length+')',mg,afterKpi);

    const totalTodasNotas=allVentas.reduce((s,v)=>s+Number(v.total||0),0);

    doc.autoTable({
        startY:afterKpi+4,
        margin:{left:mg,right:mg},
        head:[['Nota','Fecha','Cliente','Tipo','Total']],
        body:allVentas.map(v=>{
            const total=Number(v.total||0);
            const esActiv=v.producto_id==='calificacion-mes';
            const fecha=v.fecha_venta?new Date(v.fecha_venta).toLocaleDateString('es-MX'):'--';
            return [
                (v.id||'').toString().substring(0,12),
                fecha,
                stripPdf(v.cliente_nombre||'--'),
                esActiv?'Activacion':'Producto',
                _caMoneyFmt(total,moneda)
            ];
        }).concat([[
            '','','','TOTAL',
            _caMoneyFmt(totalTodasNotas,moneda)
        ]]),
        styles:{fontSize:8,cellPadding:2.5},
        headStyles:{fillColor:[99,102,241],textColor:255,fontStyle:'bold'},
        alternateRowStyles:{fillColor:[240,240,255]},
        columnStyles:{
            0:{cellWidth:28,fontSize:7},
            1:{cellWidth:22},
            2:{cellWidth:42},
            3:{cellWidth:22},
            4:{halign:'right',cellWidth:28}
        },
        didParseCell:function(data){
            if(data.row.index===allVentas.length){
                data.cell.styles.fontStyle='bold';
                data.cell.styles.fillColor=[245,245,255];
            }
        }
    });

    // Pie
    doc.setDrawColor(200,200,200);doc.setLineWidth(0.3);
    doc.line(mg,286,W-mg,286);
    doc.setFontSize(7.5);doc.setTextColor(150,150,150);
    doc.text('Duletia Backoffice  |  Reporte de Deuda  |  '+new Date().toISOString().slice(0,10),W/2,291,{align:'center'});

    const fileName='reporte_deuda_'+stripPdf(c.nombre||String(centroId)).replace(/\s+/g,'_')+'_'+new Date().toISOString().slice(0,10)+'.pdf';
    doc.save(fileName);
    BO_AUTH.showAlert('PDF generado: '+fileName,'success');
}

async function _downloadActivacionPdf(ventaId) {
    if (!ventaId) return BO_AUTH.showAlert('Esta activación no tiene nota de venta asociada.', 'warning');
    try {
        const rows = await _caSql(`SELECT v.*, c.nombre as cliente_nombre, c.ape_p, c.ape_m 
                                   FROM venta v 
                                   LEFT JOIN cliente c ON c.id=v.cliente_id 
                                   WHERE v.id='${ventaId}'`);
        if (!rows.length) return BO_AUTH.showAlert('Nota de venta no encontrada.', 'warning');
        const v = rows[0];
        let pzArr = [];
        try { pzArr = typeof v.piezas === 'string' ? JSON.parse(v.piezas) : (v.piezas||[]); } catch(e){}
        let printItems = [];
        if (pzArr && pzArr.length > 0) {
            printItems = pzArr.map(pz => {
                const pr = pz.precio_unitario ? Number(pz.precio_unitario) : Math.round(Number(v.total||0)/(pz.cantidad||1));
                const sub = pz.precio_unitario ? Number(pz.precio_unitario)*Number(pz.cantidad) : Number(v.total||0);
                return { nombre: pz.nombre || pz.sku || 'Producto', cantidad: pz.cantidad, precio: pr, subtotal: sub };
            });
        } else {
            printItems = [{nombre: 'Activación de Centro', cantidad: 1, precio: Number(v.total||0), subtotal: Number(v.total||0)}];
        }
        
        if (typeof generarNotaVentaPDF === 'function') {
            generarNotaVentaPDF({
                numero: v.id, 
                clienteNombre: v.cliente_nombre ? `${v.cliente_nombre} ${v.ape_p||''} ${v.ape_m||''}`.trim() : '', 
                clienteId: v.cliente_id,
                fecha: v.fecha_venta ? new Date(v.fecha_venta).toLocaleDateString('es-MX') : '', 
                tipoPago: 'Activación', 
                total: Number(v.total||0),
                items: printItems, 
                comentarios: v.comentario || '', 
                subioPor: v.registro_id, 
                notaFull: v.id
            });
        } else {
            BO_AUTH.showAlert('Generador de PDF no disponible.', 'warning');
        }
    } catch(e) {
        BO_AUTH.showAlert('Error: '+e.message, 'danger');
    }
}
